import { fromByteArray } from 'base64-js';
import { stringify as stringify$1 } from 'safe-stable-stringify';

// src/lib/binary_reader.ts
var textDecoder = new TextDecoder("utf-8");
var BinaryReader = class {
  /**
   * The DataView used to read values from the binary data.
   *
   * Note: The DataView's `byteOffset` is relative to the beginning of the
   * underlying ArrayBuffer, not the start of the provided Uint8Array input.
   * This `BinaryReader`'s `#offset` field is used to track the current read position
   * relative to the start of the provided Uint8Array input.
   */
  view;
  /**
   * Represents the offset (in bytes) relative to the start of the DataView
   * and provided Uint8Array input.
   *
   * Note: This is *not* the absolute byte offset within the underlying ArrayBuffer.
   */
  offset = 0;
  constructor(input) {
    this.view = input instanceof DataView ? input : new DataView(input.buffer, input.byteOffset, input.byteLength);
    this.offset = 0;
  }
  reset(input) {
    this.view = input instanceof DataView ? input : new DataView(input.buffer, input.byteOffset, input.byteLength);
    this.offset = 0;
  }
  get remaining() {
    return this.view.byteLength - this.offset;
  }
  /** Ensure we have at least `n` bytes left to read */
  #ensure(n) {
    if (this.offset + n > this.view.byteLength) {
      throw new RangeError(
        `Tried to read ${n} byte(s) at relative offset ${this.offset}, but only ${this.remaining} byte(s) remain`
      );
    }
  }
  readUInt8Array() {
    const length = this.readU32();
    this.#ensure(length);
    return this.readBytes(length).slice();
  }
  readBool() {
    const value = this.view.getUint8(this.offset);
    this.offset += 1;
    return value !== 0;
  }
  readByte() {
    const value = this.view.getUint8(this.offset);
    this.offset += 1;
    return value;
  }
  readBytes(length) {
    const array = new Uint8Array(
      this.view.buffer,
      this.view.byteOffset + this.offset,
      length
    );
    this.offset += length;
    return array;
  }
  readI8() {
    const value = this.view.getInt8(this.offset);
    this.offset += 1;
    return value;
  }
  readU8() {
    return this.readByte();
  }
  readI16() {
    const value = this.view.getInt16(this.offset, true);
    this.offset += 2;
    return value;
  }
  readU16() {
    const value = this.view.getUint16(this.offset, true);
    this.offset += 2;
    return value;
  }
  readI32() {
    const value = this.view.getInt32(this.offset, true);
    this.offset += 4;
    return value;
  }
  readU32() {
    const value = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return value;
  }
  readI64() {
    const value = this.view.getBigInt64(this.offset, true);
    this.offset += 8;
    return value;
  }
  readU64() {
    const value = this.view.getBigUint64(this.offset, true);
    this.offset += 8;
    return value;
  }
  readU128() {
    const lowerPart = this.view.getBigUint64(this.offset, true);
    const upperPart = this.view.getBigUint64(this.offset + 8, true);
    this.offset += 16;
    return (upperPart << BigInt(64)) + lowerPart;
  }
  readI128() {
    const lowerPart = this.view.getBigUint64(this.offset, true);
    const upperPart = this.view.getBigInt64(this.offset + 8, true);
    this.offset += 16;
    return (upperPart << BigInt(64)) + lowerPart;
  }
  readU256() {
    const p0 = this.view.getBigUint64(this.offset, true);
    const p1 = this.view.getBigUint64(this.offset + 8, true);
    const p2 = this.view.getBigUint64(this.offset + 16, true);
    const p3 = this.view.getBigUint64(this.offset + 24, true);
    this.offset += 32;
    return (p3 << BigInt(3 * 64)) + (p2 << BigInt(2 * 64)) + (p1 << BigInt(1 * 64)) + p0;
  }
  readI256() {
    const p0 = this.view.getBigUint64(this.offset, true);
    const p1 = this.view.getBigUint64(this.offset + 8, true);
    const p2 = this.view.getBigUint64(this.offset + 16, true);
    const p3 = this.view.getBigInt64(this.offset + 24, true);
    this.offset += 32;
    return (p3 << BigInt(3 * 64)) + (p2 << BigInt(2 * 64)) + (p1 << BigInt(1 * 64)) + p0;
  }
  readF32() {
    const value = this.view.getFloat32(this.offset, true);
    this.offset += 4;
    return value;
  }
  readF64() {
    const value = this.view.getFloat64(this.offset, true);
    this.offset += 8;
    return value;
  }
  readString() {
    const length = this.readU32();
    this.#ensure(length);
    const bytes = this.readBytes(length);
    return textDecoder.decode(bytes);
  }
};
var textEncoder = new TextEncoder();
var ArrayBufferPrototypeTransfer = ArrayBuffer.prototype.transfer ?? function(newByteLength) {
  if (newByteLength === void 0) {
    return this.slice();
  } else if (newByteLength <= this.byteLength) {
    return this.slice(0, newByteLength);
  } else {
    const copy = new Uint8Array(newByteLength);
    copy.set(new Uint8Array(this));
    return copy.buffer;
  }
};
var ResizableBuffer = class {
  buffer;
  view;
  constructor(init) {
    this.buffer = typeof init === "number" ? new ArrayBuffer(init) : init;
    this.view = new DataView(this.buffer);
  }
  get capacity() {
    return this.buffer.byteLength;
  }
  grow(newSize) {
    if (newSize <= this.buffer.byteLength) return;
    this.buffer = ArrayBufferPrototypeTransfer.call(this.buffer, newSize);
    this.view = new DataView(this.buffer);
  }
};
var BinaryWriter = class {
  buffer;
  offset = 0;
  constructor(init) {
    this.buffer = typeof init === "number" ? new ResizableBuffer(init) : init;
  }
  clear() {
    this.offset = 0;
  }
  reset(buffer) {
    this.buffer = buffer;
    this.offset = 0;
  }
  expandBuffer(additionalCapacity) {
    const minCapacity = this.offset + additionalCapacity + 1;
    if (minCapacity <= this.buffer.capacity) return;
    let newCapacity = this.buffer.capacity * 2;
    if (newCapacity < minCapacity) newCapacity = minCapacity;
    this.buffer.grow(newCapacity);
  }
  toBase64() {
    return fromByteArray(this.getBuffer());
  }
  getBuffer() {
    return new Uint8Array(this.buffer.buffer, 0, this.offset);
  }
  get view() {
    return this.buffer.view;
  }
  writeUInt8Array(value) {
    const length = value.length;
    this.expandBuffer(4 + length);
    this.writeU32(length);
    new Uint8Array(this.buffer.buffer, this.offset).set(value);
    this.offset += length;
  }
  writeBool(value) {
    this.expandBuffer(1);
    this.view.setUint8(this.offset, value ? 1 : 0);
    this.offset += 1;
  }
  writeByte(value) {
    this.expandBuffer(1);
    this.view.setUint8(this.offset, value);
    this.offset += 1;
  }
  writeBytes(value) {
    this.expandBuffer(value.length);
    new Uint8Array(this.buffer.buffer, this.offset, value.length).set(value);
    this.offset += value.length;
  }
  writeI8(value) {
    this.expandBuffer(1);
    this.view.setInt8(this.offset, value);
    this.offset += 1;
  }
  writeU8(value) {
    this.expandBuffer(1);
    this.view.setUint8(this.offset, value);
    this.offset += 1;
  }
  writeI16(value) {
    this.expandBuffer(2);
    this.view.setInt16(this.offset, value, true);
    this.offset += 2;
  }
  writeU16(value) {
    this.expandBuffer(2);
    this.view.setUint16(this.offset, value, true);
    this.offset += 2;
  }
  writeI32(value) {
    this.expandBuffer(4);
    this.view.setInt32(this.offset, value, true);
    this.offset += 4;
  }
  writeU32(value) {
    this.expandBuffer(4);
    this.view.setUint32(this.offset, value, true);
    this.offset += 4;
  }
  writeI64(value) {
    this.expandBuffer(8);
    this.view.setBigInt64(this.offset, value, true);
    this.offset += 8;
  }
  writeU64(value) {
    this.expandBuffer(8);
    this.view.setBigUint64(this.offset, value, true);
    this.offset += 8;
  }
  writeU128(value) {
    this.expandBuffer(16);
    const lowerPart = value & BigInt("0xFFFFFFFFFFFFFFFF");
    const upperPart = value >> BigInt(64);
    this.view.setBigUint64(this.offset, lowerPart, true);
    this.view.setBigUint64(this.offset + 8, upperPart, true);
    this.offset += 16;
  }
  writeI128(value) {
    this.expandBuffer(16);
    const lowerPart = value & BigInt("0xFFFFFFFFFFFFFFFF");
    const upperPart = value >> BigInt(64);
    this.view.setBigInt64(this.offset, lowerPart, true);
    this.view.setBigInt64(this.offset + 8, upperPart, true);
    this.offset += 16;
  }
  writeU256(value) {
    this.expandBuffer(32);
    const low_64_mask = BigInt("0xFFFFFFFFFFFFFFFF");
    const p0 = value & low_64_mask;
    const p1 = value >> BigInt(64 * 1) & low_64_mask;
    const p2 = value >> BigInt(64 * 2) & low_64_mask;
    const p3 = value >> BigInt(64 * 3);
    this.view.setBigUint64(this.offset + 8 * 0, p0, true);
    this.view.setBigUint64(this.offset + 8 * 1, p1, true);
    this.view.setBigUint64(this.offset + 8 * 2, p2, true);
    this.view.setBigUint64(this.offset + 8 * 3, p3, true);
    this.offset += 32;
  }
  writeI256(value) {
    this.expandBuffer(32);
    const low_64_mask = BigInt("0xFFFFFFFFFFFFFFFF");
    const p0 = value & low_64_mask;
    const p1 = value >> BigInt(64 * 1) & low_64_mask;
    const p2 = value >> BigInt(64 * 2) & low_64_mask;
    const p3 = value >> BigInt(64 * 3);
    this.view.setBigUint64(this.offset + 8 * 0, p0, true);
    this.view.setBigUint64(this.offset + 8 * 1, p1, true);
    this.view.setBigUint64(this.offset + 8 * 2, p2, true);
    this.view.setBigInt64(this.offset + 8 * 3, p3, true);
    this.offset += 32;
  }
  writeF32(value) {
    this.expandBuffer(4);
    this.view.setFloat32(this.offset, value, true);
    this.offset += 4;
  }
  writeF64(value) {
    this.expandBuffer(8);
    this.view.setFloat64(this.offset, value, true);
    this.offset += 8;
  }
  writeString(value) {
    const len = value.length;
    this.expandBuffer(4 + len);
    const bytes = new Uint8Array(this.buffer.buffer);
    let offset = this.offset + 4;
    let i = 0;
    for (; i < len; i++) {
      const c = value.charCodeAt(i);
      if (c >= 128) break;
      bytes[offset++] = c;
    }
    if (i === len) {
      this.view.setUint32(this.offset, len, true);
      this.offset = offset;
      return;
    }
    this.writeUInt8Array(textEncoder.encode(value));
  }
};

// src/lib/util.ts
function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return false;
  }
  let firstKey = 0;
  if (obj1 instanceof Uint8Array && obj2 instanceof Uint8Array) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (obj1[i] !== obj2[i]) return false;
    }
    firstKey = obj1.length;
  }
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  const values1 = obj1;
  const values2 = obj2;
  for (let i = firstKey; i < keys1.length; i++) {
    const key = keys1[i];
    if (!Object.prototype.propertyIsEnumerable.call(obj2, key) || !deepEqual(values1[key], values2[key])) {
      return false;
    }
  }
  return true;
}
function uint8ArrayToHexString(array) {
  return Array.prototype.map.call(array.reverse(), (x) => ("00" + x.toString(16)).slice(-2)).join("");
}
function uint8ArrayToU128(array) {
  if (array.length != 16) {
    throw new Error(`Uint8Array is not 16 bytes long: ${array}`);
  }
  return new BinaryReader(array).readU128();
}
function uint8ArrayToU256(array) {
  if (array.length != 32) {
    throw new Error(`Uint8Array is not 32 bytes long: [${array}]`);
  }
  return new BinaryReader(array).readU256();
}
function hexStringToUint8Array(str) {
  if (str.startsWith("0x")) {
    str = str.slice(2);
  }
  const matches = str.match(/.{1,2}/g) || [];
  const data = Uint8Array.from(
    matches.map((byte) => parseInt(byte, 16))
  );
  return data.reverse();
}
function hexStringToU128(str) {
  return uint8ArrayToU128(hexStringToUint8Array(str));
}
function hexStringToU256(str) {
  return uint8ArrayToU256(hexStringToUint8Array(str));
}
function u128ToUint8Array(data) {
  const writer = new BinaryWriter(16);
  writer.writeU128(data);
  return writer.getBuffer();
}
function u128ToHexString(data) {
  return uint8ArrayToHexString(u128ToUint8Array(data));
}
function u256ToUint8Array(data) {
  const writer = new BinaryWriter(32);
  writer.writeU256(data);
  return writer.getBuffer();
}
function u256ToHexString(data) {
  return uint8ArrayToHexString(u256ToUint8Array(data));
}
function coerceToBigInt(value, what) {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(value);
  if (typeof value === "string" && value.trim() !== "") return BigInt(value);
  throw new TypeError(
    `Cannot convert ${typeof value} to ${what}: expected bigint, integer number, or decimal string`
  );
}
function toPascalCase(s) {
  const str = toCamelCase(s);
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function toCamelCase(s) {
  const str = s.replace(/[-_]+/g, "_").replace(/_([a-zA-Z0-9])/g, (_, c) => c.toUpperCase());
  return str.charAt(0).toLowerCase() + str.slice(1);
}
function toSnakeCase(str) {
  return str.replace(/([A-Z])/g, "_$1").replace(/[-\s]+/g, "_").toLowerCase();
}
function bsatnBaseSize(typespace, ty) {
  const assumedArrayLength = 4;
  while (ty.tag === "Ref") ty = typespace.types[ty.value];
  if (ty.tag === "Product") {
    let sum = 0;
    for (const { algebraicType: elem } of ty.value.elements) {
      sum += bsatnBaseSize(typespace, elem);
    }
    return sum;
  } else if (ty.tag === "Sum") {
    let min = Infinity;
    for (const { algebraicType: vari } of ty.value.variants) {
      const vSize = bsatnBaseSize(typespace, vari);
      if (vSize < min) min = vSize;
    }
    if (min === Infinity) min = 0;
    return 4 + min;
  } else if (ty.tag == "Array") {
    return 4 + assumedArrayLength * bsatnBaseSize(typespace, ty.value);
  }
  return {
    String: 4 + assumedArrayLength,
    Sum: 1,
    Bool: 1,
    I8: 1,
    U8: 1,
    I16: 2,
    U16: 2,
    I32: 4,
    U32: 4,
    F32: 4,
    I64: 8,
    U64: 8,
    F64: 8,
    I128: 16,
    U128: 16,
    I256: 32,
    U256: 32
  }[ty.tag];
}
function coerceParams(params) {
  return Object.fromEntries(
    Object.entries(params).map(([n, c]) => [
      n,
      "typeBuilder" in c ? c.typeBuilder : c
    ])
  );
}
var hasOwn = Object.hasOwn;

// src/lib/time_duration.ts
var TimeDuration = class _TimeDuration {
  __time_duration_micros__;
  static MICROS_PER_MILLIS = 1000n;
  /**
   * Get the algebraic type representation of the {@link TimeDuration} type.
   * @returns The algebraic type representation of the type.
   */
  static getAlgebraicType() {
    return AlgebraicType.Product({
      elements: [
        {
          name: "__time_duration_micros__",
          algebraicType: AlgebraicType.I64
        }
      ]
    });
  }
  static isTimeDuration(algebraicType) {
    if (algebraicType.tag !== "Product") {
      return false;
    }
    const elements = algebraicType.value.elements;
    if (elements.length !== 1) {
      return false;
    }
    const microsElement = elements[0];
    return microsElement.name === "__time_duration_micros__" && microsElement.algebraicType.tag === "I64";
  }
  get micros() {
    return this.__time_duration_micros__;
  }
  get millis() {
    return Number(this.micros / _TimeDuration.MICROS_PER_MILLIS);
  }
  constructor(micros) {
    this.__time_duration_micros__ = coerceToBigInt(micros, "TimeDuration");
  }
  static fromMillis(millis) {
    return new _TimeDuration(BigInt(millis) * _TimeDuration.MICROS_PER_MILLIS);
  }
  /** This outputs the same string format that we use in the host and in Rust modules */
  toString() {
    const micros = this.micros;
    const sign = micros < 0 ? "-" : "+";
    const pos = micros < 0 ? -micros : micros;
    const secs = pos / 1000000n;
    const micros_remaining = pos % 1000000n;
    return `${sign}${secs}.${String(micros_remaining).padStart(6, "0")}`;
  }
};

// src/lib/timestamp.ts
var Timestamp = class _Timestamp {
  __timestamp_micros_since_unix_epoch__;
  static MICROS_PER_MILLIS = 1000n;
  get microsSinceUnixEpoch() {
    return this.__timestamp_micros_since_unix_epoch__;
  }
  constructor(micros) {
    this.__timestamp_micros_since_unix_epoch__ = coerceToBigInt(
      micros,
      "Timestamp"
    );
  }
  /**
   * Get the algebraic type representation of the {@link Timestamp} type.
   * @returns The algebraic type representation of the type.
   */
  static getAlgebraicType() {
    return AlgebraicType.Product({
      elements: [
        {
          name: "__timestamp_micros_since_unix_epoch__",
          algebraicType: AlgebraicType.I64
        }
      ]
    });
  }
  static isTimestamp(algebraicType) {
    if (algebraicType.tag !== "Product") {
      return false;
    }
    const elements = algebraicType.value.elements;
    if (elements.length !== 1) {
      return false;
    }
    const microsElement = elements[0];
    return microsElement.name === "__timestamp_micros_since_unix_epoch__" && microsElement.algebraicType.tag === "I64";
  }
  /**
   * The Unix epoch, the midnight at the beginning of January 1, 1970, UTC.
   */
  static UNIX_EPOCH = new _Timestamp(0n);
  /**
   * Get a `Timestamp` representing the execution environment's belief of the current moment in time.
   */
  static now() {
    return _Timestamp.fromDate(/* @__PURE__ */ new Date());
  }
  /** Convert to milliseconds since Unix epoch. */
  toMillis() {
    return this.microsSinceUnixEpoch / 1000n;
  }
  /**
   * Get a `Timestamp` representing the same point in time as `date`.
   */
  static fromDate(date) {
    const millis = date.getTime();
    const micros = BigInt(millis) * _Timestamp.MICROS_PER_MILLIS;
    return new _Timestamp(micros);
  }
  /**
   * Get a `Date` representing approximately the same point in time as `this`.
   *
   * This method truncates to millisecond precision,
   * and throws `RangeError` if the `Timestamp` is outside the range representable as a `Date`.
   */
  toDate() {
    const micros = this.__timestamp_micros_since_unix_epoch__;
    const millis = micros / _Timestamp.MICROS_PER_MILLIS;
    if (millis > BigInt(Number.MAX_SAFE_INTEGER) || millis < BigInt(Number.MIN_SAFE_INTEGER)) {
      throw new RangeError(
        "Timestamp is outside of the representable range of JS's Date"
      );
    }
    return new Date(Number(millis));
  }
  /**
   * Get an ISO 8601 / RFC 3339 formatted string representation of this timestamp with microsecond precision.
   *
   * This method preserves the full microsecond precision of the timestamp,
   * and throws `RangeError` if the `Timestamp` is outside the range representable in ISO format.
   *
   * @returns ISO 8601 formatted string with microsecond precision (e.g., '2025-02-17T10:30:45.123456Z')
   */
  toISOString() {
    const micros = this.__timestamp_micros_since_unix_epoch__;
    const millis = micros / _Timestamp.MICROS_PER_MILLIS;
    if (millis > BigInt(Number.MAX_SAFE_INTEGER) || millis < BigInt(Number.MIN_SAFE_INTEGER)) {
      throw new RangeError(
        "Timestamp is outside of the representable range for ISO string formatting"
      );
    }
    const date = new Date(Number(millis));
    const isoBase = date.toISOString();
    const microsRemainder = Math.abs(Number(micros % 1000000n));
    const fractionalPart = String(microsRemainder).padStart(6, "0");
    return isoBase.replace(/\.\d{3}Z$/, `.${fractionalPart}Z`);
  }
  since(other) {
    return new TimeDuration(
      this.__timestamp_micros_since_unix_epoch__ - other.__timestamp_micros_since_unix_epoch__
    );
  }
};

// src/lib/uuid.ts
var Uuid = class _Uuid {
  __uuid__;
  /**
   * The nil UUID (all zeros).
   *
   * @example
   * ```ts
   * const uuid = Uuid.NIL;
   * console.assert(
   *   uuid.toString() === "00000000-0000-0000-0000-000000000000"
   * );
   * ```
   */
  static NIL = new _Uuid(0n);
  static MAX_UUID_BIGINT = 0xffffffffffffffffffffffffffffffffn;
  /**
   * The max UUID (all ones).
   *
   * @example
   * ```ts
   * const uuid = Uuid.MAX;
   * console.assert(
   *   uuid.toString() === "ffffffff-ffff-ffff-ffff-ffffffffffff"
   * );
   * ```
   */
  static MAX = new _Uuid(_Uuid.MAX_UUID_BIGINT);
  /**
   * Create a UUID from a raw 128-bit value.
   *
   * @param u - Unsigned 128-bit integer
   * @throws {Error} If the value is outside the valid UUID range
   */
  constructor(u) {
    const v = coerceToBigInt(u, "Uuid");
    if (v < 0n || v > _Uuid.MAX_UUID_BIGINT) {
      throw new Error("Invalid UUID: must be between 0 and `MAX_UUID_BIGINT`");
    }
    this.__uuid__ = v;
  }
  /**
   * Create a UUID `v4` from explicit random bytes.
   *
   * This method assumes the bytes are already sufficiently random.
   * It only sets the appropriate bits for the UUID version and variant.
   *
   * @param bytes - Exactly 16 random bytes
   * @returns A UUID `v4`
   * @throws {Error} If `bytes.length !== 16`
   *
   * @example
   * ```ts
   * const randomBytes = new Uint8Array(16);
   * const uuid = Uuid.fromRandomBytesV4(randomBytes);
   *
   * console.assert(
   *   uuid.toString() === "00000000-0000-4000-8000-000000000000"
   * );
   * ```
   */
  static fromRandomBytesV4(bytes) {
    if (bytes.length !== 16) throw new Error("UUID v4 requires 16 bytes");
    const arr = new Uint8Array(bytes);
    arr[6] = arr[6] & 15 | 64;
    arr[8] = arr[8] & 63 | 128;
    return new _Uuid(_Uuid.bytesToBigInt(arr));
  }
  /**
   * Generate a UUID `v7` using a monotonic counter from `0` to `2^31 - 1`,
   * a timestamp, and 4 random bytes.
   *
   * The counter wraps around on overflow.
   *
   * The UUID `v7` is structured as follows:
   *
   * ```ascii
   * ┌───────────────────────────────────────────────┬───────────────────┐
   * | B0  | B1  | B2  | B3  | B4  | B5              |         B6        |
   * ├───────────────────────────────────────────────┼───────────────────┤
   * |                 unix_ts_ms                    |      version 7    |
   * └───────────────────────────────────────────────┴───────────────────┘
   * ┌──────────────┬─────────┬──────────────────┬───────────────────────┐
   * | B7           | B8      | B9  | B10 | B11  | B12 | B13 | B14 | B15 |
   * ├──────────────┼─────────┼──────────────────┼───────────────────────┤
   * | counter_high | variant |    counter_low   |        random         |
   * └──────────────┴─────────┴──────────────────┴───────────────────────┘
   * ```
   *
   * @param counter - Mutable monotonic counter (31-bit)
   * @param now - Timestamp since the Unix epoch
   * @param randomBytes - Exactly 4 random bytes
   * @returns A UUID `v7`
   *
   * @throws {Error} If the `counter` is negative
   * @throws {Error} If the `timestamp` is before the Unix epoch
   * @throws {Error} If `randomBytes.length !== 4`
   *
   * @example
   * ```ts
   * const now = Timestamp.fromMillis(1_686_000_000_000n);
   * const counter = { value: 1 };
   * const randomBytes = new Uint8Array(4);
   *
   * const uuid = Uuid.fromCounterV7(counter, now, randomBytes);
   *
   * console.assert(
   *   uuid.toString() === "0000647e-5180-7000-8000-000200000000"
   * );
   * ```
   */
  static fromCounterV7(counter, now, randomBytes) {
    if (randomBytes.length !== 4) {
      throw new Error("`fromCounterV7` requires `randomBytes.length == 4`");
    }
    if (counter.value < 0) {
      throw new Error("`fromCounterV7` uuid `counter` must be non-negative");
    }
    if (now.__timestamp_micros_since_unix_epoch__ < 0) {
      throw new Error("`fromCounterV7` `timestamp` before unix epoch");
    }
    const counterVal = counter.value;
    counter.value = counterVal + 1 & 2147483647;
    const tsMs = now.toMillis() & 0xffffffffffffn;
    const bytes = new Uint8Array(16);
    bytes[0] = Number(tsMs >> 40n & 0xffn);
    bytes[1] = Number(tsMs >> 32n & 0xffn);
    bytes[2] = Number(tsMs >> 24n & 0xffn);
    bytes[3] = Number(tsMs >> 16n & 0xffn);
    bytes[4] = Number(tsMs >> 8n & 0xffn);
    bytes[5] = Number(tsMs & 0xffn);
    bytes[7] = counterVal >>> 23 & 255;
    bytes[9] = counterVal >>> 15 & 255;
    bytes[10] = counterVal >>> 7 & 255;
    bytes[11] = (counterVal & 127) << 1 & 255;
    bytes[12] |= randomBytes[0] & 127;
    bytes[13] = randomBytes[1];
    bytes[14] = randomBytes[2];
    bytes[15] = randomBytes[3];
    bytes[6] = bytes[6] & 15 | 112;
    bytes[8] = bytes[8] & 63 | 128;
    return new _Uuid(_Uuid.bytesToBigInt(bytes));
  }
  /**
   * Parse a UUID from a string representation.
   *
   * @param s - UUID string
   * @returns Parsed UUID
   * @throws {Error} If the string is not a valid UUID
   *
   * @example
   * ```ts
   * const s = "01888d6e-5c00-7000-8000-000000000000";
   * const uuid = Uuid.parse(s);
   *
   * console.assert(uuid.toString() === s);
   * ```
   */
  static parse(s) {
    const hex = s.replace(/-/g, "");
    if (hex.length !== 32) throw new Error("Invalid hex UUID");
    let v = 0n;
    for (let i = 0; i < 32; i += 2) {
      v = v << 8n | BigInt(parseInt(hex.slice(i, i + 2), 16));
    }
    return new _Uuid(v);
  }
  /** Convert to hex string without a 0x prefix. */
  toHexString() {
    return u128ToHexString(this.asBigInt());
  }
  /** Convert to string (hyphenated form). */
  toString() {
    const hex = this.toHexString();
    return hex.slice(0, 8) + "-" + hex.slice(8, 12) + "-" + hex.slice(12, 16) + "-" + hex.slice(16, 20) + "-" + hex.slice(20);
  }
  /** Convert to bigint (u128). */
  asBigInt() {
    return this.__uuid__;
  }
  /** Return a `Uint8Array` of 16 bytes. */
  toBytes() {
    return _Uuid.bigIntToBytes(this.__uuid__);
  }
  static bytesToBigInt(bytes) {
    let result = 0n;
    for (const b of bytes) result = result << 8n | BigInt(b);
    return result;
  }
  static bigIntToBytes(value) {
    const bytes = new Uint8Array(16);
    for (let i = 15; i >= 0; i--) {
      bytes[i] = Number(value & 0xffn);
      value >>= 8n;
    }
    return bytes;
  }
  /**
   * Returns the version of this UUID.
   *
   * This represents the algorithm used to generate the value.
   *
   * @returns A `UuidVersion`
   * @throws {Error} If the version field is not recognized
   */
  getVersion() {
    const version = this.toBytes()[6] >> 4 & 15;
    switch (version) {
      case 4:
        return "V4";
      case 7:
        return "V7";
      default:
        if (this == _Uuid.NIL) {
          return "Nil";
        }
        if (this == _Uuid.MAX) {
          return "Max";
        }
        throw new Error(`Unsupported UUID version: ${version}`);
    }
  }
  /**
   * Extract the monotonic counter from a UUIDv7.
   *
   * Intended for testing and diagnostics.
   * Behavior is undefined if called on a non-V7 UUID.
   *
   * @returns 31-bit counter value
   */
  getCounter() {
    const bytes = this.toBytes();
    const high = bytes[7];
    const mid1 = bytes[9];
    const mid2 = bytes[10];
    const low = bytes[11] >>> 1;
    return high << 23 | mid1 << 15 | mid2 << 7 | low | 0;
  }
  compareTo(other) {
    if (this.__uuid__ < other.__uuid__) return -1;
    if (this.__uuid__ > other.__uuid__) return 1;
    return 0;
  }
  static getAlgebraicType() {
    return AlgebraicType.Product({
      elements: [
        {
          name: "__uuid__",
          algebraicType: AlgebraicType.U128
        }
      ]
    });
  }
};

// src/lib/identity.ts
var Identity = class _Identity {
  __identity__;
  /**
   * Creates a new `Identity`.
   *
   * `data` can be a hexadecimal string or a `bigint`.
   */
  constructor(data) {
    this.__identity__ = typeof data === "string" ? hexStringToU256(data) : coerceToBigInt(data, "Identity");
  }
  /**
   * Get the algebraic type representation of the {@link Identity} type.
   * @returns The algebraic type representation of the type.
   */
  static getAlgebraicType() {
    return AlgebraicType.Product({
      elements: [{ name: "__identity__", algebraicType: AlgebraicType.U256 }]
    });
  }
  /**
   * Check if two identities are equal.
   */
  isEqual(other) {
    return this.toHexString() === other.toHexString();
  }
  /**
   * Check if two identities are equal.
   */
  equals(other) {
    return this.isEqual(other);
  }
  /**
   * Print the identity as a hexadecimal string.
   */
  toHexString() {
    return u256ToHexString(this.__identity__);
  }
  /**
   * Convert the address to a Uint8Array.
   */
  toUint8Array() {
    return u256ToUint8Array(this.__identity__);
  }
  /**
   * Parse an Identity from a hexadecimal string.
   */
  static fromString(str) {
    return new _Identity(str);
  }
  /**
   * Zero identity (0x0000000000000000000000000000000000000000000000000000000000000000)
   */
  static zero() {
    return new _Identity(0n);
  }
  toString() {
    return this.toHexString();
  }
};

// src/lib/algebraic_type_variants.ts
var algebraic_type_variants_exports = {};

// src/lib/algebraic_type.ts
var SERIALIZERS = /* @__PURE__ */ new Map();
var DESERIALIZERS = /* @__PURE__ */ new Map();
var AlgebraicType = {
  Ref: (value) => ({ tag: "Ref", value }),
  Sum: (value) => ({
    tag: "Sum",
    value
  }),
  Product: (value) => ({
    tag: "Product",
    value
  }),
  Array: (value) => ({
    tag: "Array",
    value
  }),
  String: { tag: "String" },
  Bool: { tag: "Bool" },
  I8: { tag: "I8" },
  U8: { tag: "U8" },
  I16: { tag: "I16" },
  U16: { tag: "U16" },
  I32: { tag: "I32" },
  U32: { tag: "U32" },
  I64: { tag: "I64" },
  U64: { tag: "U64" },
  I128: { tag: "I128" },
  U128: { tag: "U128" },
  I256: { tag: "I256" },
  U256: { tag: "U256" },
  F32: { tag: "F32" },
  F64: { tag: "F64" },
  makeSerializer(ty, typespace) {
    if (ty.tag === "Ref") {
      if (!typespace)
        throw new Error("cannot serialize refs without a typespace");
      while (ty.tag === "Ref") ty = typespace.types[ty.value];
    }
    switch (ty.tag) {
      case "Product":
        return ProductType.makeSerializer(ty.value, typespace);
      case "Sum":
        return SumType.makeSerializer(ty.value, typespace);
      case "Array":
        if (ty.value.tag === "U8") {
          return serializeUint8Array;
        } else {
          const serialize = AlgebraicType.makeSerializer(ty.value, typespace);
          return (writer, value) => {
            writer.writeU32(value.length);
            for (const elem of value) {
              serialize(writer, elem);
            }
          };
        }
      default:
        return primitiveSerializers[ty.tag];
    }
  },
  /** @deprecated Use `makeSerializer` instead. */
  serializeValue(writer, ty, value, typespace) {
    AlgebraicType.makeSerializer(ty, typespace)(writer, value);
  },
  makeDeserializer(ty, typespace) {
    if (ty.tag === "Ref") {
      if (!typespace)
        throw new Error("cannot deserialize refs without a typespace");
      while (ty.tag === "Ref") ty = typespace.types[ty.value];
    }
    switch (ty.tag) {
      case "Product":
        return ProductType.makeDeserializer(ty.value, typespace);
      case "Sum":
        return SumType.makeDeserializer(ty.value, typespace);
      case "Array":
        if (ty.value.tag === "U8") {
          return deserializeUint8Array;
        } else {
          const deserialize = AlgebraicType.makeDeserializer(
            ty.value,
            typespace
          );
          return (reader) => {
            const length = reader.readU32();
            const result = Array(length);
            for (let i = 0; i < length; i++) {
              result[i] = deserialize(reader);
            }
            return result;
          };
        }
      default:
        return primitiveDeserializers[ty.tag];
    }
  },
  /** @deprecated Use `makeDeserializer` instead. */
  deserializeValue(reader, ty, typespace) {
    return AlgebraicType.makeDeserializer(ty, typespace)(reader);
  },
  /**
   * Convert a value of the algebraic type into something that can be used as a key in a map.
   * There are no guarantees about being able to order it.
   * This is only guaranteed to be comparable to other values of the same type.
   * @param value A value of the algebraic type
   * @returns Something that can be used as a key in a map.
   */
  intoMapKey: function(ty, value) {
    switch (ty.tag) {
      case "U8":
      case "U16":
      case "U32":
      case "U64":
      case "U128":
      case "U256":
      case "I8":
      case "I16":
      case "I32":
      case "I64":
      case "I128":
      case "I256":
      case "F32":
      case "F64":
      case "String":
      case "Bool":
        return value;
      case "Product":
        return ProductType.intoMapKey(ty.value, value);
      default: {
        const writer = new BinaryWriter(10);
        AlgebraicType.serializeValue(writer, ty, value);
        return writer.toBase64();
      }
    }
  }
};
function bindCall(f) {
  return Function.prototype.call.bind(f);
}
var primitiveSerializers = {
  Bool: bindCall(BinaryWriter.prototype.writeBool),
  I8: bindCall(BinaryWriter.prototype.writeI8),
  U8: bindCall(BinaryWriter.prototype.writeU8),
  I16: bindCall(BinaryWriter.prototype.writeI16),
  U16: bindCall(BinaryWriter.prototype.writeU16),
  I32: bindCall(BinaryWriter.prototype.writeI32),
  U32: bindCall(BinaryWriter.prototype.writeU32),
  I64: bindCall(BinaryWriter.prototype.writeI64),
  U64: bindCall(BinaryWriter.prototype.writeU64),
  I128: bindCall(BinaryWriter.prototype.writeI128),
  U128: bindCall(BinaryWriter.prototype.writeU128),
  I256: bindCall(BinaryWriter.prototype.writeI256),
  U256: bindCall(BinaryWriter.prototype.writeU256),
  F32: bindCall(BinaryWriter.prototype.writeF32),
  F64: bindCall(BinaryWriter.prototype.writeF64),
  String: bindCall(BinaryWriter.prototype.writeString)
};
Object.freeze(primitiveSerializers);
var serializeUint8Array = bindCall(BinaryWriter.prototype.writeUInt8Array);
var primitiveDeserializers = {
  Bool: bindCall(BinaryReader.prototype.readBool),
  I8: bindCall(BinaryReader.prototype.readI8),
  U8: bindCall(BinaryReader.prototype.readU8),
  I16: bindCall(BinaryReader.prototype.readI16),
  U16: bindCall(BinaryReader.prototype.readU16),
  I32: bindCall(BinaryReader.prototype.readI32),
  U32: bindCall(BinaryReader.prototype.readU32),
  I64: bindCall(BinaryReader.prototype.readI64),
  U64: bindCall(BinaryReader.prototype.readU64),
  I128: bindCall(BinaryReader.prototype.readI128),
  U128: bindCall(BinaryReader.prototype.readU128),
  I256: bindCall(BinaryReader.prototype.readI256),
  U256: bindCall(BinaryReader.prototype.readU256),
  F32: bindCall(BinaryReader.prototype.readF32),
  F64: bindCall(BinaryReader.prototype.readF64),
  String: bindCall(BinaryReader.prototype.readString)
};
Object.freeze(primitiveDeserializers);
var deserializeUint8Array = bindCall(BinaryReader.prototype.readUInt8Array);
var primitiveSizes = {
  Bool: 1,
  I8: 1,
  U8: 1,
  I16: 2,
  U16: 2,
  I32: 4,
  U32: 4,
  I64: 8,
  U64: 8,
  I128: 16,
  U128: 16,
  I256: 32,
  U256: 32,
  F32: 4,
  F64: 8
};
var fixedSizePrimitives = new Set(Object.keys(primitiveSizes));
var isFixedSizeProduct = (ty) => ty.elements.every(
  ({ algebraicType }) => fixedSizePrimitives.has(algebraicType.tag)
);
var productSize = (ty) => ty.elements.reduce(
  (acc, { algebraicType }) => acc + primitiveSizes[algebraicType.tag],
  0
);
var primitiveJSName = {
  Bool: "Uint8",
  I8: "Int8",
  U8: "Uint8",
  I16: "Int16",
  U16: "Uint16",
  I32: "Int32",
  U32: "Uint32",
  I64: "BigInt64",
  U64: "BigUint64",
  F32: "Float32",
  F64: "Float64"
};
var specialProductDeserializers = {
  __time_duration_micros__: (reader) => new TimeDuration(reader.readI64()),
  __timestamp_micros_since_unix_epoch__: (reader) => new Timestamp(reader.readI64()),
  __identity__: (reader) => new Identity(reader.readU256()),
  __connection_id__: (reader) => new ConnectionId(reader.readU128()),
  __uuid__: (reader) => new Uuid(reader.readU128())
};
Object.freeze(specialProductDeserializers);
var unitDeserializer = () => ({});
var getElementInitializer = (element) => {
  let init;
  switch (element.algebraicType.tag) {
    case "String":
      init = "''";
      break;
    case "Bool":
      init = "false";
      break;
    case "I8":
    case "U8":
    case "I16":
    case "U16":
    case "I32":
    case "U32":
      init = "0";
      break;
    case "I64":
    case "U64":
    case "I128":
    case "U128":
    case "I256":
    case "U256":
      init = "0n";
      break;
    case "F32":
    case "F64":
      init = "0.0";
      break;
    default:
      init = "undefined";
  }
  return `${element.name}: ${init}`;
};
var ProductType = {
  makeSerializer(ty, typespace) {
    let serializer = SERIALIZERS.get(ty);
    if (serializer != null) return serializer;
    if (isFixedSizeProduct(ty)) {
      const size = productSize(ty);
      const body2 = `"use strict";
writer.expandBuffer(${size});
const view = writer.view;
${ty.elements.map(
        ({ name, algebraicType: { tag } }) => tag in primitiveJSName ? `view.set${primitiveJSName[tag]}(writer.offset, value.${name}, ${primitiveSizes[tag] > 1 ? "true" : ""});
writer.offset += ${primitiveSizes[tag]};` : `writer.write${tag}(value.${name});`
      ).join("\n")}`;
      serializer = Function("writer", "value", body2);
      SERIALIZERS.set(ty, serializer);
      return serializer;
    }
    const serializers = {};
    const body = '"use strict";\n' + ty.elements.map(
      (element) => `this.${element.name}(writer, value.${element.name});`
    ).join("\n");
    serializer = Function("writer", "value", body).bind(
      serializers
    );
    SERIALIZERS.set(ty, serializer);
    for (const { name, algebraicType } of ty.elements) {
      serializers[name] = AlgebraicType.makeSerializer(
        algebraicType,
        typespace
      );
    }
    Object.freeze(serializers);
    return serializer;
  },
  /** @deprecated Use `makeSerializer` instead. */
  serializeValue(writer, ty, value, typespace) {
    ProductType.makeSerializer(ty, typespace)(writer, value);
  },
  makeDeserializer(ty, typespace) {
    switch (ty.elements.length) {
      case 0:
        return unitDeserializer;
      case 1: {
        const fieldName = ty.elements[0].name;
        if (hasOwn(specialProductDeserializers, fieldName))
          return specialProductDeserializers[fieldName];
      }
    }
    let deserializer = DESERIALIZERS.get(ty);
    if (deserializer != null) return deserializer;
    if (isFixedSizeProduct(ty)) {
      const body = `"use strict";
const result = { ${ty.elements.map(getElementInitializer).join(", ")} };
const view = reader.view;
${ty.elements.map(
        ({ name, algebraicType: { tag } }) => tag in primitiveJSName ? tag === "Bool" ? `result.${name} = view.getUint8(reader.offset) !== 0;
reader.offset += 1;` : `result.${name} = view.get${primitiveJSName[tag]}(reader.offset, ${primitiveSizes[tag] > 1 ? "true" : ""});
reader.offset += ${primitiveSizes[tag]};` : `result.${name} = reader.read${tag}();`
      ).join("\n")}
return result;`;
      deserializer = Function("reader", body);
      DESERIALIZERS.set(ty, deserializer);
      return deserializer;
    }
    const deserializers = {};
    deserializer = Function(
      "reader",
      `"use strict";
const result = { ${ty.elements.map(getElementInitializer).join(", ")} };
${ty.elements.map(({ name }) => `result.${name} = this.${name}(reader);`).join("\n")}
return result;`
    ).bind(deserializers);
    DESERIALIZERS.set(ty, deserializer);
    for (const { name, algebraicType } of ty.elements) {
      deserializers[name] = AlgebraicType.makeDeserializer(
        algebraicType,
        typespace
      );
    }
    Object.freeze(deserializers);
    return deserializer;
  },
  /** @deprecated Use `makeDeserializer` instead. */
  deserializeValue(reader, ty, typespace) {
    return ProductType.makeDeserializer(ty, typespace)(reader);
  },
  intoMapKey(ty, value) {
    if (ty.elements.length === 1) {
      const fieldName = ty.elements[0].name;
      if (hasOwn(specialProductDeserializers, fieldName)) {
        return value[fieldName];
      }
    }
    const writer = new BinaryWriter(10);
    AlgebraicType.serializeValue(writer, AlgebraicType.Product(ty), value);
    return writer.toBase64();
  }
};
var SumType = {
  makeSerializer(ty, typespace) {
    if (ty.variants.length == 2 && ty.variants[0].name === "some" && ty.variants[1].name === "none") {
      const serialize = AlgebraicType.makeSerializer(
        ty.variants[0].algebraicType,
        typespace
      );
      return (writer, value) => {
        if (value !== null && value !== void 0) {
          writer.writeByte(0);
          serialize(writer, value);
        } else {
          writer.writeByte(1);
        }
      };
    } else if (ty.variants.length == 2 && ty.variants[0].name === "ok" && ty.variants[1].name === "err") {
      const serializeOk = AlgebraicType.makeSerializer(
        ty.variants[0].algebraicType,
        typespace
      );
      const serializeErr = AlgebraicType.makeSerializer(
        ty.variants[1].algebraicType,
        typespace
      );
      return (writer, value) => {
        if ("ok" in value) {
          writer.writeU8(0);
          serializeOk(writer, value.ok);
        } else if ("err" in value) {
          writer.writeU8(1);
          serializeErr(writer, value.err);
        } else {
          throw new TypeError(
            "could not serialize result: object had neither a `ok` nor an `err` field"
          );
        }
      };
    } else {
      let serializer = SERIALIZERS.get(ty);
      if (serializer != null) return serializer;
      const serializers = {};
      const body = `switch (value.tag) {
${ty.variants.map(
        ({ name }, i) => `  case ${JSON.stringify(name)}:
    writer.writeByte(${i});
    return this.${name}(writer, value.value);`
      ).join("\n")}
  default:
    throw new TypeError(
      \`Could not serialize sum type; unknown tag \${value.tag}\`
    )
}
`;
      serializer = Function("writer", "value", body).bind(
        serializers
      );
      SERIALIZERS.set(ty, serializer);
      for (const { name, algebraicType } of ty.variants) {
        serializers[name] = AlgebraicType.makeSerializer(
          algebraicType,
          typespace
        );
      }
      Object.freeze(serializers);
      return serializer;
    }
  },
  /** @deprecated Use `makeSerializer` instead. */
  serializeValue(writer, ty, value, typespace) {
    SumType.makeSerializer(ty, typespace)(writer, value);
  },
  makeDeserializer(ty, typespace) {
    if (ty.variants.length == 2 && ty.variants[0].name === "some" && ty.variants[1].name === "none") {
      const deserialize = AlgebraicType.makeDeserializer(
        ty.variants[0].algebraicType,
        typespace
      );
      return (reader) => {
        const tag = reader.readU8();
        if (tag === 0) {
          return deserialize(reader);
        } else if (tag === 1) {
          return void 0;
        } else {
          throw new Error(
            `Can't deserialize an option type, couldn't find ${tag} tag`
          );
        }
      };
    } else if (ty.variants.length == 2 && ty.variants[0].name === "ok" && ty.variants[1].name === "err") {
      const deserializeOk = AlgebraicType.makeDeserializer(
        ty.variants[0].algebraicType,
        typespace
      );
      const deserializeErr = AlgebraicType.makeDeserializer(
        ty.variants[1].algebraicType,
        typespace
      );
      return (reader) => {
        const tag = reader.readByte();
        if (tag === 0) {
          return { ok: deserializeOk(reader) };
        } else if (tag === 1) {
          return { err: deserializeErr(reader) };
        } else {
          throw new Error(
            `Can't deserialize a result type, couldn't find ${tag} tag`
          );
        }
      };
    } else {
      let deserializer = DESERIALIZERS.get(ty);
      if (deserializer != null) return deserializer;
      const deserializers = {};
      deserializer = Function(
        "reader",
        `switch (reader.readU8()) {
${ty.variants.map(
          ({ name }, i) => `case ${i}: return { tag: ${JSON.stringify(name)}, value: this.${name}(reader) };`
        ).join("\n")} }`
      ).bind(deserializers);
      DESERIALIZERS.set(ty, deserializer);
      for (const { name, algebraicType } of ty.variants) {
        deserializers[name] = AlgebraicType.makeDeserializer(
          algebraicType,
          typespace
        );
      }
      Object.freeze(deserializers);
      return deserializer;
    }
  },
  /** @deprecated Use `makeDeserializer` instead. */
  deserializeValue(reader, ty, typespace) {
    return SumType.makeDeserializer(ty, typespace)(reader);
  }
};

// src/lib/connection_id.ts
var ConnectionId = class _ConnectionId {
  __connection_id__;
  /**
   * Creates a new `ConnectionId`.
   */
  constructor(data) {
    this.__connection_id__ = coerceToBigInt(data, "ConnectionId");
  }
  /**
   * Get the algebraic type representation of the {@link ConnectionId} type.
   * @returns The algebraic type representation of the type.
   */
  static getAlgebraicType() {
    return AlgebraicType.Product({
      elements: [
        { name: "__connection_id__", algebraicType: AlgebraicType.U128 }
      ]
    });
  }
  isZero() {
    return this.__connection_id__ === BigInt(0);
  }
  static nullIfZero(addr) {
    if (addr.isZero()) {
      return null;
    } else {
      return addr;
    }
  }
  static random() {
    function randomU8() {
      return Math.floor(Math.random() * 255);
    }
    let result = BigInt(0);
    for (let i = 0; i < 16; i++) {
      result = result << BigInt(8) | BigInt(randomU8());
    }
    return new _ConnectionId(result);
  }
  /**
   * Compare two connection IDs for equality.
   */
  isEqual(other) {
    return this.__connection_id__ == other.__connection_id__;
  }
  /**
   * Check if two connection IDs are equal.
   */
  equals(other) {
    return this.isEqual(other);
  }
  /**
   * Print the connection ID as a hexadecimal string.
   */
  toHexString() {
    return u128ToHexString(this.__connection_id__);
  }
  /**
   * Convert the connection ID to a Uint8Array.
   */
  toUint8Array() {
    return u128ToUint8Array(this.__connection_id__);
  }
  /**
   * Parse a connection ID from a hexadecimal string.
   */
  static fromString(str) {
    return new _ConnectionId(hexStringToU128(str));
  }
  static fromStringOrNull(str) {
    const addr = _ConnectionId.fromString(str);
    if (addr.isZero()) {
      return null;
    } else {
      return addr;
    }
  }
};

// src/lib/errors.ts
var SenderError = class extends Error {
  constructor(message) {
    super(message);
  }
  get name() {
    return "SenderError";
  }
};
var InternalError = class extends Error {
  constructor(message) {
    super(message);
  }
  get name() {
    return "InternalError";
  }
};
var DisconnectedError = class extends Error {
  constructor(message = "Not connected to SpacetimeDB") {
    super(message);
  }
  get name() {
    return "DisconnectedError";
  }
};
var UnknownCallResultError = class extends Error {
  constructor(message = "Connection lost before the call was acknowledged; it may or may not have run") {
    super(message);
  }
  get name() {
    return "UnknownCallResultError";
  }
};
var IdentityChangedError = class extends Error {
  constructor(message = "Reconnected with a different identity; the token was revoked or replaced") {
    super(message);
  }
  get name() {
    return "IdentityChangedError";
  }
};

// src/lib/algebraic_value.ts
function parseValue(ty, src) {
  const reader = new BinaryReader(src);
  return ty.deserialize(reader);
}

// src/lib/schedule_at.ts
var ScheduleAt = {
  interval(value) {
    return Interval(value);
  },
  time(value) {
    return Time(value);
  },
  getAlgebraicType() {
    return AlgebraicType.Sum({
      variants: [
        {
          name: "Interval",
          algebraicType: TimeDuration.getAlgebraicType()
        },
        { name: "Time", algebraicType: Timestamp.getAlgebraicType() }
      ]
    });
  },
  isScheduleAt(algebraicType) {
    if (algebraicType.tag !== "Sum") {
      return false;
    }
    const variants = algebraicType.value.variants;
    if (variants.length !== 2) {
      return false;
    }
    const intervalVariant = variants.find((v) => v.name === "Interval");
    const timeVariant = variants.find((v) => v.name === "Time");
    if (!intervalVariant || !timeVariant) {
      return false;
    }
    return TimeDuration.isTimeDuration(intervalVariant.algebraicType) && Timestamp.isTimestamp(timeVariant.algebraicType);
  }
};
var Interval = (micros) => ({
  tag: "Interval",
  value: new TimeDuration(micros)
});
var Time = (microsSinceUnixEpoch) => ({
  tag: "Time",
  value: new Timestamp(microsSinceUnixEpoch)
});
var schedule_at_default = ScheduleAt;

// src/lib/option.ts
var Option = {
  getAlgebraicType(innerType) {
    return AlgebraicType.Sum({
      variants: [
        { name: "some", algebraicType: innerType },
        {
          name: "none",
          algebraicType: AlgebraicType.Product({ elements: [] })
        }
      ]
    });
  }
};

// src/lib/result.ts
var Result = {
  getAlgebraicType(okType, errType) {
    return AlgebraicType.Sum({
      variants: [
        { name: "ok", algebraicType: okType },
        { name: "err", algebraicType: errType }
      ]
    });
  }
};

// src/lib/query.ts
var QueryBrand = /* @__PURE__ */ Symbol("QueryBrand");
var isRowTypedQuery = (val) => !!val && typeof val === "object" && QueryBrand in val;
var isTypedQuery = (val) => !!val && typeof val === "object" && QueryBrand in val;
function toSql(q) {
  return q.toSql();
}
var SemijoinImpl = class _SemijoinImpl {
  constructor(sourceQuery, filterQuery, joinCondition) {
    this.sourceQuery = sourceQuery;
    this.filterQuery = filterQuery;
    this.joinCondition = joinCondition;
    if (sourceQuery.table.sourceName === filterQuery.table.sourceName) {
      throw new Error("Cannot semijoin a table to itself");
    }
  }
  sourceQuery;
  filterQuery;
  joinCondition;
  [QueryBrand] = true;
  type = "semijoin";
  build() {
    return this;
  }
  where(predicate) {
    const nextSourceQuery = this.sourceQuery.where(predicate);
    return new _SemijoinImpl(
      nextSourceQuery,
      this.filterQuery,
      this.joinCondition
    );
  }
  toSql() {
    const left = this.filterQuery;
    const right = this.sourceQuery;
    const leftTable = quoteIdentifier(left.table.sourceName);
    const rightTable = quoteIdentifier(right.table.sourceName);
    let sql = `SELECT ${rightTable}.* FROM ${leftTable} JOIN ${rightTable} ON ${booleanExprToSql(this.joinCondition)}`;
    const clauses = [];
    if (left.whereClause) {
      clauses.push(booleanExprToSql(left.whereClause));
    }
    if (right.whereClause) {
      clauses.push(booleanExprToSql(right.whereClause));
    }
    if (clauses.length > 0) {
      const whereSql = clauses.length === 1 ? clauses[0] : clauses.map(wrapInParens).join(" AND ");
      sql += ` WHERE ${whereSql}`;
    }
    return sql;
  }
};
var FromBuilder = class _FromBuilder {
  constructor(table2, whereClause) {
    this.table = table2;
    this.whereClause = whereClause;
  }
  table;
  whereClause;
  [QueryBrand] = true;
  where(predicate) {
    const newCondition = normalizePredicateExpr(predicate(this.table.cols));
    const nextWhere = this.whereClause ? this.whereClause.and(newCondition) : newCondition;
    return new _FromBuilder(this.table, nextWhere);
  }
  rightSemijoin(right, on) {
    const sourceQuery = new _FromBuilder(right);
    const joinCondition = on(
      this.table.indexedCols,
      right.indexedCols
    );
    return new SemijoinImpl(sourceQuery, this, joinCondition);
  }
  leftSemijoin(right, on) {
    const filterQuery = new _FromBuilder(right);
    const joinCondition = on(
      this.table.indexedCols,
      right.indexedCols
    );
    return new SemijoinImpl(this, filterQuery, joinCondition);
  }
  toSql() {
    return renderSelectSqlWithJoins(this.table, this.whereClause);
  }
  build() {
    return this;
  }
};
var TableRefImpl = class {
  [QueryBrand] = true;
  type = "table";
  sourceName;
  accessorName;
  cols;
  indexedCols;
  tableDef;
  // Delegate UntypedTableDef properties from tableDef so this can be used as a table def.
  get columns() {
    return this.tableDef.columns;
  }
  get indexes() {
    return this.tableDef.indexes;
  }
  get rowType() {
    return this.tableDef.rowType;
  }
  get constraints() {
    return this.tableDef.constraints;
  }
  constructor(tableDef) {
    this.sourceName = tableDef.sourceName;
    this.accessorName = tableDef.accessorName;
    this.cols = createRowExpr(tableDef);
    this.indexedCols = this.cols;
    this.tableDef = tableDef;
    Object.freeze(this);
  }
  asFrom() {
    return new FromBuilder(this);
  }
  rightSemijoin(other, on) {
    return this.asFrom().rightSemijoin(other, on);
  }
  leftSemijoin(other, on) {
    return this.asFrom().leftSemijoin(other, on);
  }
  build() {
    return this.asFrom().build();
  }
  toSql() {
    return this.asFrom().toSql();
  }
  where(predicate) {
    return this.asFrom().where(predicate);
  }
};
function createTableRefFromDef(tableDef) {
  return new TableRefImpl(tableDef);
}
function makeQueryBuilder(schema2) {
  const qb = /* @__PURE__ */ Object.create(null);
  for (const table2 of Object.values(schema2.tables)) {
    const ref = createTableRefFromDef(
      table2
    );
    qb[table2.accessorName] = ref;
  }
  return Object.freeze(qb);
}
function makeFromBuilder(tables) {
  const result = /* @__PURE__ */ Object.create(null);
  const namespaces = /* @__PURE__ */ Object.create(
    null
  );
  for (const table2 of Object.values(tables)) {
    const dotIdx = table2.sourceName.indexOf(".");
    if (dotIdx === -1) {
      result[table2.accessorName] = createTableRefFromDef(table2);
    } else {
      const ns = table2.sourceName.slice(0, dotIdx);
      const key = table2.sourceName.slice(dotIdx + 1);
      (namespaces[ns] ??= /* @__PURE__ */ Object.create(null))[key] = createTableRefFromDef(
        table2
      );
    }
  }
  for (const [ns, nsTables] of Object.entries(namespaces)) {
    result[ns] = Object.freeze(nsTables);
  }
  return Object.freeze(result);
}
function createRowExpr(tableDef) {
  const row = {};
  for (const columnName of Object.keys(tableDef.columns)) {
    const columnBuilder = tableDef.columns[columnName];
    const column = new ColumnExpression(
      tableDef.sourceName,
      columnName,
      columnBuilder.typeBuilder.algebraicType,
      columnBuilder.columnMetadata.name
    );
    row[columnName] = Object.freeze(column);
  }
  return Object.freeze(row);
}
function renderSelectSqlWithJoins(table2, where, extraClauses = []) {
  const quotedTable = quoteIdentifier(table2.sourceName);
  const sql = `SELECT * FROM ${quotedTable}`;
  const clauses = [];
  if (where) clauses.push(booleanExprToSql(where));
  clauses.push(...extraClauses);
  if (clauses.length === 0) return sql;
  const whereSql = clauses.length === 1 ? clauses[0] : clauses.map(wrapInParens).join(" AND ");
  return `${sql} WHERE ${whereSql}`;
}
var ColumnExpression = class {
  type = "column";
  // This is the column accessor
  column;
  // The name of the column in the database.
  columnName;
  table;
  // phantom: actual runtime value is undefined
  tsValueType;
  spacetimeType;
  constructor(table2, column, spacetimeType, columnName) {
    this.table = table2;
    this.column = column;
    this.columnName = columnName || column;
    this.spacetimeType = spacetimeType;
  }
  eq(x) {
    return new BooleanExpr({
      type: "eq",
      left: this,
      right: normalizeValue(x)
    });
  }
  ne(x) {
    return new BooleanExpr({
      type: "ne",
      left: this,
      right: normalizeValue(x)
    });
  }
  lt(x) {
    return new BooleanExpr({
      type: "lt",
      left: this,
      right: normalizeValue(x)
    });
  }
  lte(x) {
    return new BooleanExpr({
      type: "lte",
      left: this,
      right: normalizeValue(x)
    });
  }
  gt(x) {
    return new BooleanExpr({
      type: "gt",
      left: this,
      right: normalizeValue(x)
    });
  }
  gte(x) {
    return new BooleanExpr({
      type: "gte",
      left: this,
      right: normalizeValue(x)
    });
  }
};
function literal(value) {
  return { type: "literal", value };
}
function normalizeValue(val) {
  if (val.type === "literal")
    return val;
  if (typeof val === "object" && val != null && "type" in val && val.type === "column") {
    return val;
  }
  return literal(val);
}
function normalizePredicateExpr(value) {
  if (value instanceof BooleanExpr) return value;
  if (typeof value === "boolean") {
    return new BooleanExpr({
      type: "eq",
      left: literal(value),
      right: literal(true)
    });
  }
  return new BooleanExpr({
    type: "eq",
    left: value,
    right: literal(true)
  });
}
var BooleanExpr = class _BooleanExpr {
  constructor(data) {
    this.data = data;
  }
  data;
  and(other) {
    return new _BooleanExpr({
      type: "and",
      clauses: [this.data, other.data]
    });
  }
  or(other) {
    return new _BooleanExpr({
      type: "or",
      clauses: [this.data, other.data]
    });
  }
  not() {
    return new _BooleanExpr({ type: "not", clause: this.data });
  }
};
function not(clause) {
  return new BooleanExpr({ type: "not", clause: clause.data });
}
function and(first, second, ...rest) {
  const clauses = [first, second, ...rest];
  return new BooleanExpr({
    type: "and",
    clauses: clauses.map((c) => c.data)
  });
}
function or(first, second, ...rest) {
  const clauses = [first, second, ...rest];
  return new BooleanExpr({
    type: "or",
    clauses: clauses.map((c) => c.data)
  });
}
function booleanExprToSql(expr, tableAlias) {
  const data = expr instanceof BooleanExpr ? expr.data : expr;
  switch (data.type) {
    case "eq":
      return `${valueExprToSql(data.left)} = ${valueExprToSql(data.right)}`;
    case "ne":
      return `${valueExprToSql(data.left)} <> ${valueExprToSql(data.right)}`;
    case "gt":
      return `${valueExprToSql(data.left)} > ${valueExprToSql(data.right)}`;
    case "gte":
      return `${valueExprToSql(data.left)} >= ${valueExprToSql(data.right)}`;
    case "lt":
      return `${valueExprToSql(data.left)} < ${valueExprToSql(data.right)}`;
    case "lte":
      return `${valueExprToSql(data.left)} <= ${valueExprToSql(data.right)}`;
    case "and":
      return data.clauses.map((c) => booleanExprToSql(c)).map(wrapInParens).join(" AND ");
    case "or":
      return data.clauses.map((c) => booleanExprToSql(c)).map(wrapInParens).join(" OR ");
    case "not":
      return `NOT ${wrapInParens(booleanExprToSql(data.clause))}`;
  }
}
function wrapInParens(sql) {
  return `(${sql})`;
}
function valueExprToSql(expr, tableAlias) {
  if (isLiteralExpr(expr)) {
    return literalValueToSql(expr.value);
  }
  const table2 = expr.table;
  return `${quoteIdentifier(table2)}.${quoteIdentifier(expr.columnName)}`;
}
function literalValueToSql(value) {
  if (value === null || value === void 0) {
    return "NULL";
  }
  if (value instanceof Identity || value instanceof ConnectionId || value instanceof Uuid) {
    return `0x${value.toHexString()}`;
  }
  if (value instanceof Timestamp) {
    return `'${value.toISOString()}'`;
  }
  switch (typeof value) {
    case "number":
    case "bigint":
      return String(value);
    case "boolean":
      return value ? "TRUE" : "FALSE";
    case "string":
      return `'${value.replace(/'/g, "''")}'`;
    default:
      return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
  }
}
function quoteIdentifier(name) {
  return name.split(".").map((part) => `"${part.replace(/"/g, '""')}"`).join(".");
}
function isLiteralExpr(expr) {
  return expr.type === "literal";
}
function evaluateBooleanExpr(expr, row) {
  return evaluateData(expr.data, row);
}
function evaluateData(data, row) {
  switch (data.type) {
    case "eq":
      return resolveValue(data.left, row) === resolveValue(data.right, row);
    case "ne":
      return resolveValue(data.left, row) !== resolveValue(data.right, row);
    case "gt":
      return resolveValue(data.left, row) > resolveValue(data.right, row);
    case "gte":
      return resolveValue(data.left, row) >= resolveValue(data.right, row);
    case "lt":
      return resolveValue(data.left, row) < resolveValue(data.right, row);
    case "lte":
      return resolveValue(data.left, row) <= resolveValue(data.right, row);
    case "and":
      return data.clauses.every((c) => evaluateData(c, row));
    case "or":
      return data.clauses.some((c) => evaluateData(c, row));
    case "not":
      return !evaluateData(data.clause, row);
  }
}
function resolveValue(expr, row) {
  if (isLiteralExpr(expr)) {
    return toComparableValue(expr.value);
  }
  return toComparableValue(row[expr.column]);
}
function isHexSerializableLike(value) {
  return !!value && typeof value === "object" && typeof value.toHexString === "function";
}
function isTimestampLike(value) {
  if (!value || typeof value !== "object") return false;
  if (value instanceof Timestamp) return true;
  const micros = value["__timestamp_micros_since_unix_epoch__"];
  return typeof micros === "bigint";
}
function toComparableValue(value) {
  if (isHexSerializableLike(value)) {
    return value.toHexString();
  }
  if (isTimestampLike(value)) {
    return value.__timestamp_micros_since_unix_epoch__;
  }
  return value;
}
function getQueryTableName(query) {
  if (query.table) return query.table.name;
  if (query.name) return query.name;
  if (query.sourceQuery) return query.sourceQuery.table.name;
  throw new Error("Cannot extract table name from query");
}
function getQueryAccessorName(query) {
  if (query.table) return query.table.accessorName;
  if (query.accessorName) return query.accessorName;
  if (query.sourceQuery) return query.sourceQuery.table.accessorName;
  throw new Error("Cannot extract accessor name from query");
}
function getQueryWhereClause(query) {
  if (query.whereClause) return query.whereClause;
  return void 0;
}

// src/lib/accessor_path.ts
function accessorSlot(root, accessorName) {
  const path = accessorName.split(".");
  const leaf = path.pop();
  let target = root;
  for (const segment of path) {
    target = target[segment] ??= /* @__PURE__ */ Object.create(null);
  }
  return [target, leaf];
}

// src/lib/type_util.ts
function set(x, t2) {
  return { ...x, ...t2 };
}

// src/lib/type_builders.ts
var TypeBuilder = class {
  /**
   * The TypeScript phantom type. This is not stored at runtime,
   * but is visible to the compiler
   */
  type;
  /**
   * The SpacetimeDB algebraic type (run‑time value). In addition to storing
   * the runtime representation of the `AlgebraicType`, it also captures
   * the TypeScript type information of the `AlgebraicType`. That is to say
   * the value is not merely an `AlgebraicType`, but is constructed to be
   * the corresponding concrete `AlgebraicType` for the TypeScript type `Type`.
   *
   * e.g. `string` corresponds to `AlgebraicType.String`
   */
  algebraicType;
  constructor(algebraicType) {
    this.algebraicType = algebraicType;
  }
  optional() {
    return new OptionBuilder(this);
  }
  /** Match this column to a receiver parameter in an outbox table. */
  param(opts) {
    return new ColumnBuilder(this, { paramName: opts.name });
  }
  serialize(writer, value) {
    const serialize = this.serialize = AlgebraicType.makeSerializer(
      this.algebraicType
    );
    serialize(writer, value);
  }
  deserialize(reader) {
    const deserialize = this.deserialize = AlgebraicType.makeDeserializer(
      this.algebraicType
    );
    return deserialize(reader);
  }
};
var U8Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.U8);
  }
  index(algorithm = "btree") {
    return new U8ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new U8ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new U8ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new U8ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new U8ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new U8ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var U16Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.U16);
  }
  index(algorithm = "btree") {
    return new U16ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new U16ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new U16ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new U16ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new U16ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new U16ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var U32Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.U32);
  }
  index(algorithm = "btree") {
    return new U32ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new U32ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new U32ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new U32ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new U32ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new U32ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var U64Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.U64);
  }
  index(algorithm = "btree") {
    return new U64ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new U64ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new U64ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new U64ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new U64ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new U64ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var U128Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.U128);
  }
  index(algorithm = "btree") {
    return new U128ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new U128ColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new U128ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new U128ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new U128ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new U128ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var U256Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.U256);
  }
  index(algorithm = "btree") {
    return new U256ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new U256ColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new U256ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new U256ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new U256ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new U256ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var I8Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.I8);
  }
  index(algorithm = "btree") {
    return new I8ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new I8ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new I8ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new I8ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new I8ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new I8ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var I16Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.I16);
  }
  index(algorithm = "btree") {
    return new I16ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new I16ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new I16ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new I16ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new I16ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new I16ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var I32Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.I32);
  }
  index(algorithm = "btree") {
    return new I32ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new I32ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new I32ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new I32ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new I32ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new I32ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var I64Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.I64);
  }
  index(algorithm = "btree") {
    return new I64ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new I64ColumnBuilder(this, set(defaultMetadata, { isUnique: true }));
  }
  primaryKey() {
    return new I64ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new I64ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new I64ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new I64ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var I128Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.I128);
  }
  index(algorithm = "btree") {
    return new I128ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new I128ColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new I128ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new I128ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new I128ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new I128ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var I256Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.I256);
  }
  index(algorithm = "btree") {
    return new I256ColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new I256ColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new I256ColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new I256ColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new I256ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new I256ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var F32Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.F32);
  }
  default(value) {
    return new F32ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new F32ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var F64Builder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.F64);
  }
  default(value) {
    return new F64ColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new F64ColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var BoolBuilder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.Bool);
  }
  index(algorithm = "btree") {
    return new BoolColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new BoolColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new BoolColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new BoolColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new BoolColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var StringBuilder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.String);
  }
  index(algorithm = "btree") {
    return new StringColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new StringColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new StringColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new StringColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new StringColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var ArrayBuilder = class extends TypeBuilder {
  element;
  constructor(element) {
    super(AlgebraicType.Array(element.algebraicType));
    this.element = element;
  }
  default(value) {
    return new ArrayColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new ArrayColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var ByteArrayBuilder = class extends TypeBuilder {
  constructor() {
    super(AlgebraicType.Array(AlgebraicType.U8));
  }
  default(value) {
    return new ByteArrayColumnBuilder(
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new ByteArrayColumnBuilder(set(defaultMetadata, { name }));
  }
};
var OptionBuilder = class extends TypeBuilder {
  value;
  constructor(value) {
    super(Option.getAlgebraicType(value.algebraicType));
    this.value = value;
  }
  default(value) {
    return new OptionColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new OptionColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var ProductBuilder = class extends TypeBuilder {
  typeName;
  elements;
  constructor(elements, name) {
    function elementsArrayFromElementsObj(obj) {
      return Object.keys(obj).map((key) => ({
        name: key,
        // Lazily resolve the underlying object's algebraicType.
        // This will call obj[key].algebraicType only when someone
        // actually reads this property.
        get algebraicType() {
          return obj[key].algebraicType;
        }
      }));
    }
    super(
      AlgebraicType.Product({
        elements: elementsArrayFromElementsObj(elements)
      })
    );
    this.typeName = name;
    this.elements = elements;
  }
  default(value) {
    return new ProductColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new ProductColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var ResultBuilder = class extends TypeBuilder {
  ok;
  err;
  constructor(ok, err) {
    super(Result.getAlgebraicType(ok.algebraicType, err.algebraicType));
    this.ok = ok;
    this.err = err;
  }
  default(value) {
    return new ResultColumnBuilder(this, set(defaultMetadata, { defaultValue: value }));
  }
};
var UnitBuilder = class extends TypeBuilder {
  constructor() {
    super({ tag: "Product", value: { elements: [] } });
  }
};
var RowBuilder = class extends TypeBuilder {
  row;
  typeName;
  constructor(row, name) {
    const mappedRow = Object.fromEntries(
      Object.entries(row).map(([colName, builder]) => [
        colName,
        builder instanceof ColumnBuilder ? builder : new ColumnBuilder(builder, {})
      ])
    );
    const elements = Object.keys(mappedRow).map((name2) => ({
      name: name2,
      get algebraicType() {
        return mappedRow[name2].typeBuilder.algebraicType;
      }
    }));
    super(AlgebraicType.Product({ elements }));
    this.row = mappedRow;
    this.typeName = name;
  }
};
var SumBuilderImpl = class extends TypeBuilder {
  variants;
  typeName;
  constructor(variants, name) {
    function variantsArrayFromVariantsObj(variants2) {
      return Object.keys(variants2).map((key) => ({
        name: key,
        // Lazily resolve the underlying object's algebraicType.
        // This will call obj[key].algebraicType only when someone
        // actually reads this property.
        get algebraicType() {
          return variants2[key].algebraicType;
        }
      }));
    }
    super(
      AlgebraicType.Sum({
        variants: variantsArrayFromVariantsObj(variants)
      })
    );
    this.variants = variants;
    this.typeName = name;
    for (const key of Object.keys(variants)) {
      const desc = Object.getOwnPropertyDescriptor(variants, key);
      const isAccessor = !!desc && (typeof desc.get === "function" || typeof desc.set === "function");
      let isUnit2 = false;
      if (!isAccessor) {
        const variant = variants[key];
        isUnit2 = variant instanceof UnitBuilder;
      }
      if (isUnit2) {
        const constant = this.create(key);
        Object.defineProperty(this, key, {
          value: constant,
          writable: false,
          enumerable: true,
          configurable: false
        });
      } else {
        const fn = ((value) => this.create(key, value));
        Object.defineProperty(this, key, {
          value: fn,
          writable: false,
          enumerable: true,
          configurable: false
        });
      }
    }
  }
  create(tag, value) {
    return value === void 0 ? { tag } : { tag, value };
  }
  default(value) {
    return new SumColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new SumColumnBuilder(this, set(defaultMetadata, { name }));
  }
  index(algorithm = "btree") {
    return new SumColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  primaryKey() {
    return new SumColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
};
var SumBuilder = SumBuilderImpl;
var SimpleSumBuilderImpl = class extends SumBuilderImpl {
  index(algorithm = "btree") {
    return new SimpleSumColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  primaryKey() {
    return new SimpleSumColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
};
var SimpleSumBuilder = SimpleSumBuilderImpl;
var ScheduleAtBuilder = class extends TypeBuilder {
  constructor() {
    super(schedule_at_default.getAlgebraicType());
  }
  default(value) {
    return new ScheduleAtColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new ScheduleAtColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var IdentityBuilder = class extends TypeBuilder {
  constructor() {
    super(Identity.getAlgebraicType());
  }
  /** Marks the destination database column of an IDC outbox. */
  target() {
    return new IdentityColumnBuilder(this, { isTarget: true });
  }
  index(algorithm = "btree") {
    return new IdentityColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new IdentityColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new IdentityColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new IdentityColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new IdentityColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new IdentityColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var ConnectionIdBuilder = class extends TypeBuilder {
  constructor() {
    super(ConnectionId.getAlgebraicType());
  }
  index(algorithm = "btree") {
    return new ConnectionIdColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new ConnectionIdColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new ConnectionIdColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new ConnectionIdColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new ConnectionIdColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new ConnectionIdColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var TimestampBuilder = class extends TypeBuilder {
  constructor() {
    super(Timestamp.getAlgebraicType());
  }
  index(algorithm = "btree") {
    return new TimestampColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new TimestampColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new TimestampColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new TimestampColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new TimestampColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new TimestampColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var TimeDurationBuilder = class extends TypeBuilder {
  constructor() {
    super(TimeDuration.getAlgebraicType());
  }
  index(algorithm = "btree") {
    return new TimeDurationColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new TimeDurationColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new TimeDurationColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new TimeDurationColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new TimeDurationColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new TimeDurationColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var UuidBuilder = class extends TypeBuilder {
  constructor() {
    super(Uuid.getAlgebraicType());
  }
  index(algorithm = "btree") {
    return new UuidColumnBuilder(
      this,
      set(defaultMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new UuidColumnBuilder(
      this,
      set(defaultMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new UuidColumnBuilder(
      this,
      set(defaultMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new UuidColumnBuilder(
      this,
      set(defaultMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new UuidColumnBuilder(
      this,
      set(defaultMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new UuidColumnBuilder(this, set(defaultMetadata, { name }));
  }
};
var defaultMetadata = {};
var ColumnBuilder = class _ColumnBuilder {
  typeBuilder;
  columnMetadata;
  constructor(typeBuilder, metadata) {
    this.typeBuilder = typeBuilder;
    this.columnMetadata = metadata;
  }
  /** Match this column to a receiver parameter in an outbox table. */
  param(opts) {
    return new _ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { paramName: opts.name })
    );
  }
  serialize(writer, value) {
    this.typeBuilder.serialize(writer, value);
  }
  deserialize(reader) {
    return this.typeBuilder.deserialize(reader);
  }
};
var U8ColumnBuilder = class _U8ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _U8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _U8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _U8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _U8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _U8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _U8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var U16ColumnBuilder = class _U16ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _U16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _U16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _U16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _U16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _U16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _U16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var U32ColumnBuilder = class _U32ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _U32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _U32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _U32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _U32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _U32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _U32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var U64ColumnBuilder = class _U64ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _U64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _U64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _U64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _U64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _U64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _U64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var U128ColumnBuilder = class _U128ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _U128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _U128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _U128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _U128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _U128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _U128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var U256ColumnBuilder = class _U256ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _U256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _U256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _U256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _U256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _U256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _U256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var I8ColumnBuilder = class _I8ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _I8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _I8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _I8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _I8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _I8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _I8ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var I16ColumnBuilder = class _I16ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _I16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _I16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _I16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _I16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _I16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _I16ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var I32ColumnBuilder = class _I32ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _I32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _I32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _I32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _I32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _I32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _I32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var I64ColumnBuilder = class _I64ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _I64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _I64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _I64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _I64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _I64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _I64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var I128ColumnBuilder = class _I128ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _I128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _I128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _I128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _I128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _I128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _I128ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var I256ColumnBuilder = class _I256ColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _I256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _I256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _I256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  autoInc() {
    return new _I256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isAutoIncrement: true })
    );
  }
  default(value) {
    return new _I256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _I256ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var F32ColumnBuilder = class _F32ColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _F32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _F32ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var F64ColumnBuilder = class _F64ColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _F64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _F64ColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var BoolColumnBuilder = class _BoolColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _BoolColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _BoolColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _BoolColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _BoolColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _BoolColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var StringColumnBuilder = class _StringColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _StringColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _StringColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _StringColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _StringColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _StringColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var ArrayColumnBuilder = class _ArrayColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _ArrayColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _ArrayColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var ByteArrayColumnBuilder = class _ByteArrayColumnBuilder extends ColumnBuilder {
  constructor(metadata) {
    super(new TypeBuilder(AlgebraicType.Array(AlgebraicType.U8)), metadata);
  }
  default(value) {
    return new _ByteArrayColumnBuilder(
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _ByteArrayColumnBuilder(set(this.columnMetadata, { name }));
  }
};
var OptionColumnBuilder = class _OptionColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _OptionColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
  name(name) {
    return new _OptionColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var ResultColumnBuilder = class _ResultColumnBuilder extends ColumnBuilder {
  constructor(typeBuilder, metadata) {
    super(typeBuilder, metadata);
  }
  default(value) {
    return new _ResultColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, {
        defaultValue: value
      })
    );
  }
};
var ProductColumnBuilder = class _ProductColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _ProductColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _ProductColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var SumColumnBuilder = class _SumColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _SumColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _SumColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
  index(algorithm = "btree") {
    return new _SumColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  primaryKey() {
    return new _SumColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
};
var SimpleSumColumnBuilder = class _SimpleSumColumnBuilder extends SumColumnBuilder {
  index(algorithm = "btree") {
    return new _SimpleSumColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  primaryKey() {
    return new _SimpleSumColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
};
var ScheduleAtColumnBuilder = class _ScheduleAtColumnBuilder extends ColumnBuilder {
  default(value) {
    return new _ScheduleAtColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _ScheduleAtColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var IdentityColumnBuilder = class _IdentityColumnBuilder extends ColumnBuilder {
  target() {
    return new _IdentityColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isTarget: true })
    );
  }
  index(algorithm = "btree") {
    return new _IdentityColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _IdentityColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _IdentityColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _IdentityColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _IdentityColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var ConnectionIdColumnBuilder = class _ConnectionIdColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _ConnectionIdColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _ConnectionIdColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _ConnectionIdColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _ConnectionIdColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _ConnectionIdColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var TimestampColumnBuilder = class _TimestampColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _TimestampColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _TimestampColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _TimestampColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _TimestampColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _TimestampColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var TimeDurationColumnBuilder = class _TimeDurationColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _TimeDurationColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _TimeDurationColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _TimeDurationColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _TimeDurationColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _TimeDurationColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var UuidColumnBuilder = class _UuidColumnBuilder extends ColumnBuilder {
  index(algorithm = "btree") {
    return new _UuidColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { indexType: algorithm })
    );
  }
  unique() {
    return new _UuidColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isUnique: true })
    );
  }
  primaryKey() {
    return new _UuidColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { isPrimaryKey: true })
    );
  }
  default(value) {
    return new _UuidColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { defaultValue: value })
    );
  }
  name(name) {
    return new _UuidColumnBuilder(
      this.typeBuilder,
      set(this.columnMetadata, { name })
    );
  }
};
var RefBuilder = class extends TypeBuilder {
  ref;
  /** The phantom type of the pointee of this ref. */
  __spacetimeType;
  constructor(ref) {
    super(AlgebraicType.Ref(ref));
    this.ref = ref;
  }
};
var enumImpl = ((nameOrObj, maybeObj) => {
  let obj = nameOrObj;
  let name = void 0;
  if (typeof nameOrObj === "string") {
    if (!maybeObj) {
      throw new TypeError(
        "When providing a name, you must also provide the variants object or array."
      );
    }
    obj = maybeObj;
    name = nameOrObj;
  }
  if (Array.isArray(obj)) {
    const simpleVariantsObj = {};
    for (const variant of obj) {
      simpleVariantsObj[variant] = new UnitBuilder();
    }
    return new SimpleSumBuilderImpl(simpleVariantsObj, name);
  }
  return new SumBuilder(obj, name);
});
var t = {
  /**
   * Creates a new `Bool` {@link AlgebraicType} to be used in table definitions
   * Represented as `boolean` in TypeScript.
   * @returns A new {@link BoolBuilder} instance
   */
  bool: () => new BoolBuilder(),
  /**
   * Creates a new `String` {@link AlgebraicType} to be used in table definitions
   * Represented as `string` in TypeScript.
   * @returns A new {@link StringBuilder} instance
   */
  string: () => new StringBuilder(),
  /**
   * Creates a new `F64` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link F64Builder} instance
   */
  number: () => new F64Builder(),
  /**
   * Creates a new `I8` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link I8Builder} instance
   */
  i8: () => new I8Builder(),
  /**
   * Creates a new `U8` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link U8Builder} instance
   */
  u8: () => new U8Builder(),
  /**
   * Creates a new `I16` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link I16Builder} instance
   */
  i16: () => new I16Builder(),
  /**
   * Creates a new `U16` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link U16Builder} instance
   */
  u16: () => new U16Builder(),
  /**
   * Creates a new `I32` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link I32Builder} instance
   */
  i32: () => new I32Builder(),
  /**
   * Creates a new `U32` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link U32Builder} instance
   */
  u32: () => new U32Builder(),
  /**
   * Creates a new `I64` {@link AlgebraicType} to be used in table definitions
   * Represented as `bigint` in TypeScript.
   * @returns A new {@link I64Builder} instance
   */
  i64: () => new I64Builder(),
  /**
   * Creates a new `U64` {@link AlgebraicType} to be used in table definitions
   * Represented as `bigint` in TypeScript.
   * @returns A new {@link U64Builder} instance
   */
  u64: () => new U64Builder(),
  /**
   * Creates a new `I128` {@link AlgebraicType} to be used in table definitions
   * Represented as `bigint` in TypeScript.
   * @returns A new {@link I128Builder} instance
   */
  i128: () => new I128Builder(),
  /**
   * Creates a new `U128` {@link AlgebraicType} to be used in table definitions
   * Represented as `bigint` in TypeScript.
   * @returns A new {@link U128Builder} instance
   */
  u128: () => new U128Builder(),
  /**
   * Creates a new `I256` {@link AlgebraicType} to be used in table definitions
   * Represented as `bigint` in TypeScript.
   * @returns A new {@link I256Builder} instance
   */
  i256: () => new I256Builder(),
  /**
   * Creates a new `U256` {@link AlgebraicType} to be used in table definitions
   * Represented as `bigint` in TypeScript.
   * @returns A new {@link U256Builder} instance
   */
  u256: () => new U256Builder(),
  /**
   * Creates a new `F32` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link F32Builder} instance
   */
  f32: () => new F32Builder(),
  /**
   * Creates a new `F64` {@link AlgebraicType} to be used in table definitions
   * Represented as `number` in TypeScript.
   * @returns A new {@link F64Builder} instance
   */
  f64: () => new F64Builder(),
  /**
   * Creates a new `Product` {@link AlgebraicType} to be used in table definitions. Product types in SpacetimeDB
   * are essentially the same as objects in JavaScript/TypeScript.
   * Properties of the object must also be {@link TypeBuilder}s.
   * Represented as an object with specific properties in TypeScript.
   *
   * @param name (optional) A display name for the product type. If omitted, an anonymous product type is created.
   * @param obj The object defining the properties of the type, whose property
   * values must be {@link TypeBuilder}s.
   * @returns A new {@link ProductBuilder} instance.
   */
  object: ((nameOrObj, maybeObj) => {
    if (typeof nameOrObj === "string") {
      if (!maybeObj) {
        throw new TypeError(
          "When providing a name, you must also provide the object."
        );
      }
      return new ProductBuilder(maybeObj, nameOrObj);
    }
    return new ProductBuilder(nameOrObj, void 0);
  }),
  /**
   * Creates a new `Row` {@link AlgebraicType} to be used in table definitions. Row types in SpacetimeDB
   * are similar to `Product` types, but are specifically used to define the schema of a table row.
   * Properties of the object must also be {@link TypeBuilder} or {@link ColumnBuilder}s.
   *
   * You can represent a `Row` as either a {@link RowObj} or an {@link RowBuilder} type when
   * defining a table schema.
   *
   * The {@link RowBuilder} type is useful when you want to create a type which can be used anywhere
   * a {@link TypeBuilder} is accepted, such as in nested objects or arrays, or as the argument
   * to a scheduled function.
   *
   * @param obj The object defining the properties of the row, whose property
   * values must be {@link TypeBuilder}s or {@link ColumnBuilder}s.
   * @returns A new {@link RowBuilder} instance
   */
  row: ((nameOrObj, maybeObj) => {
    const [obj, name] = typeof nameOrObj === "string" ? [maybeObj, nameOrObj] : [nameOrObj, void 0];
    return new RowBuilder(obj, name);
  }),
  /**
   * Creates a new `Array` {@link AlgebraicType} to be used in table definitions.
   * Represented as an array in TypeScript.
   * @param element The element type of the array, which must be a `TypeBuilder`.
   * @returns A new {@link ArrayBuilder} instance
   */
  array(e) {
    return new ArrayBuilder(e);
  },
  enum: enumImpl,
  /**
   * This is a special helper function for conveniently creating `Product` type columns with no fields.
   *
   * @returns A new {@link ProductBuilder} instance with no fields.
   */
  unit() {
    return new UnitBuilder();
  },
  /**
   * Creates a lazily-evaluated {@link TypeBuilder}. This is useful for creating
   * recursive types, such as a tree or linked list.
   * @param thunk A function that returns a {@link TypeBuilder}.
   * @returns A proxy {@link TypeBuilder} that evaluates the thunk on first access.
   */
  lazy(thunk) {
    let cached = null;
    const get = () => cached ??= thunk();
    const proxy = new Proxy({}, {
      get(_t, prop, recv) {
        const target = get();
        const val = Reflect.get(target, prop, recv);
        return typeof val === "function" ? val.bind(target) : val;
      },
      set(_t, prop, value, recv) {
        return Reflect.set(get(), prop, value, recv);
      },
      has(_t, prop) {
        return prop in get();
      },
      ownKeys() {
        return Reflect.ownKeys(get());
      },
      getOwnPropertyDescriptor(_t, prop) {
        return Object.getOwnPropertyDescriptor(get(), prop);
      },
      getPrototypeOf() {
        return Object.getPrototypeOf(get());
      }
    });
    return proxy;
  },
  /**
   * This is a special helper function for conveniently creating {@link ScheduleAt} type columns.
   * @returns A new ColumnBuilder instance with the {@link ScheduleAt} type.
   */
  scheduleAt: () => {
    return new ScheduleAtBuilder();
  },
  /**
   * This is a convenience method for creating a column with the {@link Option} type.
   * You can create a column of the same type by constructing an enum with a `some` and `none` variant.
   * @param value The type of the value contained in the `some` variant of the `Option`.
   * @returns A new {@link OptionBuilder} instance with the {@link Option} type.
   */
  option(value) {
    return new OptionBuilder(value);
  },
  /**
   * This is a convenience method for creating a column with the {@link Result} type.
   * You can create a column of the same type by constructing an enum with an `ok` and `err` variant.
   * @param ok The type of the value contained in the `ok` variant of the `Result`.
   * @param err The type of the value contained in the `err` variant of the `Result`.
   * @returns A new {@link ResultBuilder} instance with the {@link Result} type.
   */
  result(ok, err) {
    return new ResultBuilder(ok, err);
  },
  /**
   * This is a convenience method for creating a column with the {@link Identity} type.
   * You can create a column of the same type by constructing an `object` with a single `__identity__` element.
   * @returns A new {@link TypeBuilder} instance with the {@link Identity} type.
   */
  identity: () => {
    return new IdentityBuilder();
  },
  /**
   * This is a convenience method for creating a column with the {@link ConnectionId} type.
   * You can create a column of the same type by constructing an `object` with a single `__connection_id__` element.
   * @returns A new {@link TypeBuilder} instance with the {@link ConnectionId} type.
   */
  connectionId: () => {
    return new ConnectionIdBuilder();
  },
  /**
   * This is a convenience method for creating a column with the {@link Timestamp} type.
   * You can create a column of the same type by constructing an `object` with a single `__timestamp_micros_since_unix_epoch__` element.
   * @returns A new {@link TypeBuilder} instance with the {@link Timestamp} type.
   */
  timestamp: () => {
    return new TimestampBuilder();
  },
  /**
   * This is a convenience method for creating a column with the {@link TimeDuration} type.
   * You can create a column of the same type by constructing an `object` with a single `__time_duration_micros__` element.
   * @returns A new {@link TypeBuilder} instance with the {@link TimeDuration} type.
   */
  timeDuration: () => {
    return new TimeDurationBuilder();
  },
  /**
   * This is a convenience method for creating a column with the {@link Uuid} type.
   * You can create a column of the same type by constructing an `object` with a single `__uuid__` element.
   * @returns A new {@link TypeBuilder} instance with the {@link Uuid} type.
   */
  uuid: () => {
    return new UuidBuilder();
  },
  /**
   * This is a convenience method for creating a column with the `ByteArray` type.
   * You can create a column of the same type by constructing an `array` of `u8`.
   * The TypeScript representation is {@link Uint8Array}.
   * @returns A new {@link ByteArrayBuilder} instance with the `ByteArray` type.
   */
  byteArray: () => {
    return new ByteArrayBuilder();
  }
};

// src/sdk/client_api/types.ts
var BsatnRowList = t.object("BsatnRowList", {
  get sizeHint() {
    return RowSizeHint;
  },
  rowsData: t.byteArray()
});
var CallProcedure = t.object("CallProcedure", {
  requestId: t.u32(),
  flags: t.u8(),
  procedure: t.string(),
  args: t.byteArray()
});
var CallReducer = t.object("CallReducer", {
  requestId: t.u32(),
  flags: t.u8(),
  reducer: t.string(),
  args: t.byteArray()
});
var ClientMessage = t.enum("ClientMessage", {
  get Subscribe() {
    return Subscribe;
  },
  get Unsubscribe() {
    return Unsubscribe;
  },
  get OneOffQuery() {
    return OneOffQuery;
  },
  get CallReducer() {
    return CallReducer;
  },
  get CallProcedure() {
    return CallProcedure;
  },
  get SubscribeBatch() {
    return SubscribeBatch;
  }
});
var EventTableRows = t.object("EventTableRows", {
  get events() {
    return BsatnRowList;
  }
});
var InitialConnection = t.object("InitialConnection", {
  identity: t.identity(),
  connectionId: t.connectionId(),
  token: t.string()
});
var OneOffQuery = t.object("OneOffQuery", {
  requestId: t.u32(),
  queryString: t.string()
});
var OneOffQueryResult = t.object("OneOffQueryResult", {
  requestId: t.u32(),
  get result() {
    return t.result(QueryRows, t.string());
  }
});
var PauseReplay = t.object("PauseReplay", {
  requestId: t.u32()
});
var PersistentTableRows = t.object("PersistentTableRows", {
  get inserts() {
    return BsatnRowList;
  },
  get deletes() {
    return BsatnRowList;
  }
});
var ProcedureResult = t.object("ProcedureResult", {
  get status() {
    return ProcedureStatus;
  },
  timestamp: t.timestamp(),
  totalHostExecutionDuration: t.timeDuration(),
  requestId: t.u32()
});
var ProcedureStatus = t.enum("ProcedureStatus", {
  Returned: t.byteArray(),
  InternalError: t.string()
});
var QueryRows = t.object("QueryRows", {
  get tables() {
    return t.array(SingleTableRows);
  }
});
var QuerySetId = t.object("QuerySetId", {
  id: t.u32()
});
var QuerySetUpdate = t.object("QuerySetUpdate", {
  get querySetId() {
    return QuerySetId;
  },
  get tables() {
    return t.array(TableUpdate);
  }
});
var ReducerOk = t.object("ReducerOk", {
  retValue: t.byteArray(),
  get transactionUpdate() {
    return TransactionUpdate;
  }
});
var ReducerOutcome = t.enum("ReducerOutcome", {
  get Ok() {
    return ReducerOk;
  },
  OkEmpty: t.unit(),
  Err: t.byteArray(),
  InternalError: t.string()
});
var ReducerResult = t.object("ReducerResult", {
  requestId: t.u32(),
  timestamp: t.timestamp(),
  get result() {
    return ReducerOutcome;
  }
});
var ReplayCaughtUp = t.object("ReplayCaughtUp", {
  replayTimeMicros: t.u64()
});
var ReplayClientMessage = t.enum("ReplayClientMessage", {
  get StartReplay() {
    return StartReplay;
  },
  get SetReplaySpeed() {
    return SetReplaySpeed;
  },
  get SetReplayDirection() {
    return SetReplayDirection;
  },
  get SetReplayPosition() {
    return SetReplayPosition;
  },
  get PauseReplay() {
    return PauseReplay;
  },
  get ResumeReplay() {
    return ResumeReplay;
  },
  get StartReplayPrecise() {
    return StartReplayPrecise;
  },
  get SetReplayDensity() {
    return SetReplayDensity;
  }
});
var ReplayDensityChanged = t.object("ReplayDensityChanged", {
  requestId: t.u32(),
  desiredSamplesPerFrame: t.u16()
});
var ReplayDirectionChanged = t.object("ReplayDirectionChanged", {
  requestId: t.u32(),
  forward: t.bool()
});
var ReplayEndOfHistory = t.object("ReplayEndOfHistory", {
  replayTimeMicros: t.u64()
});
var ReplayError = t.object("ReplayError", {
  requestId: t.u32(),
  message: t.string()
});
var ReplayHeartbeat = t.object("ReplayHeartbeat", {
  replayTimeMicros: t.u64()
});
var ReplayPaused = t.object("ReplayPaused", {
  requestId: t.u32()
});
var ReplayProcedureAuth = t.object("ReplayProcedureAuth", {
  procedure: t.string(),
  args: t.byteArray()
});
var ReplayResumed = t.object("ReplayResumed", {
  requestId: t.u32()
});
var ReplaySeeked = t.object("ReplaySeeked", {
  requestId: t.u32(),
  toMicros: t.u64()
});
var ReplayServerMessage = t.enum("ReplayServerMessage", {
  get ReplayStarted() {
    return ReplayStarted;
  },
  get ReplaySpeedChanged() {
    return ReplaySpeedChanged;
  },
  get ReplayDirectionChanged() {
    return ReplayDirectionChanged;
  },
  get ReplaySeeked() {
    return ReplaySeeked;
  },
  get ReplayPaused() {
    return ReplayPaused;
  },
  get ReplayResumed() {
    return ReplayResumed;
  },
  get ReplayCaughtUp() {
    return ReplayCaughtUp;
  },
  get ReplayEndOfHistory() {
    return ReplayEndOfHistory;
  },
  get ReplayError() {
    return ReplayError;
  },
  get ReplayDensityChanged() {
    return ReplayDensityChanged;
  },
  get ReplayHeartbeat() {
    return ReplayHeartbeat;
  },
  get ReplayTimestampedUpdate() {
    return ReplayTimestampedUpdate;
  }
});
var ReplaySpeedChanged = t.object("ReplaySpeedChanged", {
  requestId: t.u32(),
  speed: t.f32()
});
var ReplayStarted = t.object("ReplayStarted", {
  requestId: t.u32(),
  fromMicros: t.u64(),
  speed: t.f32(),
  forward: t.bool()
});
var ReplayTimestampedUpdate = t.object("ReplayTimestampedUpdate", {
  get tables() {
    return t.array(TimestampedTableUpdate);
  }
});
var ResumeReplay = t.object("ResumeReplay", {
  requestId: t.u32()
});
var RowSizeHint = t.enum("RowSizeHint", {
  FixedSize: t.u16(),
  RowOffsets: t.array(t.u64())
});
var ServerMessage = t.enum("ServerMessage", {
  get InitialConnection() {
    return InitialConnection;
  },
  get SubscribeApplied() {
    return SubscribeApplied;
  },
  get UnsubscribeApplied() {
    return UnsubscribeApplied;
  },
  get SubscriptionError() {
    return SubscriptionError;
  },
  get TransactionUpdate() {
    return TransactionUpdate;
  },
  get OneOffQueryResult() {
    return OneOffQueryResult;
  },
  get ReducerResult() {
    return ReducerResult;
  },
  get ProcedureResult() {
    return ProcedureResult;
  },
  get SubscribeBatchApplied() {
    return SubscribeBatchApplied;
  }
});
var SetReplayDensity = t.object("SetReplayDensity", {
  requestId: t.u32(),
  desiredSamplesPerFrame: t.u16()
});
var SetReplayDirection = t.object("SetReplayDirection", {
  requestId: t.u32(),
  forward: t.bool()
});
var SetReplayPosition = t.object("SetReplayPosition", {
  requestId: t.u32(),
  toMicros: t.u64()
});
var SetReplaySpeed = t.object("SetReplaySpeed", {
  requestId: t.u32(),
  speed: t.f32()
});
var SingleTableRows = t.object("SingleTableRows", {
  table: t.string(),
  get rows() {
    return BsatnRowList;
  }
});
var StartReplay = t.object("StartReplay", {
  requestId: t.u32(),
  fromMicros: t.u64(),
  speed: t.f32(),
  get procedure() {
    return t.option(ReplayProcedureAuth);
  }
});
var StartReplayPrecise = t.object("StartReplayPrecise", {
  requestId: t.u32(),
  fromMicros: t.u64(),
  speed: t.f32(),
  desiredSamplesPerFrame: t.u16(),
  get procedure() {
    return t.option(ReplayProcedureAuth);
  }
});
var Subscribe = t.object("Subscribe", {
  requestId: t.u32(),
  get querySetId() {
    return QuerySetId;
  },
  queryStrings: t.array(t.string())
});
var SubscribeApplied = t.object("SubscribeApplied", {
  requestId: t.u32(),
  get querySetId() {
    return QuerySetId;
  },
  get rows() {
    return QueryRows;
  }
});
var SubscribeBatch = t.object("SubscribeBatch", {
  requestId: t.u32(),
  get sets() {
    return t.array(SubscribeSet);
  }
});
var SubscribeBatchApplied = t.object("SubscribeBatchApplied", {
  requestId: t.u32(),
  get results() {
    return t.array(SubscribeSetResult);
  }
});
var SubscribeSet = t.object("SubscribeSet", {
  get querySetId() {
    return QuerySetId;
  },
  queryStrings: t.array(t.string())
});
var SubscribeSetOutcome = t.enum("SubscribeSetOutcome", {
  get Applied() {
    return QueryRows;
  },
  Error: t.string()
});
var SubscribeSetResult = t.object("SubscribeSetResult", {
  get querySetId() {
    return QuerySetId;
  },
  get outcome() {
    return SubscribeSetOutcome;
  }
});
var SubscriptionError = t.object("SubscriptionError", {
  requestId: t.option(t.u32()),
  get querySetId() {
    return QuerySetId;
  },
  error: t.string()
});
var TableUpdate = t.object("TableUpdate", {
  tableName: t.string(),
  get rows() {
    return t.array(TableUpdateRows);
  }
});
var TableUpdateRows = t.enum("TableUpdateRows", {
  get PersistentTable() {
    return PersistentTableRows;
  },
  get EventTable() {
    return EventTableRows;
  }
});
var TimestampedTableUpdate = t.object("TimestampedTableUpdate", {
  tableName: t.string(),
  get inserts() {
    return BsatnRowList;
  },
  get deletes() {
    return BsatnRowList;
  }
});
var TransactionUpdate = t.object("TransactionUpdate", {
  get querySets() {
    return t.array(QuerySetUpdate);
  }
});
t.enum("TtClientMessage", {
  get Std() {
    return ClientMessage;
  },
  get Replay() {
    return ReplayClientMessage;
  }
});
var TtServerMessage = t.enum("TtServerMessage", {
  get Std() {
    return ServerMessage;
  },
  get Replay() {
    return ReplayServerMessage;
  }
});
var Unsubscribe = t.object("Unsubscribe", {
  requestId: t.u32(),
  get querySetId() {
    return QuerySetId;
  },
  get flags() {
    return UnsubscribeFlags;
  }
});
var UnsubscribeApplied = t.object("UnsubscribeApplied", {
  requestId: t.u32(),
  get querySetId() {
    return QuerySetId;
  },
  get rows() {
    return t.option(QueryRows);
  }
});
var UnsubscribeFlags = t.enum("UnsubscribeFlags", {
  Default: t.unit(),
  SendDroppedRows: t.unit()
});

// src/sdk/deferred.ts
function createDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

// src/sdk/event_emitter.ts
var EventEmitter = class {
  #events = /* @__PURE__ */ new Map();
  on(event, callback) {
    let callbacks = this.#events.get(event);
    if (!callbacks) {
      callbacks = /* @__PURE__ */ new Set();
      this.#events.set(event, callbacks);
    }
    callbacks.add(callback);
  }
  off(event, callback) {
    const callbacks = this.#events.get(event);
    if (!callbacks) {
      return;
    }
    callbacks.delete(callback);
  }
  emit(event, ...args) {
    const callbacks = this.#events.get(event);
    if (!callbacks) {
      return;
    }
    for (const callback of callbacks) {
      callback(...args);
    }
  }
};
var LogLevelIdentifierIcon = {
  component: "\u{1F4E6}",
  info: "\u2139\uFE0F",
  warn: "\u26A0\uFE0F",
  error: "\u274C",
  debug: "\u{1F41B}",
  trace: "\u{1F50D}"
};
var LogStyle = {
  component: "color: #fff; background-color: #8D6FDD; padding: 2px 5px; border-radius: 3px;",
  info: "color: #fff; background-color: #007bff; padding: 2px 5px; border-radius: 3px;",
  warn: "color: #fff; background-color: #ffc107; padding: 2px 5px; border-radius: 3px;",
  error: "color: #fff; background-color: #dc3545; padding: 2px 5px; border-radius: 3px;",
  debug: "color: #fff; background-color: #28a745; padding: 2px 5px; border-radius: 3px;",
  trace: "color: #fff; background-color: #17a2b8; padding: 2px 5px; border-radius: 3px;"
};
var LogTextStyle = {
  component: "color: #8D6FDD;",
  info: "color: #007bff;",
  warn: "color: #ffc107;",
  error: "color: #dc3545;",
  debug: "color: #28a745;",
  trace: "color: #17a2b8;"
};
var LogLevelRank = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4
};
var globalLogLevel = "info";
var setGlobalLogLevel = (level) => {
  globalLogLevel = level;
};
var getGlobalLogLevel = () => globalLogLevel;
var shouldLog = (level) => LogLevelRank[level] <= LogLevelRank[globalLogLevel];
var resolveLazy = (v) => typeof v === "function" ? v() : v;
var toHex = (bytes) => Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
var ARRAY_TRUNCATION_THRESHOLD = 25;
var ARRAY_PREVIEW_COUNT = 10;
var SENSITIVE_KEYS = /* @__PURE__ */ new Set([
  "token",
  "authToken",
  "authorization",
  "accessToken",
  "refreshToken"
]);
var stringify = (value) => stringify$1(value, (key, current) => {
  if (SENSITIVE_KEYS.has(key)) {
    return "[REDACTED]";
  }
  if (current && typeof current === "object" && "__identity__" in current && typeof current.__identity__ === "bigint") {
    return u256ToHexString(
      current.__identity__
    );
  }
  if (current && typeof current === "object" && "__connection_id__" in current && typeof current.__connection_id__ === "bigint") {
    return u128ToHexString(
      current.__connection_id__
    );
  }
  if (current instanceof Uint8Array) {
    if (current.length < 25) {
      return `0x${toHex(current)}`;
    }
    const head = current.subarray(0, 10);
    return `Uint8Array(len=${current.length}, head=0x${toHex(head)})`;
  }
  if (Array.isArray(current) && current.length >= ARRAY_TRUNCATION_THRESHOLD) {
    const head = stringify$1(current.slice(0, ARRAY_PREVIEW_COUNT));
    return `Array(len=${current.length}, head=${head ?? "[]"})`;
  }
  return current;
});
var stdbLogger = (level, message, ...args) => {
  if (!shouldLog(level)) {
    return;
  }
  const resolvedMessage = resolveLazy(message);
  const resolvedArgs = args.map(resolveLazy);
  console.log(
    `%c${LogLevelIdentifierIcon[level]} ${level.toUpperCase()}%c ${resolvedMessage}`,
    LogStyle[level],
    LogTextStyle[level],
    ...resolvedArgs
  );
};

// src/sdk/replay_controller.ts
var ReplayController = class {
  #transport;
  #emitter = new EventEmitter();
  #status = "idle";
  #speed = 1;
  #direction = "forward";
  #replayTimeMicros = null;
  #isAtLiveTail = false;
  #desiredSamplesPerFrame = void 0;
  // Promise resolvers keyed by requestId so control methods can await the
  // corresponding server ack before resolving.
  #pending = /* @__PURE__ */ new Map();
  /** @internal */
  constructor(transport) {
    this.#transport = transport;
  }
  get status() {
    return this.#status;
  }
  get speed() {
    return this.#speed;
  }
  get direction() {
    return this.#direction;
  }
  get replayTimeMicros() {
    return this.#replayTimeMicros;
  }
  get isAtLiveTail() {
    return this.#isAtLiveTail;
  }
  /**
   * The current temporal resolution (1-100). Always defined once a
   * replay session has started (precise mode is always on).
   */
  get resolution() {
    return this.#desiredSamplesPerFrame ?? 100;
  }
  /**
   * @deprecated Use `resolution` instead.
   */
  get desiredSamplesPerFrame() {
    return this.#desiredSamplesPerFrame;
  }
  /**
   * Register a listener for a replay event. Returns a disposer that removes
   * the listener when called.
   */
  on(event, handler) {
    this.#emitter.on(event, handler);
    return () => this.#emitter.off(event, handler);
  }
  /**
   * Remove a previously-registered listener.
   */
  off(event, handler) {
    this.#emitter.off(event, handler);
  }
  /**
   * Start the replay. The connection must have been built with
   * `DbConnectionBuilder.withReplay()` and have at least one active
   * subscription. Resolves when the server confirms with `ReplayStarted`.
   *
   * Always sends `StartReplayPrecise` — the server always provides
   * per-row commit timestamps and multi-row batching on the wire.
   */
  start(opts) {
    const speed = opts.speed ?? 1;
    const direction = opts.direction ?? "forward";
    const desired = opts.resolution ?? opts.desiredSamplesPerFrame ?? 100;
    const priorStatus = this.#status;
    this.#status = "connecting";
    this.#speed = speed;
    this.#direction = direction;
    this.#desiredSamplesPerFrame = desired;
    this.#isAtLiveTail = false;
    const started = this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.StartReplayPrecise({
          requestId,
          fromMicros: BigInt(Math.trunc(opts.fromMicros)),
          speed,
          desiredSamplesPerFrame: desired,
          procedure: opts.procedure
        })
      );
      if (direction === "backward") {
        void this.setDirection("backward");
      }
    });
    started.catch(() => {
      if (this.#status === "connecting") this.#status = priorStatus;
    });
    return started;
  }
  /**
   * Change playback speed. Absolute value, always `>= 0`. `0` = unpaced.
   */
  setSpeed(speed) {
    this.#speed = speed;
    return this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.SetReplaySpeed({ requestId, speed })
      );
    });
  }
  /**
   * Adjust the temporal resolution at runtime without reconnecting.
   *
   * Resolves when the server acks with `ReplayDensityChanged`. The actual
   * per-frame density picked by the server's density controller may still
   * be lower than the supplied value (data thin / query slow — see design
   * doc §4.4).
   */
  setDensity(desiredSamplesPerFrame) {
    this.#desiredSamplesPerFrame = desiredSamplesPerFrame;
    return this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.SetReplayDensity({
          requestId,
          desiredSamplesPerFrame
        })
      );
    });
  }
  /**
   * Set playback direction.
   */
  setDirection(direction) {
    this.#direction = direction;
    const forward = direction === "forward";
    return this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.SetReplayDirection({ requestId, forward })
      );
    });
  }
  /**
   * Flip the current playback direction.
   */
  reverse() {
    return this.setDirection(
      this.#direction === "forward" ? "backward" : "forward"
    );
  }
  /**
   * Pause the replay producer.
   */
  pause() {
    return this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.PauseReplay({ requestId })
      );
    });
  }
  /**
   * Resume the replay producer.
   */
  resume() {
    return this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.ResumeReplay({ requestId })
      );
    });
  }
  /**
   * Jump the replay cursor to an absolute timestamp (microseconds).
   */
  seek(toMicros) {
    return this.#sendAwait((requestId) => {
      this.#transport.sendMessage(
        ReplayClientMessage.SetReplayPosition({
          requestId,
          toMicros: BigInt(Math.trunc(toMicros))
        })
      );
    });
  }
  /**
   * Jump the replay cursor by `deltaMs` milliseconds relative to the current
   * replay time. If the current replay time is unknown, this is a no-op.
   */
  seekBy(deltaMs) {
    if (this.#replayTimeMicros == null) {
      return Promise.resolve();
    }
    const target = this.#replayTimeMicros + Math.trunc(deltaMs) * 1e3;
    return this.seek(target);
  }
  // =======================================================================
  // Internal handlers invoked by DbConnectionImpl when it sees replay
  // messages from the server.
  // =======================================================================
  /** @internal */
  _handleReplayStarted(msg) {
    const fromMicros = Number(msg.fromMicros);
    this.#status = "replaying";
    this.#speed = msg.speed;
    this.#direction = msg.forward ? "forward" : "backward";
    this.#replayTimeMicros = fromMicros;
    this.#isAtLiveTail = false;
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("started", {
      fromMicros,
      speed: msg.speed,
      direction: this.#direction
    });
  }
  /** @internal */
  _handleReplaySpeedChanged(msg) {
    this.#speed = msg.speed;
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("speedChanged", msg.speed);
  }
  /** @internal */
  _handleReplayDensityChanged(msg) {
    this.#desiredSamplesPerFrame = msg.desiredSamplesPerFrame;
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("densityChanged", msg.desiredSamplesPerFrame);
  }
  /** @internal */
  _handleReplayDirectionChanged(msg) {
    this.#direction = msg.forward ? "forward" : "backward";
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("directionChanged", this.#direction);
  }
  /** @internal */
  _handleReplaySeeked(msg) {
    const toMicros = Number(msg.toMicros);
    this.#replayTimeMicros = toMicros;
    this.#isAtLiveTail = false;
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("seeked", toMicros);
  }
  /** @internal */
  _handleReplayPaused(msg) {
    this.#status = "paused";
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("paused");
  }
  /** @internal */
  _handleReplayResumed(msg) {
    this.#status = "replaying";
    this.#resolvePending(msg.requestId);
    this.#emitter.emit("resumed");
  }
  /** @internal */
  _handleReplayCaughtUp(msg) {
    const replayMicros = Number(msg.replayTimeMicros);
    this.#replayTimeMicros = replayMicros;
    this.#isAtLiveTail = true;
    this.#emitter.emit("caughtUp", replayMicros);
  }
  /** @internal */
  _handleReplayHeartbeat(msg) {
    const replayMicros = Number(msg.replayTimeMicros);
    this.#replayTimeMicros = replayMicros;
    this.#emitter.emit("progress", replayMicros);
  }
  /** @internal */
  _handleReplayEndOfHistory(msg) {
    const replayMicros = Number(msg.replayTimeMicros);
    this.#replayTimeMicros = replayMicros;
    this.#status = "ended";
    this.#emitter.emit("endOfHistory", replayMicros);
    this.#emitter.emit("ended", { reason: "endOfHistory" });
  }
  /** @internal */
  _handleReplayError(msg) {
    const error = new Error(msg.message);
    this.#status = "error";
    this.#rejectPending(msg.requestId, error);
    this.#emitter.emit("error", error);
    this.#emitter.emit("ended", { reason: "error", error });
  }
  /** @internal */
  _disconnect() {
    const err = new Error("Replay connection disconnected");
    for (const { reject } of this.#pending.values()) {
      reject(err);
    }
    this.#pending.clear();
    if (this.#status !== "ended" && this.#status !== "error") {
      this.#status = "idle";
    }
  }
  // =======================================================================
  // Internals
  // =======================================================================
  #sendAwait(send) {
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#transport.nextRequestId();
    this.#pending.set(requestId, { resolve, reject });
    try {
      send(requestId);
    } catch (e) {
      this.#pending.delete(requestId);
      reject(e);
    }
    return promise;
  }
  #resolvePending(requestId) {
    const entry = this.#pending.get(requestId);
    if (!entry) {
      stdbLogger(
        "trace",
        () => `ReplayController: no pending promise for requestId=${requestId}`
      );
      return;
    }
    this.#pending.delete(requestId);
    entry.resolve();
  }
  #rejectPending(requestId, err) {
    const entry = this.#pending.get(requestId);
    if (!entry) return;
    this.#pending.delete(requestId);
    entry.reject(err);
  }
};

// src/sdk/table_cache.ts
var scalarCompare = (x, y) => {
  if (x === y) return 0;
  return x < y ? -1 : 1;
};
var TableCacheImpl = class {
  hasPrimaryKey;
  rows;
  tableDef;
  emitter;
  /**
   * @param name the table name
   * @param primaryKeyCol column index designated as `#[primarykey]`
   * @param primaryKey column name designated as `#[primarykey]`
   * @param entityClass the entityClass
   */
  constructor(tableDef) {
    this.tableDef = tableDef;
    this.rows = /* @__PURE__ */ new Map();
    this.emitter = new EventEmitter();
    this.hasPrimaryKey = Object.values(this.tableDef.columns).some(
      (col) => col.columnMetadata.isPrimaryKey === true
    );
    for (const idxDef of this.tableDef.resolvedIndexes) {
      const index = this.#makeReadonlyIndex(this.tableDef, idxDef);
      this[idxDef.name] = index;
    }
  }
  // TODO: this just scans the whole table; we should build proper index structures
  #makeReadonlyIndex(tableDef, idx) {
    if (idx.algorithm !== "btree") {
      throw new Error("Only btree indexes are supported in TableCacheImpl");
    }
    const columns = idx.columns;
    const getKey = (row) => columns.map((c) => row[c]);
    const matchRange = (row, rangeArg) => {
      const key = getKey(row);
      const arr = Array.isArray(rangeArg) ? rangeArg : [rangeArg];
      const prefixLen = Math.max(0, arr.length - 1);
      for (let i = 0; i < prefixLen; i++) {
        if (!deepEqual(key[i], arr[i])) return false;
      }
      const lastProvided = arr[arr.length - 1];
      const kLast = key[prefixLen];
      if (lastProvided && typeof lastProvided === "object" && "from" in lastProvided && "to" in lastProvided) {
        const from = lastProvided.from;
        const to = lastProvided.to;
        if (from.tag !== "unbounded") {
          const c = scalarCompare(kLast, from.value);
          if (c < 0) return false;
          if (c === 0 && from.tag === "excluded") return false;
        }
        if (to.tag !== "unbounded") {
          const c = scalarCompare(kLast, to.value);
          if (c > 0) return false;
          if (c === 0 && to.tag === "excluded") return false;
        }
        return true;
      } else {
        if (!deepEqual(kLast, lastProvided)) return false;
        return true;
      }
    };
    const isUnique = tableDef.constraints.some((constraint) => {
      if (constraint.constraint !== "unique") {
        return false;
      }
      return deepEqual(constraint.columns, idx.columns);
    });
    const self = this;
    if (isUnique) {
      const impl = {
        find: (colVal) => {
          const expected = Array.isArray(colVal) ? colVal : [colVal];
          for (const row of self.iter()) {
            if (deepEqual(getKey(row), expected)) return row;
          }
          return null;
        }
      };
      return impl;
    } else {
      const impl = {
        *filter(range) {
          for (const row of self.iter()) {
            if (matchRange(row, range)) yield row;
          }
        }
      };
      return impl;
    }
  }
  /**
   * @returns number of rows in the table
   */
  count() {
    return BigInt(this.rows.size);
  }
  /**
   * @returns The values of the rows in the table
   */
  iter() {
    function* generator(rows) {
      for (const [row] of rows.values()) {
        yield row;
      }
    }
    return generator(this.rows);
  }
  /**
   * Allows iteration over the rows in the table
   * @returns An iterator over the rows in the table
   */
  [Symbol.iterator]() {
    return this.iter();
  }
  /** Delete every cached reference, including overlaps between subscriptions. */
  snapshotDeleteOperations = () => {
    const operations = [];
    for (const [rowId, [row, refCount]] of this.rows) {
      for (let i = 0; i < refCount; i++) {
        operations.push({ type: "delete", rowId, row });
      }
    }
    return operations;
  };
  applyOperations = (operations, ctx, options) => {
    const pendingCallbacks = [];
    if (this.tableDef.isEvent) {
      for (const op of operations) {
        if (op.type === "insert") {
          pendingCallbacks.push({
            type: "insert",
            table: this.tableDef.sourceName,
            cb: () => {
              this.emitter.emit("insert", ctx, op.row);
            }
          });
        }
      }
      return pendingCallbacks;
    }
    if (this.hasPrimaryKey || options?.skipIdenticalUpdates) {
      const insertMap = /* @__PURE__ */ new Map();
      const deleteMap = /* @__PURE__ */ new Map();
      for (const op of operations) {
        if (op.type === "insert") {
          const [_, prevCount] = insertMap.get(op.rowId) || [op, 0];
          insertMap.set(op.rowId, [op, prevCount + 1]);
        } else {
          const [_, prevCount] = deleteMap.get(op.rowId) || [op, 0];
          deleteMap.set(op.rowId, [op, prevCount + 1]);
        }
      }
      for (const [primaryKey, [insertOp, refCount]] of insertMap) {
        const deleteEntry = deleteMap.get(primaryKey);
        if (deleteEntry) {
          const [_, deleteCount] = deleteEntry;
          const refCountDelta = refCount - deleteCount;
          const maybeCb = this.update(
            ctx,
            primaryKey,
            insertOp.row,
            refCountDelta,
            options?.skipIdenticalUpdates
          );
          if (maybeCb) {
            pendingCallbacks.push(maybeCb);
          }
          deleteMap.delete(primaryKey);
        } else {
          const maybeCb = this.insert(ctx, insertOp, refCount);
          if (maybeCb) {
            pendingCallbacks.push(maybeCb);
          }
        }
      }
      for (const [deleteOp, refCount] of deleteMap.values()) {
        const maybeCb = this.delete(ctx, deleteOp, refCount);
        if (maybeCb) {
          pendingCallbacks.push(maybeCb);
        }
      }
    } else {
      for (const op of operations) {
        if (op.type === "insert") {
          const maybeCb = this.insert(ctx, op);
          if (maybeCb) {
            pendingCallbacks.push(maybeCb);
          }
        } else {
          const maybeCb = this.delete(ctx, op);
          if (maybeCb) {
            pendingCallbacks.push(maybeCb);
          }
        }
      }
    }
    return pendingCallbacks;
  };
  update = (ctx, rowId, newRow, refCountDelta = 0, skipIfIdentical = false) => {
    const existingEntry = this.rows.get(rowId);
    if (!existingEntry) {
      stdbLogger(
        "error",
        `Updating a row that was not present in the cache. Table: ${this.tableDef.sourceName}, RowId: ${rowId}`
      );
      return void 0;
    }
    const [oldRow, previousCount] = existingEntry;
    const refCount = Math.max(1, previousCount + refCountDelta);
    if (previousCount + refCountDelta <= 0) {
      stdbLogger(
        "error",
        `Negative reference count for in table ${this.tableDef.sourceName} row ${rowId} (${previousCount} + ${refCountDelta})`
      );
      return void 0;
    }
    this.rows.set(rowId, [newRow, refCount]);
    if (skipIfIdentical && deepEqual(oldRow, newRow)) {
      return void 0;
    }
    if (previousCount === 0) {
      stdbLogger(
        "error",
        `Updating a row id in table ${this.tableDef.sourceName} which was not present in the cache (rowId: ${rowId})`
      );
      return {
        type: "insert",
        table: this.tableDef.sourceName,
        cb: () => {
          this.emitter.emit("insert", ctx, newRow);
        }
      };
    }
    return {
      type: "update",
      table: this.tableDef.sourceName,
      cb: () => {
        this.emitter.emit("update", ctx, oldRow, newRow);
      }
    };
  };
  insert = (ctx, operation, count = 1) => {
    const [_, previousCount] = this.rows.get(operation.rowId) || [
      operation.row,
      0
    ];
    this.rows.set(operation.rowId, [operation.row, previousCount + count]);
    if (previousCount === 0) {
      return {
        type: "insert",
        table: this.tableDef.sourceName,
        cb: () => {
          this.emitter.emit("insert", ctx, operation.row);
        }
      };
    }
    return void 0;
  };
  delete = (ctx, operation, count = 1) => {
    const [_, previousCount] = this.rows.get(operation.rowId) || [
      operation.row,
      0
    ];
    if (previousCount === 0) {
      stdbLogger("warn", "Deleting a row that was not present in the cache");
      return void 0;
    }
    if (previousCount <= count) {
      this.rows.delete(operation.rowId);
      return {
        type: "delete",
        table: this.tableDef.sourceName,
        cb: () => {
          this.emitter.emit("delete", ctx, operation.row);
        }
      };
    }
    this.rows.set(operation.rowId, [operation.row, previousCount - count]);
    return void 0;
  };
  /**
   * Register a callback for when a row is newly inserted into the database.
   *
   * ```ts
   * ctx.db.user.onInsert((reducerEvent, user) => {
   *   if (reducerEvent) {
   *      console.log("New user on reducer", reducerEvent, user);
   *   } else {
   *      console.log("New user received during subscription update on insert", user);
   *  }
   * });
   * ```
   *
   * @param cb Callback to be called when a new row is inserted
   */
  onInsert = (cb) => {
    this.emitter.on("insert", cb);
  };
  /** Fire an on_insert callback without touching the cache. Used by the
   *  replay fast path where rows are append-only to the consumer. */
  emitInsert = (ctx, row) => {
    this.emitter.emit("insert", ctx, row);
  };
  /**
   * Register a callback for when a row is deleted from the database.
   *
   * ```ts
   * ctx.db.user.onDelete((reducerEvent, user) => {
   *   if (reducerEvent) {
   *      console.log("Deleted user on reducer", reducerEvent, user);
   *   } else {
   *      console.log("Deleted user received during subscription update on update", user);
   *  }
   * });
   * ```
   *
   * @param cb Callback to be called when a new row is inserted
   */
  onDelete = (cb) => {
    this.emitter.on("delete", cb);
  };
  /**
   * Register a callback for when a row is updated into the database.
   *
   * ```ts
   * ctx.db.user.onInsert((reducerEvent, oldUser, user) => {
   *   if (reducerEvent) {
   *      console.log("Updated user on reducer", reducerEvent, user);
   *   } else {
   *      console.log("Updated user received during subscription update on delete", user);
   *  }
   * });
   * ```
   *
   * @param cb Callback to be called when a new row is inserted
   */
  onUpdate = (cb) => {
    this.emitter.on("update", cb);
  };
  /**
   * Remove a callback for when a row is newly inserted into the database.
   *
   * @param cb Callback to be removed
   */
  removeOnInsert = (cb) => {
    this.emitter.off("insert", cb);
  };
  /**
   * Remove a callback for when a row is deleted from the database.
   *
   * @param cb Callback to be removed
   */
  removeOnDelete = (cb) => {
    this.emitter.off("delete", cb);
  };
  /**
   * Remove a callback for when a row is updated into the database.
   *
   * @param cb Callback to be removed
   */
  removeOnUpdate = (cb) => {
    this.emitter.off("update", cb);
  };
};

// src/sdk/client_cache.ts
var TableMap = class {
  map = /* @__PURE__ */ new Map();
  get(key) {
    return this.map.get(key);
  }
  set(key, value) {
    this.map.set(key, value);
    return this;
  }
  has(key) {
    return this.map.has(key);
  }
  delete(key) {
    return this.map.delete(key);
  }
  // optional: iteration stays broadly typed (cannot express per-key relation here)
  keys() {
    return this.map.keys();
  }
  values() {
    return this.map.values();
  }
  entries() {
    return this.map.entries();
  }
  [Symbol.iterator]() {
    return this.entries();
  }
};
var ClientCache = class {
  /**
   * The tables in the database.
   */
  tables = new TableMap();
  /**
   * Returns the table with the given name.
   * - If SchemaDef is a concrete schema, `name` is constrained to known table names,
   *   and the return type matches that table.
   * - If SchemaDef is undefined, `name` is string and the return type is untyped.
   */
  getTable(name) {
    const table2 = this.tables.get(name);
    if (!table2) {
      console.error(
        "The table has not been registered for this client. Please register the table before using it. If you have registered global tables using the SpacetimeDBClient.registerTables() or `registerTable()` method, please make sure that is executed first!"
      );
      throw new Error(`Table ${String(name)} does not exist`);
    }
    return table2;
  }
  /**
   * Returns the table with the given name, creating it if needed.
   * - Typed mode: `tableTypeInfo.tableName` is constrained to known names and
   *   the return type matches that table.
   * - Untyped mode: accepts any string and returns an untyped TableCache.
   */
  getOrCreateTable(tableDef) {
    const name = tableDef.accessorName;
    const table2 = this.tables.get(name);
    if (table2) {
      return table2;
    }
    const newTable = new TableCacheImpl(
      tableDef
    );
    this.tables.set(name, newTable);
    return newTable;
  }
};

// src/sdk/version.ts
function comparePreReleases(a, b) {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const aPart = a[i];
    const bPart = b[i];
    if (aPart === bPart) continue;
    if (typeof aPart === "number" && typeof bPart === "number") {
      return aPart - bPart;
    }
    if (typeof aPart === "string" && typeof bPart === "string") {
      return aPart.localeCompare(bPart);
    }
    return typeof aPart === "string" ? 1 : -1;
  }
  return a.length - b.length;
}
var SemanticVersion = class _SemanticVersion {
  major;
  minor;
  patch;
  preRelease;
  buildInfo;
  constructor(major, minor, patch, preRelease = null, buildInfo = null) {
    this.major = major;
    this.minor = minor;
    this.patch = patch;
    this.preRelease = preRelease;
    this.buildInfo = buildInfo;
  }
  toString() {
    let versionString = `${this.major}.${this.minor}.${this.patch}`;
    if (this.preRelease) {
      versionString += `-${this.preRelease.join(".")}`;
    }
    if (this.buildInfo) {
      versionString += `+${this.buildInfo}`;
    }
    return versionString;
  }
  compare(other) {
    if (this.major !== other.major) {
      return this.major - other.major;
    }
    if (this.minor !== other.minor) {
      return this.minor - other.minor;
    }
    if (this.patch !== other.patch) {
      return this.patch - other.patch;
    }
    if (this.preRelease && other.preRelease) {
      return comparePreReleases(this.preRelease, other.preRelease);
    }
    if (this.preRelease) {
      return -1;
    }
    if (other.preRelease) {
      return -1;
    }
    return 0;
  }
  clone() {
    return new _SemanticVersion(
      this.major,
      this.minor,
      this.patch,
      this.preRelease ? [...this.preRelease] : null,
      this.buildInfo
    );
  }
  static parseVersionString(version) {
    const regex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*))?(?:\+([\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*))?$/;
    const match = version.match(regex);
    if (!match) {
      throw new Error(`Invalid version string: ${version}`);
    }
    const major = parseInt(match[1], 10);
    const minor = parseInt(match[2], 10);
    const patch = parseInt(match[3], 10);
    const preRelease = match[4] ? match[4].split(".").map((id) => isNaN(Number(id)) ? id : Number(id)) : null;
    const buildInfo = match[5] || null;
    return new _SemanticVersion(major, minor, patch, preRelease, buildInfo);
  }
};
var _MINIMUM_CLI_VERSION = new SemanticVersion(
  1,
  4,
  0
);
function ensureMinimumVersionOrThrow(versionString) {
  if (versionString === void 0) {
    throw new Error(versionErrorMessage(versionString));
  }
  const version = SemanticVersion.parseVersionString(versionString);
  if (version.compare(_MINIMUM_CLI_VERSION) < 0) {
    throw new Error(versionErrorMessage(versionString));
  }
}
function versionErrorMessage(incompatibleVersion) {
  return `Module code was generated with an incompatible version of the spacetimedb cli (${incompatibleVersion}). Update the cli version to at least ${_MINIMUM_CLI_VERSION.toString()} and regenerate the bindings. You can upgrade to the latest cli version by running: spacetime version upgrade`;
}

// src/sdk/decompress.ts
async function decompress(buffer, type, chunkSize = 128 * 1024) {
  let offset = 0;
  const readableStream = new ReadableStream({
    pull(controller) {
      if (offset < buffer.length) {
        const chunk = buffer.subarray(
          offset,
          Math.min(offset + chunkSize, buffer.length)
        );
        controller.enqueue(chunk);
        offset += chunkSize;
      } else {
        controller.close();
      }
    }
  });
  const decompressionStream = new DecompressionStream(type);
  const decompressedStream = readableStream.pipeThrough(decompressionStream);
  const reader = decompressedStream.getReader();
  const chunks = [];
  let totalLength = 0;
  let result;
  while (!(result = await reader.read()).done) {
    chunks.push(result.value);
    totalLength += result.value.length;
  }
  const decompressedArray = new Uint8Array(totalLength);
  let chunkOffset = 0;
  for (const chunk of chunks) {
    decompressedArray.set(chunk, chunkOffset);
    chunkOffset += chunk.length;
  }
  return decompressedArray;
}

// src/sdk/ws.ts
async function resolveWS() {
  if (typeof WebSocket !== "undefined") {
    return WebSocket;
  }
  const dynamicImport = new Function("m", "return import(m)");
  try {
    const { WebSocket: UndiciWS } = await dynamicImport("undici");
    return UndiciWS;
  } catch (err) {
    stdbLogger(
      "warn",
      "[spacetimedb-sdk] No global WebSocket found. On Node 18\u201321, please install `undici` (npm install undici) to enable WebSocket support."
    );
    throw err;
  }
}
var WebSocketTokenError = class extends Error {
  constructor(status, statusText) {
    super(`Failed to verify token: ${status} ${statusText}`.trimEnd());
    this.status = status;
    this.name = "WebSocketTokenError";
  }
  status;
};
async function openWebSocket({
  url,
  nameOrAddress,
  wsProtocol,
  authToken,
  compression,
  lightMode,
  confirmedReads,
  replay,
  connectionId,
  sessionId
}) {
  const headers = new Headers();
  const WS = await resolveWS();
  let temporaryAuthToken;
  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
    const tokenUrl = new URL("v1/identity/websocket-token", url);
    tokenUrl.protocol = url.protocol === "wss:" ? "https:" : "http:";
    const response = await fetch(tokenUrl, { method: "POST", headers });
    if (response.ok) {
      const { token } = await response.json();
      temporaryAuthToken = token;
    } else {
      const body = await response.text().catch(() => "");
      const detail = [response.statusText, body.trim().slice(0, 200)].filter(Boolean).join(" ");
      throw new WebSocketTokenError(response.status, detail);
    }
  }
  const databaseUrl = new URL(`v1/database/${nameOrAddress}/subscribe`, url);
  if (temporaryAuthToken) {
    databaseUrl.searchParams.set("token", temporaryAuthToken);
  }
  databaseUrl.searchParams.set(
    "compression",
    { gzip: "Gzip", brotli: "Brotli", none: "None" }[compression] ?? "None"
  );
  if (lightMode) {
    databaseUrl.searchParams.set("light", "true");
  }
  if (confirmedReads !== void 0) {
    databaseUrl.searchParams.set("confirmed", confirmedReads.toString());
  }
  if (replay) {
    databaseUrl.searchParams.set("replay", "true");
  }
  if (connectionId) {
    databaseUrl.searchParams.set("connection_id", connectionId);
  }
  if (sessionId) {
    databaseUrl.searchParams.set("session_id", sessionId);
  }
  const ws = new WS(databaseUrl.toString(), wsProtocol);
  ws.binaryType = "arraybuffer";
  return ws;
}

// src/sdk/websocket_decompress_adapter.ts
var WebsocketDecompressAdapter = class _WebsocketDecompressAdapter {
  get protocol() {
    return this.#ws.protocol;
  }
  get readyState() {
    return this.#ws.readyState;
  }
  set onclose(handler) {
    this.#ws.onclose = handler;
  }
  set onopen(handler) {
    this.#ws.onopen = handler;
  }
  set onmessage(handler) {
    let tail = Promise.resolve();
    this.#ws.onmessage = async (msg) => {
      const pending = this.#decompress(new Uint8Array(msg.data));
      pending.catch(() => {
      });
      const previous = tail;
      let release;
      tail = new Promise((resolve) => {
        release = resolve;
      });
      try {
        await previous;
        let data;
        try {
          data = await pending;
        } catch (e) {
          console.error(
            "[SpacetimeDB] WebSocket decompress failed, closing socket:",
            e
          );
          this.#ws.close();
          return;
        }
        handler({ data });
      } finally {
        release();
      }
    };
  }
  set onerror(handler) {
    this.#ws.onerror = handler;
  }
  #ws;
  async #decompress(buffer) {
    const tag = buffer[0];
    const data = buffer.subarray(1);
    switch (tag) {
      case 0:
        return data;
      case 1:
        return await decompress(data, "brotli");
      case 2:
        return await decompress(data, "gzip");
      default:
        throw new Error(
          "Unexpected Compression Algorithm. Please use `gzip` or `none`"
        );
    }
  }
  send(msg) {
    this.#ws.send(msg);
  }
  close() {
    this.#ws.close();
  }
  constructor(ws) {
    this.#ws = ws;
  }
  static async openWebSocket(args) {
    return new _WebsocketDecompressAdapter(await openWebSocket(args));
  }
};

// src/sdk/db_connection_builder.ts
var DbConnectionBuilder = class {
  /**
   * Creates a new `DbConnectionBuilder` database client and set the initial parameters.
   *
   * Users are not expected to call this constructor directly. Instead, use the static method `DbConnection.builder()`.
   *
   * @param remoteModule The remote module to use to connect to the SpacetimeDB server.
   * @param dbConnectionConstructor The constructor to use to create a new `DbConnection`.
   */
  constructor(remoteModule, dbConnectionCtor) {
    this.remoteModule = remoteModule;
    this.dbConnectionCtor = dbConnectionCtor;
    this.#createWSFn = WebsocketDecompressAdapter.openWebSocket;
  }
  remoteModule;
  dbConnectionCtor;
  #uri;
  #nameOrAddress;
  #identity;
  #token;
  #tokenProvider;
  #emitter = new EventEmitter();
  #compression = "gzip";
  #lightMode = false;
  #confirmedReads;
  #replay = false;
  #automaticReconnect = false;
  #reconnectPolicy;
  #createWSFn;
  /**
   * Set the URI of the SpacetimeDB server to connect to.
   *
   * @param uri The URI of the SpacetimeDB server to connect to.
   *
   **/
  withUri(uri) {
    this.#uri = new URL(uri);
    return this;
  }
  /**
   * Set the name or Identity of the remote database to connect to.
   *
   * @param nameOrAddress
   *
   * @returns The `DbConnectionBuilder` instance.
   */
  withDatabaseName(nameOrAddress) {
    this.#nameOrAddress = nameOrAddress;
    return this;
  }
  /**
   * Set the identity of the client to connect to the database.
   *
   * @param token The credentials to use to authenticate with SpacetimeDB. This
   * is optional. You can store the token returned by the `onConnect` callback
   * to use in future connections.
   *
   * @returns The `DbConnectionBuilder` instance.
   */
  withToken(token) {
    this.#token = token;
    return this;
  }
  /**
   * Set a callback that supplies the auth token.
   *
   * Prefer this over {@link withToken} whenever the token can expire. A builder
   * is re-run on every reconnect (see `ConnectionManager`), so a token passed as
   * a string is frozen at build time: once it expires, every later attempt
   * replays a credential the server rejects, indefinitely, at the backoff cap.
   * Without {@link withToken}, each connection this builder makes asks the
   * provider for its first attempt, so a rebuilt connection picks up whatever
   * the app's auth layer holds now. If the provider throws or rejects, the
   * attempt fails with `connectError`; no stale token is used instead.
   *
   * With {@link withToken} as well, the first attempt uses that token, as in
   * upstream. With {@link withAutomaticReconnect}, the provider is also asked
   * before a reconnect whose token is near expiry or was rejected. It must
   * return a token for the same identity: a reconnect under another identity
   * ends automatic reconnection.
   *
   * @param provider Returns the current token, or a promise of it.
   *
   * @returns The `DbConnectionBuilder` instance.
   */
  withTokenProvider(provider) {
    this.#tokenProvider = provider;
    return this;
  }
  withWSFn(createWSFn) {
    this.#createWSFn = createWSFn;
    return this;
  }
  /**
   * Set the compression algorithm to use for the connection.
   *
   * @param compression The compression algorithm to use for the connection.
   */
  withCompression(compression) {
    if (compression === "brotli") {
      try {
        new DecompressionStream("brotli");
      } catch (e) {
        throw new TypeError(
          `Brotli compression is not supported by the runtime. Please choose a different compression method.`,
          { cause: e }
        );
      }
    }
    this.#compression = compression;
    return this;
  }
  /**
   * Sets the connection to operate in light mode.
   *
   * Light mode is a mode that reduces the amount of data sent over the network.
   *
   * @param lightMode The light mode for the connection.
   */
  withLightMode(lightMode) {
    this.#lightMode = lightMode;
    return this;
  }
  /**
   * Sets the connection to use confirmed reads.
   *
   * When enabled, the server will send query results only after they are
   * confirmed to be durable.
   *
   * What durable means depends on the server configuration: a single node
   * server may consider a transaction durable once it is `fsync`'ed to disk,
   * whereas a cluster may require that some number of replicas have
   * acknowledge that they have stored the transactions.
   *
   * Note that enabling confirmed reads will increase the latency between a
   * reducer call and the corresponding subscription update arriving at the
   * client.
   *
   * If this method is not called, not preference is sent to the server, and
   * the server will choose the default.
   *
   * @param confirmedReads `true` to enable confirmed reads, `false` to disable.
   */
  withConfirmedReads(confirmedReads) {
    this.#confirmedReads = confirmedReads;
    return this;
  }
  /**
   * Mark this connection as a replay session. The server treats replay
   * connections as read-only time-travel streams: no live data is
   * delivered, `Subscribe` returns an empty initial state, and replay
   * control messages (`StartReplay`, `PauseReplay`, etc.) are accepted.
   *
   * When set, `?replay=true` is appended to the WebSocket URL and the
   * connection negotiates the TurboTwin subprotocol (`v3.bsatn.turbotwin`,
   * else `v2.bsatn.turbotwin`), the only one that carries replay messages;
   * the server rejects `replay=true` on any other subprotocol. Replay
   * controls on a connection built without this reject with an error.
   */
  withReplay(enabled = true) {
    this.#replay = enabled;
    return this;
  }
  /**
   * Reconnect after an established connection drops, preserving handles and callbacks.
   * Retries use exponential backoff from `minDelayMs` (default 1 s) up to
   * `maxDelayMs` (default 30 s) until disconnect() or a terminal failure.
   * Values below the 500 ms and 1 s floors are raised with a warning, so
   * that retrying clients cannot overwhelm the database.
   * Initial connection failures are not retried. Lifecycle callbacks report
   * the next attempt and delay, or undefined when no retry is scheduled.
   */
  withAutomaticReconnect(options) {
    this.#automaticReconnect = true;
    if (options) {
      this.#reconnectPolicy = resolveReconnectPolicy(options);
    }
    return this;
  }
  /**
   * Register a callback for the first successful connection to the database.
   * Automatic reconnects invoke {@link DbConnectionBuilder.onAutomaticReconnect} instead.
   *
   * @param identity A unique identifier for a client connected to a database.
   * @param token The credentials to use to authenticate with SpacetimeDB.
   *
   * @returns The `DbConnectionBuilder` instance.
   *
   * The callback will be invoked with the `Identity` and private authentication `token` provided by the database to identify this connection.
   *
   * If credentials were supplied to connect, those passed to the callback will be equivalent to the ones used to connect.
   *
   * If the initial connection was anonymous, a new set of credentials will be generated by the database to identify this user.
   *
   * The credentials passed to the callback can be saved and used to authenticate the same user in future connections.
   *
   * @example
   *
   * ```ts
   * DbConnection.builder().onConnect((ctx, identity, token) => {
   *  console.log("Connected to SpacetimeDB with identity:", identity.toHexString());
   * });
   * ```
   */
  onConnect(callback) {
    this.#emitter.on("connect", callback);
    return this;
  }
  /**
   * Register a callback for each successful automatic reconnect after the
   * initial connection. Receives the current identity and authentication token.
   * Runs before subscription replay; use subscription `onApplied` callbacks
   * to observe refreshed data.
   */
  onAutomaticReconnect(callback) {
    this.#emitter.on("automaticReconnect", callback);
    return this;
  }
  /**
   * Register a callback to be invoked upon an error.
   *
   * @example
   *
   * ```ts
   * DbConnection.builder().onConnectError((ctx, error) => {
   *   console.log("Error connecting to SpacetimeDB:", error);
   * });
   * ```
   *
   * With {@link DbConnectionBuilder.withAutomaticReconnect} enabled, this
   * callback also reports each failed reconnect attempt:
   * `nextReconnectAttempt` is the number of the upcoming attempt and
   * `nextReconnectDelayMs` the wait before it. Both are `undefined` when the
   * SDK will not retry, as for a failed initial connection.
   */
  onConnectError(callback) {
    this.#emitter.on("connectError", callback);
    return this;
  }
  /**
   * Registers a callback to run when a {@link DbConnection} whose connection initially succeeded
   * is disconnected, either after a {@link DbConnection.disconnect()} call or due to an error.
   *
   * If the connection ended because of an error, the error is passed to the callback.
   *
   * The `callback` will be installed on the `DbConnection` created by `build`
   * before initiating the connection, ensuring there's no opportunity for the disconnect to happen
   * before the callback is installed.
   *
   * Note that this does not trigger if `build` fails
   * or in cases where {@link DbConnectionBuilder.onConnectError} would trigger.
   * This callback only triggers if the connection closes after `build` returns successfully
   * and {@link DbConnectionBuilder.onConnect} is invoked, i.e., after the initial connection
   * message is received.
   *
   * To simplify SDK implementation, at most one such callback can be registered.
   * Calling `onDisconnect` on the same `DbConnectionBuilder` multiple times throws an error.
   *
   * Unlike callbacks registered via {@link DbConnection},
   * no mechanism is provided to unregister the provided callback.
   * This is a concession to ergonomics; there's no clean place to return a `CallbackId` from this method
   * or from `build`.
   *
   * With {@link DbConnectionBuilder.withAutomaticReconnect} enabled, this
   * callback also reports connections lost mid-session, and the SDK keeps
   * reconnecting afterwards: `nextReconnectAttempt` is the number of the
   * upcoming attempt and `nextReconnectDelayMs` the wait before it. Both are
   * `undefined` when the SDK will not retry, which is always the case without
   * automatic reconnection.
   *
   * @param {function(error?: Error): void} callback - The callback to invoke upon disconnection.
   * @throws {Error} Throws an error if called multiple times on the same `DbConnectionBuilder`.
   */
  onDisconnect(callback) {
    this.#emitter.on("disconnect", callback);
    return this;
  }
  getUri() {
    return this.#uri?.toString() ?? "";
  }
  getModuleName() {
    return this.#nameOrAddress ?? "";
  }
  /**
   * Builds a new `DbConnection` with the parameters set on this `DbConnectionBuilder` and attempts to connect to the SpacetimeDB server.
   *
   * @returns A new `DbConnection` with the parameters set on this `DbConnectionBuilder`.
   *
   * @example
   *
   * ```ts
   * const host = "http://localhost:3000";
   * const name_or_address = "database_name"
   * const auth_token = undefined;
   * DbConnection.builder().withUri(host).withDatabaseName(name_or_address).withToken(auth_token).build();
   * ```
   */
  build() {
    if (!this.#uri) {
      throw new Error("URI is required to connect to SpacetimeDB");
    }
    if (!this.#nameOrAddress) {
      throw new Error(
        "Database name or address is required to connect to SpacetimeDB"
      );
    }
    ensureMinimumVersionOrThrow(this.remoteModule.versionInfo?.cliVersion);
    return this.dbConnectionCtor({
      uri: this.#uri,
      nameOrAddress: this.#nameOrAddress,
      identity: this.#identity,
      token: this.#token,
      tokenProvider: this.#tokenProvider,
      emitter: this.#emitter,
      compression: this.#compression,
      lightMode: this.#lightMode,
      confirmedReads: this.#confirmedReads,
      replay: this.#replay,
      createWSFn: this.#createWSFn,
      remoteModule: this.remoteModule,
      automaticReconnect: this.#automaticReconnect,
      reconnectPolicy: this.#reconnectPolicy
    });
  }
};

// src/sdk/internal.ts
var INTERNAL_REMOTE_MODULE = /* @__PURE__ */ Symbol("INTERNAL_REMOTE_MODULE");

// src/sdk/subscription_builder_impl.ts
var SubscriptionBuilderImpl = class {
  constructor(db) {
    this.db = db;
  }
  db;
  #onApplied = void 0;
  #onError = void 0;
  /**
   * Registers `callback` to run when this query is successfully added to our subscribed set,
   * I.e. when its `SubscriptionApplied` message is received.
   *
   * The database state exposed via the `&EventContext` argument
   * includes all the rows added to the client cache as a result of the new subscription.
   *
   * The event in the `&EventContext` argument is `Event::SubscribeApplied`.
   *
   * Multiple `on_applied` callbacks for the same query may coexist.
   * No mechanism for un-registering `on_applied` callbacks is exposed.
   *
   * @param cb - Callback to run when the subscription is applied.
   * @returns The current `SubscriptionBuilder` instance.
   */
  onApplied(cb) {
    this.#onApplied = cb;
    return this;
  }
  /**
   * Registers `callback` to run when this query either:
   * - Fails to be added to our subscribed set.
   * - Is unexpectedly removed from our subscribed set.
   *
   * If the subscription had previously started and has been unexpectedly removed,
   * the database state exposed via the `&EventContext` argument contains no rows
   * from any subscriptions removed within the same error event.
   * As proposed, it must therefore contain no rows.
   *
   * The event in the `&EventContext` argument is `Event::SubscribeError`,
   * containing a dynamic error object with a human-readable description of the error
   * for diagnostic purposes.
   *
   * Multiple `on_error` callbacks for the same query may coexist.
   * No mechanism for un-registering `on_error` callbacks is exposed.
   *
   * @param cb - Callback to run when there is an error in subscription.
   * @returns The current `SubscriptionBuilder` instance.
   */
  onError(cb) {
    this.#onError = cb;
    return this;
  }
  subscribe(query_sql) {
    let queries;
    if (typeof query_sql === "function") {
      const tables = this.db.getFromBuilder();
      const result = query_sql(tables);
      queries = Array.isArray(result) ? result : [result];
    } else {
      queries = Array.isArray(query_sql) ? query_sql : [query_sql];
    }
    if (queries.length === 0) {
      throw new Error("Subscriptions must have at least one query");
    }
    const queryStrings = queries.map((q) => {
      if (typeof q === "string") return q;
      if (isRowTypedQuery(q)) return toSql(q);
      throw new Error("Subscriptions must be SQL strings or typed queries");
    });
    return new SubscriptionHandleImpl(
      this.db,
      queryStrings,
      this.#onApplied,
      this.#onError
    );
  }
  /**
   * Subscribes to all rows from all tables.
   *
   * This method is intended as a convenience
   * for applications where client-side memory use and network bandwidth are not concerns.
   * Applications where these resources are a constraint
   * should register more precise queries via `subscribe`
   * in order to replicate only the subset of data which the client needs to function.
   *
   * This method should not be combined with `subscribe` on the same `DbConnection`.
   * A connection may either `subscribe` to particular queries,
   * or `subscribeToAllTables`, but not both.
   * Attempting to call `subscribe`
   * on a `DbConnection` that has previously used `subscribeToAllTables`,
   * or vice versa, may misbehave in any number of ways,
   * including dropping subscriptions, corrupting the client cache, or throwing errors.
   *
   * Views that take arguments are left out, as there are no arguments to pass them.
   */
  subscribeToAllTables() {
    const remoteModule = this.db[INTERNAL_REMOTE_MODULE]();
    const viewsWithArgs = new Set(remoteModule.viewsWithArgs);
    const queries = Object.values(remoteModule.tables).filter((table2) => !viewsWithArgs.has(table2.sourceName)).map((table2) => `SELECT * FROM ${table2.sourceName}`);
    this.subscribe(queries);
  }
};
var SubscriptionManager = class {
  subscriptions = /* @__PURE__ */ new Map();
};
var SubscriptionHandleImpl = class {
  constructor(db, querySql, onApplied, onError) {
    this.db = db;
    this.#emitter.on(
      "applied",
      (ctx) => {
        this.#activeState = true;
        if (onApplied) {
          onApplied(ctx);
        }
      }
    );
    this.#emitter.on(
      "error",
      (ctx, error) => {
        this.#activeState = false;
        this.#endedState = true;
        if (onError) {
          onError(ctx, error);
        }
      }
    );
    this.#querySetId = this.db.registerSubscription(
      this,
      this.#emitter,
      querySql
    );
  }
  db;
  #querySetId;
  #unsubscribeCalled = false;
  #endedState = false;
  #activeState = false;
  #emitter = new EventEmitter();
  /** @internal Rebind the retained handle to its replayed query set. */
  rebindQuerySetId(querySetId) {
    this.#querySetId = querySetId;
  }
  /**
   * Consumes self and issues an `Unsubscribe` message,
   * removing this query from the client's set of subscribed queries.
   * It is only valid to call this method if `is_active()` is `true`.
   */
  unsubscribe() {
    if (this.#unsubscribeCalled) {
      throw new Error("Unsubscribe has already been called");
    }
    this.#unsubscribeCalled = true;
    this.#emitter.on(
      "end",
      (_ctx) => {
        this.#endedState = true;
        this.#activeState = false;
      }
    );
    this.db.unregisterSubscription(this.#querySetId);
  }
  /**
   * Unsubscribes and also registers a callback to run upon success.
   * I.e. when an `UnsubscribeApplied` message is received.
   *
   * If `Unsubscribe` returns an error,
   * or if the `on_error` callback(s) are invoked before this subscription would end normally,
   * the `on_end` callback is not invoked.
   *
   * @param onEnd - Callback to run upon successful unsubscribe.
   */
  unsubscribeThen(onEnd) {
    if (this.#endedState) {
      throw new Error("Subscription has already ended");
    }
    if (this.#unsubscribeCalled) {
      throw new Error("Unsubscribe has already been called");
    }
    this.#unsubscribeCalled = true;
    this.#emitter.on(
      "end",
      (ctx) => {
        this.#endedState = true;
        this.#activeState = false;
        onEnd(ctx);
      }
    );
    this.db.unregisterSubscription(this.#querySetId);
  }
  /**
   * True if this `SubscriptionHandle` has ended,
   * either due to an error or a call to `unsubscribe`.
   *
   * This is initially false, and becomes true when either the `on_end` or `on_error` callback is invoked.
   * A subscription which has not yet been applied is not active, but is also not ended.
   */
  isEnded() {
    return this.#endedState;
  }
  /**
   * True if this `SubscriptionHandle` is active, meaning it has been successfully applied
   * and has not since ended, either due to an error or a complete `unsubscribe` request-response pair.
   *
   * This corresponds exactly to the interval bounded at the start by the `on_applied` callback
   * and at the end by either the `on_end` or `on_error` callback.
   */
  isActive() {
    return this.#activeState;
  }
};

// src/sdk/websocket_protocols.ts
var V2_WS_PROTOCOL = "v2.bsatn.spacetimedb";
var V3_WS_PROTOCOL = "v3.bsatn.spacetimedb";
var PREFERRED_WS_PROTOCOLS = [V3_WS_PROTOCOL, V2_WS_PROTOCOL];
var TT_V2_WS_PROTOCOL = "v2.bsatn.turbotwin";
var TT_V3_WS_PROTOCOL = "v3.bsatn.turbotwin";
var TURBOTWIN_WS_PROTOCOLS = [
  TT_V3_WS_PROTOCOL,
  TT_V2_WS_PROTOCOL
];
function isV3Framing(protocol) {
  return protocol === V3_WS_PROTOCOL || protocol === TT_V3_WS_PROTOCOL;
}
function normalizeWsProtocol(protocol) {
  if (protocol === V3_WS_PROTOCOL || protocol === TT_V3_WS_PROTOCOL || protocol === TT_V2_WS_PROTOCOL) {
    return protocol;
  }
  if (protocol === "" || protocol === V2_WS_PROTOCOL) {
    return V2_WS_PROTOCOL;
  }
  stdbLogger(
    "warn",
    `Unexpected websocket subprotocol "${protocol}", falling back to ${V2_WS_PROTOCOL}.`
  );
  return V2_WS_PROTOCOL;
}

// src/sdk/websocket_v3_frames.ts
var EMPTY_V3_PAYLOAD_ERR = "v3 websocket payloads must contain at least one message";
function ensureMessages(messages) {
  if (messages.length === 0) {
    throw new RangeError(EMPTY_V3_PAYLOAD_ERR);
  }
}
function ensureMessageCount(messages, messageCount) {
  ensureMessages(messages);
  if (messageCount < 1 || messageCount > messages.length) {
    throw new RangeError(
      `v3 websocket payload requested ${messageCount} messages from ${messages.length}`
    );
  }
}
function concatenateMessagesV3(writer, messages, messageCount = messages.length) {
  ensureMessageCount(messages, messageCount);
  writer.clear();
  for (let i = 0; i < messageCount; i++) {
    writer.writeBytes(messages[i]);
  }
  return writer.getBuffer();
}
function countClientMessagesForV3Frame(messages, maxFrameBytes) {
  ensureMessages(messages);
  const firstMessage = messages[0];
  if (firstMessage.length > maxFrameBytes) {
    return 1;
  }
  let count = 1;
  let frameSize = firstMessage.length;
  while (count < messages.length) {
    const nextMessage = messages[count];
    const nextFrameSize = frameSize + nextMessage.length;
    if (nextFrameSize > maxFrameBytes) {
      break;
    }
    frameSize = nextFrameSize;
    count += 1;
  }
  return count;
}
function encodeClientMessagesV3(writer, messages, messageCount = messages.length) {
  return concatenateMessagesV3(writer, messages, messageCount);
}
function forEachServerMessageV3(reader, data, visit) {
  reader.reset(data);
  if (reader.remaining === 0) {
    throw new RangeError(EMPTY_V3_PAYLOAD_ERR);
  }
  let count = 0;
  while (reader.remaining > 0) {
    visit(ServerMessage.deserialize(reader));
    count += 1;
  }
  return count;
}

// src/sdk/db_connection_impl.ts
var REPLAY_TIMESTAMP_SYMBOL = /* @__PURE__ */ Symbol.for("stdb.replayTimestamp");
var RECONNECT_INITIAL_DELAY_MS = 1e3;
var RECONNECT_MAX_DELAY_MS = 3e4;
var RECONNECT_JITTER = 0.5;
var RECONNECT_MIN_DELAY_FLOOR_MS = 500;
var RECONNECT_MAX_DELAY_FLOOR_MS = 1e3;
var DEFAULT_RECONNECT_POLICY = {
  minDelayMs: RECONNECT_INITIAL_DELAY_MS,
  maxDelayMs: RECONNECT_MAX_DELAY_MS
};
function resolveReconnectPolicy(options = {}) {
  let {
    minDelayMs = RECONNECT_INITIAL_DELAY_MS,
    maxDelayMs = RECONNECT_MAX_DELAY_MS
  } = options;
  if (!(minDelayMs >= RECONNECT_MIN_DELAY_FLOOR_MS)) {
    stdbLogger(
      "warn",
      `Reconnect minDelayMs ${minDelayMs} is below the ${RECONNECT_MIN_DELAY_FLOOR_MS} ms floor; using ${RECONNECT_MIN_DELAY_FLOOR_MS} ms.`
    );
    minDelayMs = RECONNECT_MIN_DELAY_FLOOR_MS;
  }
  const maxFloor = Math.max(RECONNECT_MAX_DELAY_FLOOR_MS, minDelayMs);
  if (!(maxDelayMs >= maxFloor)) {
    stdbLogger(
      "warn",
      `Reconnect maxDelayMs ${maxDelayMs} is below the ${maxFloor} ms floor; using ${maxFloor} ms.`
    );
    maxDelayMs = maxFloor;
  }
  return { minDelayMs, maxDelayMs };
}
var TOKEN_REFRESH_MARGIN_FRACTION = 0.05;
var TOKEN_REFRESH_MIN_MARGIN_MS = 3e4;
function computeReconnectDelayMs(attempt, random = Math.random, { minDelayMs, maxDelayMs } = DEFAULT_RECONNECT_POLICY) {
  const base = Math.min(
    minDelayMs * Math.pow(2, Math.max(0, attempt - 1)),
    maxDelayMs
  );
  const jittered = base * (1 + RECONNECT_JITTER * (2 * random() - 1));
  return Math.max(minDelayMs, Math.min(jittered, maxDelayMs));
}
function tokenExpired(token, nowMs = Date.now()) {
  const exp = decodeJwtClaims(token)?.exp;
  return exp !== void 0 && exp * 1e3 <= nowMs;
}
function tokenNeedsRefresh(token, nowMs = Date.now()) {
  if (!token) {
    return true;
  }
  const claims = decodeJwtClaims(token);
  if (claims === void 0 || claims.exp === void 0) {
    return true;
  }
  const expiryMs = claims.exp * 1e3;
  const lifetimeMs = claims.iat !== void 0 ? expiryMs - claims.iat * 1e3 : void 0;
  const margin = Math.max(
    TOKEN_REFRESH_MIN_MARGIN_MS,
    lifetimeMs !== void 0 ? lifetimeMs * TOKEN_REFRESH_MARGIN_FRACTION : 0
  );
  return expiryMs - nowMs <= margin;
}
function errorFromEvent(value, fallbackMessage) {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === "object" && value !== null) {
    const message = "message" in value ? value.message : void 0;
    const error = "error" in value ? value.error : void 0;
    if (error instanceof Error) {
      return error;
    }
    if (typeof message === "string" && message.length > 0) {
      return new Error(message, { cause: value });
    }
  }
  return new Error(fallbackMessage, { cause: value });
}
function isTerminalConnectError(error) {
  return error instanceof IdentityChangedError || error instanceof WebSocketProtocolError;
}
function isAuthError(error) {
  return error instanceof WebSocketTokenError && (error.status === 401 || error.status === 403);
}
var WebSocketProtocolError = class extends Error {
};
var SESSION_BUSY_CLOSE_CODE = 4e3;
function decodeJwtClaims(token) {
  const parts = token.split(".");
  if (parts.length < 2) {
    return void 0;
  }
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload.padEnd(
      payload.length + (4 - payload.length % 4) % 4,
      "="
    );
    const json = atob(padded);
    const claims = JSON.parse(json);
    if (typeof claims !== "object" || claims === null) {
      return void 0;
    }
    const exp = "exp" in claims ? claims.exp : void 0;
    const iat = "iat" in claims ? claims.iat : void 0;
    return {
      exp: typeof exp === "number" && Number.isFinite(exp) ? exp : void 0,
      iat: typeof iat === "number" && Number.isFinite(iat) ? iat : void 0
    };
  } catch {
    return void 0;
  }
}
function quietRejecter(promise, reject) {
  return (reason) => {
    promise.catch(() => {
    });
    reject(reason);
  };
}
function quietRejection(reason) {
  const promise = Promise.reject(reason);
  promise.catch(() => {
  });
  return promise;
}
function thenQuietOnLoss(inner, map) {
  const { promise, resolve, reject } = createDeferred();
  inner.then(
    (value) => {
      try {
        resolve(map(value));
      } catch (e) {
        reject(e);
      }
    },
    (e) => {
      if (e instanceof DisconnectedError || e instanceof UnknownCallResultError) {
        promise.catch(() => {
        });
      }
      reject(e);
    }
  );
  return promise;
}
var TEXT_ENCODER = new TextEncoder();
function getClientMessageVariantTag(name) {
  if (ClientMessage.algebraicType.tag !== "Sum") {
    throw new TypeError("ClientMessage must be a sum type");
  }
  const tag = ClientMessage.algebraicType.value.variants.findIndex(
    (variant) => variant.name === name
  );
  if (tag === -1) {
    throw new RangeError(`Unknown ClientMessage variant: ${name}`);
  }
  return tag;
}
var CLIENT_MESSAGE_CALL_REDUCER_TAG = getClientMessageVariantTag("CallReducer");
var CLIENT_MESSAGE_CALL_PROCEDURE_TAG = getClientMessageVariantTag("CallProcedure");
var MAX_V3_OUTBOUND_FRAME_BYTES = 256 * 1024;
var WS_READY_STATE_CLOSING = 2;
var WS_READY_STATE_CLOSED = 3;
var DbConnectionImpl2 = class {
  /**
   * Whether or not the connection is active.
   */
  isActive = false;
  /**
   * Whether `disconnect()` has been called on this connection.
   * Once requested, the connection will not be reused: managed environments
   * (such as the React `SpacetimeDBProvider`) use this to avoid reconnecting
   * after an intentional disconnect.
   */
  isDisconnectRequested = false;
  /**
   * The websocket error that ended an established connection, if any,
   * normalized to `Error`. Passed to the `disconnect` emit so `onDisconnect`
   * callbacks receive the documented `error?: Error` shape.
   */
  #connectionError = void 0;
  /**
   * Whether the underlying websocket has entered `CLOSING` (2) or `CLOSED`
   * (3). This becomes true even when the browser never delivered an
   * `onclose` event, for example if the socket was torn down while the tab was
   * frozen or the machine was asleep. The liveness listeners (see
   * {@link DbConnectionImpl.#installLivenessListeners}) use this to detect
   * such "zombie" sockets when the page resumes and to force a reconnect.
   *
   * Returns false while the socket is still `CONNECTING`/`OPEN`, or before
   * the socket has been created.
   */
  get isSocketClosed() {
    const ws = this.ws;
    if (!ws) {
      return false;
    }
    return ws.readyState === WS_READY_STATE_CLOSING || ws.readyState === WS_READY_STATE_CLOSED;
  }
  /**
   * This connection's public identity.
   */
  identity = void 0;
  /**
   * This connection's private authentication token.
   */
  token = void 0;
  /** @internal */
  [INTERNAL_REMOTE_MODULE]() {
    return this.#remoteModule;
  }
  /**
   * The accessor field to access the tables in the database and associated
   * callback functions.
   */
  db;
  /**
   * The accessor field to access the reducers in the database.
   */
  reducers;
  /**
   * The accessor field to access the procedures in the database.
   */
  procedures;
  /**
   * Controls a replay session on this connection. For connections built
   * without `DbConnectionBuilder.withReplay()`, this object still exists
   * but calling `start()` will error out on the server.
   */
  replay;
  /**
   * The `ConnectionId` of the connection to to the database.
   */
  connectionId = ConnectionId.random();
  #connectionIdHex = this.connectionId.toHexString();
  // Stable across sockets; each attempt still gets a fresh ConnectionId.
  #sessionIdHex = ConnectionId.random().toHexString();
  #automaticReconnect;
  #reconnectPolicy;
  #tokenProvider;
  #wsBaseUrl;
  #nameOrAddress;
  #createWSFn;
  #compression;
  #lightMode;
  #confirmedReads;
  // Invalidates events and asynchronous work from discarded sockets.
  #socketGeneration = 0;
  #hasEverConnected = false;
  #connectionEnded = false;
  #reconnectAttempt = 0;
  #reconnectTimer;
  #forceTokenRefresh = false;
  /**
   * TurboTwin: a connection without automatic reconnection lost its socket.
   * It never comes back, so its calls fail instead of waiting (TurboTwin-SDK#4).
   */
  #socketLost = false;
  #usedFreshToken = false;
  #livenessCleanup;
  #pendingReplay;
  #preparingReplay = false;
  #socketEstablished = false;
  // These fields are meant to be strictly private.
  #queryId = 0;
  #requestId = 0;
  #eventId = 0;
  #emitter;
  #inboundQueue = [];
  #inboundQueueOffset = 0;
  #isDrainingInboundQueue = false;
  /**
   * Set when a row failed to decode, which means the table cache can no longer
   * be trusted. Stops the inbound drain and is surfaced as the `disconnect`
   * event's error so the app can tell schema drift apart from a user-requested
   * `disconnect()` (which reports no error).
   */
  #fatalDecodeError;
  #outboundQueue = [];
  #isOutboundFlushScheduled = false;
  #negotiatedWsProtocol = V2_WS_PROTOCOL;
  /**
   * TurboTwin: a replay connection negotiates `*.bsatn.turbotwin`, where every
   * message is wrapped in the `TtClientMessage` / `TtServerMessage` envelope.
   * Fixed at build time: messages queued before the socket opens are already
   * encoded with the envelope.
   */
  #turbotwin;
  #subscriptionManager = new SubscriptionManager();
  #remoteModule;
  #reducerCallbacks = /* @__PURE__ */ new Map();
  #reducerCallInfo = /* @__PURE__ */ new Map();
  #procedureCallbacks = /* @__PURE__ */ new Map();
  #sqlQueryCallbacks = /* @__PURE__ */ new Map();
  /**
   * Reject pending reducer and procedure calls when the socket drops.
   */
  #pendingCallRejecters = /* @__PURE__ */ new Map();
  #rowDeserializers;
  #rowIdMetadata;
  #reducerArgsSerializers;
  #procedureSerializers;
  #reducerNameBytes;
  #procedureNameBytes;
  #sourceNameToTableDef;
  #messageReader = new BinaryReader(new Uint8Array());
  #rowListReader = new BinaryReader(new Uint8Array());
  // Tables whose rows carried columns these bindings do not know (warned once).
  #appendedColumnTables = /* @__PURE__ */ new Set();
  #clientFrameEncoder = new BinaryWriter(1024);
  #boundSubscriptionBuilder;
  #boundDisconnect;
  // These fields are not part of the public API, but in a pinch you
  // could use JavaScript to access them by bypassing TypeScript's
  // private fields.
  // We use them in testing.
  clientCache;
  ws;
  wsPromise;
  constructor({
    uri,
    nameOrAddress,
    identity,
    token,
    tokenProvider,
    emitter,
    remoteModule,
    createWSFn,
    compression,
    lightMode,
    confirmedReads,
    replay,
    automaticReconnect,
    reconnectPolicy
  }) {
    stdbLogger("info", "Connecting to SpacetimeDB WS...");
    const url = new URL(uri.toString());
    if (!/^wss?:/.test(uri.protocol)) {
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    }
    this.identity = identity;
    this.token = token;
    this.#remoteModule = remoteModule;
    this.#emitter = emitter;
    this.#boundSubscriptionBuilder = this.subscriptionBuilder.bind(this);
    this.#boundDisconnect = this.disconnect.bind(this);
    this.#rowDeserializers = /* @__PURE__ */ Object.create(null);
    this.#rowIdMetadata = /* @__PURE__ */ Object.create(null);
    this.#sourceNameToTableDef = /* @__PURE__ */ Object.create(null);
    for (const table2 of Object.values(remoteModule.tables)) {
      this.#rowDeserializers[table2.sourceName] = ProductType.makeDeserializer(
        table2.rowType
      );
      this.#sourceNameToTableDef[table2.sourceName] = table2;
      const primaryKeyColumn = Object.entries(table2.columns).find(
        ([, column]) => column.columnMetadata.isPrimaryKey
      );
      this.#rowIdMetadata[table2.sourceName] = primaryKeyColumn ? {
        primaryKeyColName: primaryKeyColumn[0],
        primaryKeyColType: primaryKeyColumn[1].typeBuilder.algebraicType
      } : {};
    }
    this.#reducerArgsSerializers = /* @__PURE__ */ Object.create(null);
    this.#reducerNameBytes = /* @__PURE__ */ Object.create(null);
    for (const reducer of remoteModule.reducers) {
      this.#reducerArgsSerializers[reducer.name] = {
        serialize: ProductType.makeSerializer(reducer.paramsType),
        deserialize: ProductType.makeDeserializer(reducer.paramsType)
      };
      this.#reducerNameBytes[reducer.name] = TEXT_ENCODER.encode(reducer.name);
    }
    this.#procedureSerializers = /* @__PURE__ */ Object.create(null);
    this.#procedureNameBytes = /* @__PURE__ */ Object.create(null);
    for (const procedure of remoteModule.procedures) {
      this.#procedureSerializers[procedure.name] = {
        serializeArgs: ProductType.makeSerializer(
          new ProductBuilder(procedure.params).algebraicType.value
        ),
        deserializeReturn: AlgebraicType.makeDeserializer(
          procedure.returnType.algebraicType
        )
      };
      this.#procedureNameBytes[procedure.name] = TEXT_ENCODER.encode(
        procedure.name
      );
    }
    this.clientCache = new ClientCache();
    this.db = this.#makeDbView();
    this.reducers = this.#makeReducers(remoteModule);
    this.procedures = this.#makeProcedures(remoteModule);
    this.#turbotwin = !!replay;
    this.replay = new ReplayController({
      sendMessage: (msg) => this.#sendReplayMessage(msg),
      nextRequestId: () => this.#getNextRequestId()
    });
    if (automaticReconnect && replay) {
      stdbLogger(
        "warn",
        "withAutomaticReconnect() is ignored on a replay connection"
      );
    }
    this.#automaticReconnect = (automaticReconnect ?? false) && !replay;
    this.#preparingReplay = this.#automaticReconnect;
    this.#reconnectPolicy = reconnectPolicy ?? DEFAULT_RECONNECT_POLICY;
    this.#tokenProvider = tokenProvider;
    this.#wsBaseUrl = url;
    this.#nameOrAddress = nameOrAddress;
    this.#createWSFn = createWSFn;
    this.#compression = compression;
    this.#lightMode = lightMode;
    this.#confirmedReads = confirmedReads;
    this.wsPromise = this.#openSocket();
  }
  async #openSocket() {
    const generation = ++this.#socketGeneration;
    if (this.#hasEverConnected) {
      this.#setConnectionId(ConnectionId.random());
    }
    this.#usedFreshToken = false;
    const url = new URL(this.#wsBaseUrl.toString());
    url.searchParams.set("connection_id", this.#connectionIdHex);
    try {
      const authToken = await this.#tokenForAttempt();
      if (generation !== this.#socketGeneration || this.#connectionEnded) {
        return void 0;
      }
      this.token = authToken;
      const ws = await this.#createWSFn({
        url,
        nameOrAddress: this.#nameOrAddress,
        wsProtocol: this.#turbotwin ? [...TURBOTWIN_WS_PROTOCOLS] : [...PREFERRED_WS_PROTOCOLS],
        authToken,
        compression: this.#compression,
        lightMode: this.#lightMode,
        confirmedReads: this.#confirmedReads,
        connectionId: this.#connectionIdHex,
        sessionId: this.#automaticReconnect ? this.#sessionIdHex : void 0,
        replay: this.#turbotwin
      });
      if (generation !== this.#socketGeneration || this.#connectionEnded) {
        ws.close();
        return void 0;
      }
      this.ws = ws;
      this.#socketEstablished = false;
      const isCurrent = () => generation === this.#socketGeneration && !this.#connectionEnded;
      const handleLoss = (error, isErrorEvent) => {
        if (!isCurrent()) return;
        this.isActive = false;
        if (!this.#automaticReconnect) {
          this.#failCallsOfLostSocket();
          this.#emitter.emit(
            isErrorEvent ? "connectError" : "disconnect",
            this,
            isErrorEvent ? error : this.#connectionError
          );
        } else if (this.#socketEstablished) {
          this.#handleConnectionLoss(error);
        } else {
          this.#handleAttemptFailure(error);
        }
      };
      ws.onclose = (event) => {
        if (!isCurrent()) return;
        this.replay._disconnect();
        if (this.#automaticReconnect && this.#hasEverConnected && !this.#socketEstablished && event.code === SESSION_BUSY_CLOSE_CODE) {
          this.#discardSocket();
          const delayMs = this.#reconnectDelayMs(1);
          this.#emitter.emit(
            "connectError",
            this,
            new Error("Session busy"),
            this.#reconnectAttempt,
            delayMs
          );
          this.#scheduleReconnect(this.#reconnectAttempt, delayMs);
          return;
        }
        const message = `WebSocket closed (code ${event.code}${event.reason ? `: ${event.reason}` : ""})`;
        const error = [1002, 1003, 1007, 1008].includes(event.code) ? new WebSocketProtocolError(message) : new Error(message);
        if (!this.#automaticReconnect) {
          this.isActive = false;
          this.#failCallsOfLostSocket();
          this.#emitter.emit(
            "disconnect",
            this,
            this.#fatalDecodeError ?? this.#connectionError ?? (event.wasClean ? void 0 : error)
          );
          return;
        }
        if (this.#fatalDecodeError) {
          this.#endConnection(this.#fatalDecodeError);
          return;
        }
        handleLoss(error, false);
      };
      ws.onerror = (event) => {
        if (!isCurrent()) return;
        const error = errorFromEvent(event, "WebSocket error");
        if (!this.#automaticReconnect && this.#hasEverConnected) {
          this.isActive = false;
          this.#connectionError = error;
          ws.close();
          return;
        }
        handleLoss(error, true);
      };
      ws.onopen = () => {
        if (isCurrent()) this.#handleOnOpen();
      };
      ws.onmessage = (message) => {
        if (isCurrent()) this.#handleOnMessage(message);
      };
      return ws;
    } catch (e) {
      if (generation !== this.#socketGeneration || this.#connectionEnded) {
        return void 0;
      }
      stdbLogger("error", "Error connecting to SpacetimeDB WS");
      this.isActive = false;
      this.#handleAttemptFailure(errorFromEvent(e, "Failed to connect"));
      return void 0;
    }
  }
  async #tokenForAttempt() {
    if (!this.#tokenProvider) {
      return this.token;
    }
    if (!this.#hasEverConnected) {
      if (this.token !== void 0 && !tokenExpired(this.token)) {
        return this.token;
      }
    } else if (!this.#forceTokenRefresh && !tokenNeedsRefresh(this.token)) {
      return this.token;
    }
    const token = await this.#tokenProvider();
    this.#forceTokenRefresh = false;
    this.#usedFreshToken = true;
    return token ?? this.token;
  }
  #handleConnectionLoss(error) {
    if (this.#connectionEnded) {
      return;
    }
    this.#discardSocket();
    this.#failInFlightCalls(new UnknownCallResultError());
    const willReconnect = this.#automaticReconnect && !this.isDisconnectRequested && !(error instanceof WebSocketProtocolError);
    if (!willReconnect) {
      this.#endConnection(error);
      return;
    }
    const attempt = this.#reconnectAttempt + 1;
    const delayMs = this.#reconnectDelayMs(attempt);
    try {
      this.#emitter.emit("disconnect", this, error, attempt, delayMs);
    } finally {
      this.#scheduleReconnect(attempt, delayMs);
    }
  }
  #handleAttemptFailure(error) {
    if (this.#connectionEnded) {
      return;
    }
    this.#discardSocket();
    this.#failInFlightCalls(new UnknownCallResultError());
    const willReconnect = this.#automaticReconnect && this.#hasEverConnected && !this.isDisconnectRequested && !isTerminalConnectError(error) && !(isAuthError(error) && (!this.#tokenProvider || this.#usedFreshToken));
    if (!willReconnect) {
      this.#endConnection(void 0, { alreadyReported: true });
      this.#emitter.emit("connectError", this, error);
      return;
    }
    if (this.#tokenProvider && isAuthError(error)) {
      this.#forceTokenRefresh = true;
    }
    const attempt = this.#reconnectAttempt + 1;
    const delayMs = this.#reconnectDelayMs(attempt);
    try {
      this.#emitter.emit("connectError", this, error, attempt, delayMs);
    } finally {
      this.#scheduleReconnect(attempt, delayMs);
    }
  }
  #reconnectDelayMs(attempt) {
    return computeReconnectDelayMs(attempt, Math.random, this.#reconnectPolicy);
  }
  #scheduleReconnect(attempt, delayMs) {
    if (this.#connectionEnded || this.isDisconnectRequested) return;
    this.#reconnectAttempt = attempt;
    this.#clearReconnectTimer();
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = void 0;
      if (this.#connectionEnded || this.isDisconnectRequested) {
        return;
      }
      this.wsPromise = this.#openSocket();
    }, delayMs);
  }
  #clearReconnectTimer() {
    if (this.#reconnectTimer !== void 0) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = void 0;
    }
  }
  #endConnection(disconnectError, options) {
    if (this.#connectionEnded) {
      return;
    }
    this.#connectionEnded = true;
    this.isActive = false;
    this.#clearReconnectTimer();
    this.#discardSocket();
    this.#removeLivenessListeners();
    this.#failInFlightCalls(new UnknownCallResultError());
    if (!options?.alreadyReported) {
      this.#emitter.emit("disconnect", this, disconnectError);
    }
  }
  #failInFlightCalls(error) {
    const rejecters = [...this.#pendingCallRejecters.values()];
    this.#pendingCallRejecters.clear();
    this.#reducerCallbacks.clear();
    this.#reducerCallInfo.clear();
    this.#procedureCallbacks.clear();
    for (const cb of this.#sqlQueryCallbacks.values()) {
      rejecters.push(quietRejecter(cb.promise, cb.reject));
    }
    this.#sqlQueryCallbacks.clear();
    for (const reject of rejecters) {
      reject(error);
    }
  }
  /** True while the SDK is between a lost connection and a completed reconnect. */
  get isReconnecting() {
    return this.#automaticReconnect && this.#hasEverConnected && !this.isActive && !this.#connectionEnded;
  }
  #discardSocket() {
    this.#socketGeneration += 1;
    this.isActive = false;
    this.#socketEstablished = false;
    this.#pendingReplay = void 0;
    this.#preparingReplay = this.#hasEverConnected;
    this.#outboundQueue.length = 0;
    this.#inboundQueue.length = 0;
    const ws = this.ws;
    this.ws = void 0;
    ws?.close();
    for (const [id, entry] of this.#subscriptionManager.subscriptions) {
      if (entry.unsubscribeRequested) this.#endSubscription(id);
    }
  }
  // Resume events recover sockets that closed silently while the page was frozen.
  #installLivenessListeners() {
    if (!this.#automaticReconnect || this.#livenessCleanup) {
      return;
    }
    const doc = typeof document !== "undefined" ? document : void 0;
    const win = typeof window !== "undefined" ? window : void 0;
    if (!doc && !win) {
      return;
    }
    const onResume = () => this.#handleLivenessResume();
    const onVisibilityChange = () => {
      if (doc?.visibilityState === "visible") {
        onResume();
      }
    };
    doc?.addEventListener("visibilitychange", onVisibilityChange);
    win?.addEventListener("focus", onResume);
    win?.addEventListener("online", onResume);
    win?.addEventListener("pageshow", onResume);
    this.#livenessCleanup = () => {
      doc?.removeEventListener("visibilitychange", onVisibilityChange);
      win?.removeEventListener("focus", onResume);
      win?.removeEventListener("online", onResume);
      win?.removeEventListener("pageshow", onResume);
      this.#livenessCleanup = void 0;
    };
  }
  #removeLivenessListeners() {
    this.#livenessCleanup?.();
  }
  #handleLivenessResume() {
    if (this.#connectionEnded || this.isDisconnectRequested) {
      return;
    }
    if (this.isSocketClosed) {
      const error = new Error("WebSocket closed while suspended");
      if (this.#socketEstablished) this.#handleConnectionLoss(error);
      else this.#handleAttemptFailure(error);
      return;
    }
    if (this.#reconnectTimer !== void 0) {
      this.#clearReconnectTimer();
      this.wsPromise = this.#openSocket();
    }
  }
  #getNextQueryId = () => {
    const queryId = this.#queryId;
    this.#queryId += 1;
    return queryId;
  };
  #getNextRequestId = () => this.#requestId++;
  #makeDbView() {
    const view = /* @__PURE__ */ Object.create(null);
    for (const tbl of Object.values(this.#sourceNameToTableDef)) {
      const [target, key] = accessorSlot(view, tbl.accessorName);
      Object.defineProperty(target, key, {
        enumerable: true,
        configurable: false,
        get: () => this.clientCache.getOrCreateTable(tbl)
      });
    }
    return view;
  }
  #makeReducers(def) {
    const out = {};
    for (const reducer of def.reducers) {
      const reducerName = reducer.name;
      const encodedReducerName = this.#reducerNameBytes[reducerName];
      const [target, key] = accessorSlot(out, reducer.accessorName);
      const { serialize: serializeArgs } = this.#reducerArgsSerializers[reducerName];
      target[key] = (params) => {
        const writer = this.#reducerArgsEncoder;
        writer.clear();
        serializeArgs(writer, params);
        const argsBuffer = writer.getBuffer();
        return this.#callReducerWithEncodedName(
          reducerName,
          encodedReducerName,
          argsBuffer,
          params
        );
      };
    }
    return out;
  }
  #makeProcedures(def) {
    const out = {};
    const writer = new BinaryWriter(1024);
    for (const procedure of def.procedures) {
      const procedureName = procedure.name;
      const encodedProcedureName = this.#procedureNameBytes[procedureName];
      const [target, key] = accessorSlot(out, procedure.accessorName);
      const { serializeArgs, deserializeReturn } = this.#procedureSerializers[procedureName];
      target[key] = (params) => {
        writer.clear();
        serializeArgs(writer, params);
        const argsBuffer = writer.getBuffer();
        return thenQuietOnLoss(
          this.#callProcedureWithEncodedName(
            procedureName,
            encodedProcedureName,
            argsBuffer
          ),
          (returnBuf) => deserializeReturn(new BinaryReader(returnBuf))
        );
      };
    }
    return out;
  }
  /**
   * Start a replay session authorized by a module procedure whose declared
   * return type is the well-known `Spacetimedb::ReplayGrant`.
   *
   * The server invokes the procedure with this connection's caller as
   * `ctx.sender`; the returned grant bounds the session to one table and an
   * inclusive time window. Procedure failure — or naming a procedure with any
   * other declared return type — denies the session (fail closed). The
   * connection must have been built with `withReplay()` and have an active
   * subscription, exactly as for `this.replay.start()`.
   *
   * Typed equivalent of setting `ReplayStartOptions.procedure` by hand: the
   * procedure's arguments are typed against its schema and BSATN-serialized
   * here. The returned promise resolves on the server's `ReplayStarted` ack,
   * like `ReplayController.start`.
   */
  startReplayAuthorized(procedureAccessor, params, opts) {
    const procedure = this.#remoteModule.procedures.find(
      (p) => p.accessorName === procedureAccessor
    );
    if (procedure === void 0) {
      throw new Error(
        `No procedure with accessor name \`${procedureAccessor}\``
      );
    }
    const writer = new BinaryWriter(1024);
    this.#procedureSerializers[procedure.name].serializeArgs(writer, params);
    return this.replay.start({
      ...opts,
      procedure: { procedure: procedure.name, args: writer.getBuffer() }
    });
  }
  #makeEventContext(event) {
    return {
      db: this.db,
      reducers: this.reducers,
      isActive: this.isActive,
      subscriptionBuilder: this.#boundSubscriptionBuilder,
      disconnect: this.#boundDisconnect,
      event
    };
  }
  // NOTE: This is very important!!! This is the actual function that
  // gets called when you call `connection.subscriptionBuilder()`.
  // The `subscriptionBuilder` function which is generated, just shadows
  // this function in the type system, but not the actual implementation!
  // Do not remove this function, or shoot yourself in the foot please.
  // It's not clear what would be a better way to do this at this exact
  // moment.
  subscriptionBuilder = () => {
    return new SubscriptionBuilderImpl(this);
  };
  getFromBuilder() {
    return makeFromBuilder(
      this.#remoteModule.tables
    );
  }
  registerSubscription(handle, handleEmitter, querySql) {
    const querySetId = this.#getNextQueryId();
    this.#subscriptionManager.subscriptions.set(querySetId, {
      handle,
      emitter: handleEmitter,
      // Retained so the subscription can be replayed after a reconnect.
      querySql: [...querySql]
    });
    if (!this.#preparingReplay) this.#sendSubscription(querySetId, querySql);
    return querySetId;
  }
  #sendSubscription(querySetId, querySql) {
    const requestId = this.#getNextRequestId();
    this.#sendMessage(
      ClientMessage.Subscribe({
        queryStrings: querySql,
        querySetId: { id: querySetId },
        requestId
      })
    );
  }
  #replaySubscriptions() {
    const entries = [...this.#subscriptionManager.subscriptions.entries()];
    const sets = [];
    const replayed = /* @__PURE__ */ new Map();
    for (const [, entry] of entries) {
      const querySetId = this.#getNextQueryId();
      entry.handle.rebindQuerySetId(querySetId);
      replayed.set(querySetId, entry);
      sets.push({
        querySetId: { id: querySetId },
        queryStrings: entry.querySql
      });
    }
    this.#subscriptionManager.subscriptions = replayed;
    const requestId = this.#getNextRequestId();
    this.#pendingReplay = {
      requestId,
      querySetIds: new Set(replayed.keys())
    };
    this.#preparingReplay = false;
    if (sets.length === 0) {
      this.#applyReplayBatch({ requestId, results: [] });
      return;
    }
    this.#sendMessage(
      ClientMessage.SubscribeBatch({
        requestId,
        sets
      })
    );
  }
  #applyReplayBatch(applied) {
    const pending = this.#pendingReplay;
    const resultIds = new Set(
      applied.results.map((result) => result.querySetId.id)
    );
    if (!pending || pending.requestId !== applied.requestId || resultIds.size !== applied.results.length || resultIds.size !== pending.querySetIds.size || [...resultIds].some((id) => !pending.querySetIds.has(id))) {
      this.#handleProtocolError(
        new Error("Unexpected subscription replay response")
      );
      return;
    }
    this.#pendingReplay = void 0;
    const event = {
      id: this.#nextEventId(),
      tag: "SubscribeApplied"
    };
    const eventContext = this.#makeEventContext(event);
    const tableUpdates = [];
    for (const tableDef of Object.values(this.#sourceNameToTableDef)) {
      const table2 = this.clientCache.tables.get(tableDef.accessorName);
      const operations = table2?.snapshotDeleteOperations() ?? [];
      if (operations.length > 0) {
        tableUpdates.push({ tableName: tableDef.sourceName, operations });
      }
    }
    const failures = [];
    for (const result of applied.results) {
      const entry = this.#subscriptionManager.subscriptions.get(
        result.querySetId.id
      );
      if (!entry) {
        continue;
      }
      if (result.outcome.tag === "Error") {
        this.#subscriptionManager.subscriptions.delete(result.querySetId.id);
        failures.push({ entry, error: result.outcome.value });
        continue;
      }
      tableUpdates.push(
        ...this.#queryRowsToTableUpdates(result.outcome.value, "insert")
      );
    }
    const merged = this.#mergeTableUpdates(tableUpdates);
    const callbacks = this.#applyTableUpdates(merged, eventContext, {
      // A row which is unchanged across the outage appears as a
      // delete/insert pair and must produce no callback.
      skipIdenticalUpdates: true
    });
    const { event: _, ...subscriptionEventContext } = eventContext;
    for (const [querySetId, entry] of this.#subscriptionManager.subscriptions) {
      if (pending.querySetIds.has(querySetId)) {
        entry.emitter.emit("applied", subscriptionEventContext);
      }
    }
    for (const { entry, error: message } of failures) {
      const error = Error(message);
      const errorEventContext = this.#makeEventContext({
        id: this.#nextEventId(),
        tag: "Error",
        value: error
      });
      entry.emitter.emit(
        "error",
        { ...errorEventContext, event: error },
        error
      );
    }
    this.#dispatchPendingCallbacks(callbacks);
  }
  #endSubscription(querySetId) {
    const entry = this.#subscriptionManager.subscriptions.get(querySetId);
    this.#subscriptionManager.subscriptions.delete(querySetId);
    const { event: _, ...ctx } = this.#makeEventContext({
      id: this.#nextEventId(),
      tag: "UnsubscribeApplied"
    });
    entry?.emitter.emit("end", ctx);
  }
  unregisterSubscription(querySetId) {
    const entry = this.#subscriptionManager.subscriptions.get(querySetId);
    if (!entry) return;
    entry.unsubscribeRequested = true;
    if (this.#automaticReconnect && (!this.isActive || this.#preparingReplay)) {
      this.#endSubscription(querySetId);
      return;
    }
    const requestId = this.#getNextRequestId();
    this.#sendMessage(
      ClientMessage.Unsubscribe({
        querySetId: { id: querySetId },
        requestId,
        flags: UnsubscribeFlags.SendDroppedRows
      })
    );
  }
  /**
   * Shared low-level row reader used by every decode path in the SDK.
   *
   * Returns `null` when the SDK has no generated deserializer for the table
   * (fallback: caller should skip). When `withSystemTime=true`, reads an i64
   * micros prefix BEFORE the row body and returns it as a `Timestamp` in
   * `systemTime`. This matches both the OneOffQuery temporal result envelope
   * and the `ReplayTimestampedUpdate` replay frame (§4.6 of the
   * precise-mode design doc).
   */
  #readRow(reader, tableName, withSystemTime) {
    const deserializeRow = this.#rowDeserializers[tableName];
    if (!deserializeRow) return null;
    let systemTime;
    if (withSystemTime) {
      systemTime = new Timestamp(reader.readI64());
    }
    let row;
    try {
      row = deserializeRow(reader);
    } catch (e) {
      const error = new Error(
        `Failed to decode a row of '${tableName}' \u2014 generated bindings are out of date with the module schema. Disconnecting; regenerate bindings. (${e})`
      );
      stdbLogger("error", error.message);
      if (!this.#fatalDecodeError) {
        this.#fatalDecodeError = error;
        this.disconnect();
      }
      return null;
    }
    return { row, systemTime };
  }
  /**
   * Decode every row of a row list, one slice per row as the server's size
   * hint cuts them. A column the module appended after these bindings were
   * generated (an auto-migrated change) makes every row longer than the
   * bindings read, so the rows must never be read back to back: the
   * bindings decode the columns they know and the rest of the slice is
   * skipped. A row shorter than the known columns still fails in #readRow.
   *
   * `body` is the row's bytes after any `__system_time__` prefix: the bytes
   * the row id of a table without a primary key is made from, the same in
   * #parseRowList and #parseTimestampedRowList.
   */
  *#readRows(tableName, rowList, withSystemTime) {
    const data = rowList.rowsData;
    const hint = rowList.sizeHint;
    const prefix = withSystemTime ? 8 : 0;
    const reader = this.#rowListReader;
    let slices;
    if (hint.tag === "FixedSize" && hint.value > 0) {
      slices = [];
      for (let start = 0; start < data.length; start += hint.value) {
        slices.push(data.subarray(start, start + hint.value));
      }
    } else if (hint.tag === "RowOffsets" && hint.value.length > 0) {
      const offsets = hint.value;
      slices = offsets.map(
        (start, i) => data.subarray(
          Number(start),
          i + 1 < offsets.length ? Number(offsets[i + 1]) : data.length
        )
      );
    }
    if (!slices) {
      reader.reset(data);
      while (reader.remaining > 0) {
        const start = reader.offset + prefix;
        const read = this.#readRow(reader, tableName, withSystemTime);
        if (!read) return;
        yield { ...read, body: data.subarray(start, reader.offset) };
      }
      return;
    }
    for (const slice of slices) {
      reader.reset(slice);
      const read = this.#readRow(reader, tableName, withSystemTime);
      if (!read) return;
      if (reader.remaining > 0 && !this.#appendedColumnTables.has(tableName)) {
        this.#appendedColumnTables.add(tableName);
        stdbLogger(
          "warn",
          `Rows of '${tableName}' carry ${reader.remaining} byte(s) past the known columns: the bindings are older than the module. Skipping the unknown columns; regenerate bindings to read them.`
        );
      }
      yield { ...read, body: slice.subarray(prefix) };
    }
  }
  #parseRowList(type, tableName, rowList) {
    const rows = [];
    const rowIdMetadata = this.#rowIdMetadata[tableName];
    if (!rowIdMetadata) {
      stdbLogger(
        "warn",
        `Skipping rows for unknown table '${tableName}' \u2014 not in generated bindings. Regenerate bindings to consume this table.`
      );
      return rows;
    }
    const { primaryKeyColName, primaryKeyColType } = rowIdMetadata;
    for (const { row, body } of this.#readRows(tableName, rowList, false)) {
      let rowId = void 0;
      if (primaryKeyColName !== void 0 && primaryKeyColType !== void 0) {
        rowId = AlgebraicType.intoMapKey(
          primaryKeyColType,
          row[primaryKeyColName]
        );
      } else {
        rowId = fromByteArray(body);
      }
      rows.push({
        type,
        rowId,
        row
      });
    }
    return rows;
  }
  /**
   * Decode a `ReplayTimestampedUpdate` row list (precise-mode replay).
   *
   * Each row in the underlying `BsatnRowList` is prefixed with an i64
   * `__system_time__` (micros since Unix epoch), followed by the regular
   * BSATN-encoded row body. Returns standard `Operation`s annotated with a
   * `replayTimestamp` field that the cache-apply path uses to set
   * `EventContextInterface.replayTimestamp` per row.
   *
   * Note on rowId fallback: when a table has no primary key we encode the
   * row bytes AFTER the i64 prefix (`body` of #readRows), so the rowId
   * matches what `#parseRowList` would produce for the same row body. This
   * keeps classic and precise replay rowIds stable across modes.
   */
  #parseTimestampedRowList(type, tableName, rowList) {
    const rows = [];
    const rowIdMetadata = this.#rowIdMetadata[tableName];
    if (!rowIdMetadata) {
      stdbLogger(
        "warn",
        `Skipping timestamped rows for unknown table '${tableName}' \u2014 not in generated bindings. Regenerate bindings to consume this table.`
      );
      return rows;
    }
    const { primaryKeyColName, primaryKeyColType } = rowIdMetadata;
    for (const { row, systemTime, body } of this.#readRows(
      tableName,
      rowList,
      true
    )) {
      if (!systemTime) {
        return rows;
      }
      let rowId = void 0;
      if (primaryKeyColName !== void 0 && primaryKeyColType !== void 0) {
        rowId = AlgebraicType.intoMapKey(
          primaryKeyColType,
          row[primaryKeyColName]
        );
      } else {
        rowId = fromByteArray(body);
      }
      rows.push({
        type,
        rowId,
        row,
        replayTimestamp: systemTime
      });
    }
    return rows;
  }
  // Take a bunch of table updates and ensure that there is at most one update per table.
  #mergeTableUpdates(updates) {
    const merged = /* @__PURE__ */ new Map();
    for (const update of updates) {
      const ops = merged.get(update.tableName);
      if (ops) {
        for (const op of update.operations) ops.push(op);
      } else {
        merged.set(update.tableName, update.operations.slice());
      }
    }
    return Array.from(merged, ([tableName, operations]) => ({
      tableName,
      operations
    }));
  }
  #queryRowsToTableUpdates(rows, opType) {
    const updates = [];
    for (const tableRows of rows.tables) {
      updates.push({
        tableName: tableRows.table,
        operations: this.#parseRowList(opType, tableRows.table, tableRows.rows)
      });
    }
    return this.#mergeTableUpdates(updates);
  }
  #tableUpdateRowsToOperations(tableName, rows) {
    if (rows.tag === "PersistentTable") {
      const inserts = this.#parseRowList(
        "insert",
        tableName,
        rows.value.inserts
      );
      const deletes = this.#parseRowList(
        "delete",
        tableName,
        rows.value.deletes
      );
      return inserts.concat(deletes);
    }
    if (rows.tag === "EventTable") {
      return this.#parseRowList("insert", tableName, rows.value.events);
    }
    return [];
  }
  /**
   * TurboTwin: a precise-mode replay frame. Each row carries its own commit
   * timestamp in an i64 prefix; the cache-apply path reads `replayTimestamp`
   * and sets it on a fresh EventContext per row.
   *
   * IMPORTANT: deletes MUST come before inserts so that per-row apply
   * (the precise-mode path in #applyTableUpdates) handles PK-matched
   * update pairs correctly. When inserts come first, a row that already
   * exists in the cache (from baseline or prior frame) gets its ref
   * count bumped to 2 (no callback), then the delete brings it back to
   * 1 (no callback), so both ops are silent. By processing deletes first,
   * the delete removes the old row (ref count 1 -> 0, fires on_delete),
   * and the insert adds the new row (ref count 0 -> 1, fires on_insert).
   */
  #timestampedUpdateToTableUpdates(update) {
    const updates = [];
    for (const table2 of update.tables) {
      const deletes = this.#parseTimestampedRowList(
        "delete",
        table2.tableName,
        table2.deletes
      );
      const inserts = this.#parseTimestampedRowList(
        "insert",
        table2.tableName,
        table2.inserts
      );
      updates.push({
        tableName: table2.tableName,
        operations: deletes.concat(inserts)
      });
    }
    return this.#mergeTableUpdates(updates);
  }
  #querySetUpdateToTableUpdates(querySetUpdate) {
    const updates = [];
    for (const tableUpdate of querySetUpdate.tables) {
      let operations = [];
      for (const rows of tableUpdate.rows) {
        operations = operations.concat(
          this.#tableUpdateRowsToOperations(tableUpdate.tableName, rows)
        );
      }
      updates.push({
        tableName: tableUpdate.tableName,
        operations
      });
    }
    return this.#mergeTableUpdates(updates);
  }
  #flushOutboundQueue(wsResolved) {
    if (isV3Framing(this.#negotiatedWsProtocol)) {
      this.#flushOutboundQueueV3(wsResolved);
      return;
    }
    this.#flushOutboundQueueV2(wsResolved);
  }
  #flushOutboundQueueV2(wsResolved) {
    const pending = this.#outboundQueue.splice(0);
    for (const message of pending) {
      wsResolved.send(message);
    }
  }
  #flushOutboundQueueV3(wsResolved) {
    if (this.#outboundQueue.length === 0) {
      return;
    }
    const batchSize = countClientMessagesForV3Frame(
      this.#outboundQueue,
      MAX_V3_OUTBOUND_FRAME_BYTES
    );
    wsResolved.send(
      encodeClientMessagesV3(
        this.#clientFrameEncoder,
        this.#outboundQueue,
        batchSize
      )
    );
    if (batchSize === this.#outboundQueue.length) {
      this.#outboundQueue.length = 0;
      return;
    }
    this.#outboundQueue.copyWithin(0, batchSize);
    this.#outboundQueue.length -= batchSize;
    if (this.#outboundQueue.length > 0) {
      this.#scheduleDeferredOutboundFlush();
    }
  }
  #scheduleOutboundFlush() {
    this.#scheduleOutboundFlushWith("microtask");
  }
  #scheduleDeferredOutboundFlush() {
    this.#scheduleOutboundFlushWith("next-task");
  }
  #scheduleOutboundFlushWith(schedule) {
    if (this.#isOutboundFlushScheduled) {
      return;
    }
    this.#isOutboundFlushScheduled = true;
    const flush = () => {
      this.#isOutboundFlushScheduled = false;
      if (this.ws && this.isActive) {
        this.#flushOutboundQueue(this.ws);
      }
    };
    if (schedule === "next-task") {
      setTimeout(flush, 0);
    } else {
      queueMicrotask(flush);
    }
  }
  #reducerArgsEncoder = new BinaryWriter(1024);
  #clientMessageEncoder = new BinaryWriter(1024);
  #sendEncodedMessage(encoded, describe) {
    stdbLogger("trace", describe);
    if (this.ws && this.isActive) {
      if (!isV3Framing(this.#negotiatedWsProtocol)) {
        if (this.#outboundQueue.length) this.#flushOutboundQueue(this.ws);
        this.ws.send(encoded);
        return;
      }
      this.#outboundQueue.push(encoded.slice());
      this.#scheduleOutboundFlush();
    } else {
      this.#outboundQueue.push(encoded.slice());
    }
  }
  /** TurboTwin: on a replay connection, open the envelope with its `Std` tag. */
  #writeStdTag(writer) {
    if (this.#turbotwin) writer.writeByte(0);
  }
  #rejectCallIfDisconnected() {
    return this.#automaticReconnect && !this.isActive || this.#socketLost ? new DisconnectedError() : void 0;
  }
  /**
   * TurboTwin: upstream fails calls on a lost socket only with automatic
   * reconnection. Without it, a call waiting for its reply waited forever
   * (TurboTwin-SDK#4, the stuck "Submitting..." dialog). Calls in flight
   * reject with `UnknownCallResultError` (they may have run), and later calls
   * with `DisconnectedError`. Both stay quiet when nobody awaits them.
   */
  #failCallsOfLostSocket() {
    this.#socketLost = true;
    this.#failInFlightCalls(new UnknownCallResultError());
  }
  #sendMessage(message) {
    const writer = this.#clientMessageEncoder;
    writer.clear();
    this.#writeStdTag(writer);
    ClientMessage.serialize(writer, message);
    const encoded = writer.getBuffer();
    const isLive = !!(this.ws && this.isActive);
    this.#sendEncodedMessage(
      encoded,
      () => isLive ? `Sending message to server: ${stringify(message)}` : `Queuing message to server: ${stringify(message)}`
    );
  }
  /** TurboTwin: replay control, envelope tag 1. Replay-mode connections only. */
  #sendReplayMessage(message) {
    if (!this.#turbotwin) {
      throw new Error(
        "Replay needs a connection built with DbConnectionBuilder.withReplay()."
      );
    }
    const writer = this.#clientMessageEncoder;
    writer.clear();
    writer.writeByte(1);
    ReplayClientMessage.serialize(writer, message);
    const encoded = writer.getBuffer();
    const isLive = !!(this.ws && this.isActive);
    this.#sendEncodedMessage(
      encoded,
      () => isLive ? `Sending replay message to server: ${stringify(message)}` : `Queuing replay message to server: ${stringify(message)}`
    );
  }
  #sendCallReducerMessage(requestId, reducerNameBytes, argsBuffer) {
    const writer = this.#clientMessageEncoder;
    writer.clear();
    this.#writeStdTag(writer);
    writer.writeByte(CLIENT_MESSAGE_CALL_REDUCER_TAG);
    writer.writeU32(requestId);
    writer.writeU8(0);
    writer.writeUInt8Array(reducerNameBytes);
    writer.writeUInt8Array(argsBuffer);
    const encoded = writer.getBuffer();
    this.#sendEncodedMessage(
      encoded,
      () => `Sending reducer call message to server: requestId=${requestId}`
    );
  }
  #sendCallProcedureMessage(requestId, procedureNameBytes, argsBuffer) {
    const writer = this.#clientMessageEncoder;
    writer.clear();
    this.#writeStdTag(writer);
    writer.writeByte(CLIENT_MESSAGE_CALL_PROCEDURE_TAG);
    writer.writeU32(requestId);
    writer.writeU8(0);
    writer.writeUInt8Array(procedureNameBytes);
    writer.writeUInt8Array(argsBuffer);
    const encoded = writer.getBuffer();
    this.#sendEncodedMessage(
      encoded,
      () => `Sending procedure call message to server: requestId=${requestId}`
    );
  }
  #setConnectionId(connectionId) {
    this.connectionId = connectionId;
    this.#connectionIdHex = connectionId.toHexString();
  }
  #nextEventId() {
    this.#eventId += 1;
    return `${this.#connectionIdHex}:${this.#eventId}`;
  }
  /**
   * Handles WebSocket onOpen event.
   */
  #handleOnOpen() {
    if (this.ws) {
      this.#negotiatedWsProtocol = normalizeWsProtocol(this.ws.protocol);
    }
    this.isActive = !this.#automaticReconnect;
    this.#installLivenessListeners();
    if (this.ws && this.isActive) {
      this.#flushOutboundQueue(this.ws);
    }
  }
  #applyTableUpdates(tableUpdates, eventContext, options) {
    const pendingCallbacks = [];
    const replayStatus = this.replay.status;
    const isReplayActive = replayStatus === "replaying" || replayStatus === "paused";
    for (const tableUpdate of tableUpdates) {
      const tableName = tableUpdate.tableName;
      const tableDef = this.#sourceNameToTableDef[tableName];
      if (!tableDef) {
        continue;
      }
      const table2 = this.clientCache.getOrCreateTable(tableDef);
      if (isReplayActive) {
        const tsOps = tableUpdate.operations;
        for (const op of tsOps) {
          if (op.type !== "insert") continue;
          if (op.replayTimestamp) {
            Object.defineProperty(op.row, REPLAY_TIMESTAMP_SYMBOL, {
              value: op.replayTimestamp,
              enumerable: false,
              configurable: true,
              writable: true
            });
          }
          const rowCtx = op.replayTimestamp ? { ...eventContext, replayTimestamp: op.replayTimestamp } : eventContext;
          pendingCallbacks.push({
            type: "insert",
            table: tableName,
            cb: () => {
              table2.emitInsert(rowCtx, op.row);
            }
          });
        }
      } else {
        const newCallbacks = table2.applyOperations(
          tableUpdate.operations,
          eventContext,
          options
        );
        for (const callback of newCallbacks) {
          pendingCallbacks.push(callback);
        }
      }
    }
    return pendingCallbacks;
  }
  #applyTransactionUpdates(eventContext, tu) {
    const allUpdates = [];
    for (const querySetUpdate of tu.querySets) {
      const tableUpdates = this.#querySetUpdateToTableUpdates(querySetUpdate);
      for (const update of tableUpdates) {
        allUpdates.push(update);
      }
    }
    return this.#applyTableUpdates(
      this.#mergeTableUpdates(allUpdates),
      eventContext
    );
  }
  #dispatchPendingCallbacks(callbacks) {
    stdbLogger(
      "trace",
      () => `Calling ${callbacks.length} triggered row callbacks`
    );
    for (const callback of callbacks) {
      callback.cb();
    }
  }
  #processServerMessage(serverMessage) {
    stdbLogger(
      "trace",
      () => `Processing server message: ${stringify(serverMessage)}`
    );
    if (this.#automaticReconnect && serverMessage.tag === "InitialConnection" === this.#socketEstablished) {
      this.#handleProtocolError(
        new Error("Unexpected message during connection handshake")
      );
      return;
    }
    switch (serverMessage.tag) {
      case "InitialConnection": {
        const isReconnect = this.#hasEverConnected;
        if (isReconnect && this.identity && !this.identity.isEqual(serverMessage.value.identity)) {
          const error = new IdentityChangedError();
          this.#endConnection(void 0, { alreadyReported: true });
          this.#emitter.emit("connectError", this, error);
          break;
        }
        this.identity = serverMessage.value.identity;
        if (!this.token && serverMessage.value.token) {
          this.token = serverMessage.value.token;
        }
        this.#setConnectionId(serverMessage.value.connectionId);
        this.isActive = true;
        this.#hasEverConnected = true;
        this.#socketEstablished = true;
        this.#reconnectAttempt = 0;
        this.#emitter.emit(
          isReconnect ? "automaticReconnect" : "connect",
          this,
          this.identity,
          this.token
        );
        if (this.#connectionEnded) break;
        if (isReconnect) {
          this.#replaySubscriptions();
        } else if (this.#preparingReplay) {
          this.#preparingReplay = false;
          for (const [id, entry] of this.#subscriptionManager.subscriptions) {
            this.#sendSubscription(id, entry.querySql);
          }
        }
        if (this.ws) this.#flushOutboundQueue(this.ws);
        break;
      }
      case "SubscribeApplied": {
        const querySetId = serverMessage.value.querySetId.id;
        const subscription = this.#subscriptionManager.subscriptions.get(querySetId);
        if (!subscription) {
          stdbLogger(
            "error",
            `Received SubscribeApplied for unknown querySetId ${querySetId}.`
          );
          return;
        }
        const event = {
          id: this.#nextEventId(),
          tag: "SubscribeApplied"
        };
        const eventContext = this.#makeEventContext(event);
        const tableUpdates = this.#queryRowsToTableUpdates(
          serverMessage.value.rows,
          "insert"
        );
        const callbacks = this.#applyTableUpdates(tableUpdates, eventContext);
        const { event: _, ...subscriptionEventContext } = eventContext;
        subscription.emitter.emit("applied", subscriptionEventContext);
        this.#dispatchPendingCallbacks(callbacks);
        break;
      }
      case "UnsubscribeApplied": {
        const querySetId = serverMessage.value.querySetId.id;
        const subscription = this.#subscriptionManager.subscriptions.get(querySetId);
        if (!subscription) {
          stdbLogger(
            "error",
            `Received UnsubscribeApplied for unknown querySetId ${querySetId}.`
          );
          return;
        }
        const event = {
          id: this.#nextEventId(),
          tag: "UnsubscribeApplied"
        };
        const eventContext = this.#makeEventContext(event);
        const tableUpdates = serverMessage.value.rows ? this.#queryRowsToTableUpdates(serverMessage.value.rows, "delete") : [];
        const callbacks = this.#applyTableUpdates(tableUpdates, eventContext);
        const { event: _, ...subscriptionEventContext } = eventContext;
        subscription.emitter.emit("end", subscriptionEventContext);
        this.#subscriptionManager.subscriptions.delete(querySetId);
        this.#dispatchPendingCallbacks(callbacks);
        break;
      }
      case "SubscriptionError": {
        const querySetId = serverMessage.value.querySetId.id;
        const requestId = serverMessage.value.requestId;
        const error = Error(serverMessage.value.error);
        const event = {
          id: this.#nextEventId(),
          tag: "Error",
          value: error
        };
        const eventContext = this.#makeEventContext(event);
        const errorContext = {
          ...eventContext,
          event: error
        };
        if (requestId == null) {
          stdbLogger(
            "error",
            `Disconnecting due to error for a previously applied subscription: ${serverMessage.value.error}`
          );
          this.disconnect();
          break;
        }
        const subscription = this.#subscriptionManager.subscriptions.get(querySetId);
        if (subscription) {
          subscription.emitter.emit("error", errorContext, error);
          this.#subscriptionManager.subscriptions.delete(querySetId);
        } else {
          stdbLogger(
            "error",
            `Received SubscriptionError for unknown querySetId ${querySetId}:`,
            error
          );
        }
        break;
      }
      case "TransactionUpdate": {
        const event = {
          id: this.#nextEventId(),
          tag: "Transaction"
        };
        const eventContext = this.#makeEventContext(event);
        const callbacks = this.#applyTransactionUpdates(
          eventContext,
          serverMessage.value
        );
        this.#dispatchPendingCallbacks(callbacks);
        break;
      }
      case "ReducerResult": {
        const { requestId, result } = serverMessage.value;
        if (result.tag === "Ok") {
          const reducerInfo = this.#reducerCallInfo.get(requestId);
          const eventId = this.#nextEventId();
          const event = reducerInfo ? {
            id: eventId,
            tag: "Reducer",
            value: {
              timestamp: serverMessage.value.timestamp,
              outcome: result,
              reducer: {
                name: reducerInfo.name,
                args: reducerInfo.args
              }
            }
          } : {
            id: eventId,
            tag: "Transaction"
          };
          const eventContext = this.#makeEventContext(event);
          const callbacks = this.#applyTransactionUpdates(
            eventContext,
            result.value.transactionUpdate
          );
          this.#dispatchPendingCallbacks(callbacks);
        }
        this.#reducerCallInfo.delete(requestId);
        const cb = this.#reducerCallbacks.get(requestId);
        this.#reducerCallbacks.delete(requestId);
        cb?.(result);
        break;
      }
      case "ProcedureResult": {
        const { status, requestId } = serverMessage.value;
        const result = status.tag === "Returned" ? { tag: "Ok", value: status.value } : { tag: "Err", value: status.value };
        const cb = this.#procedureCallbacks.get(requestId);
        this.#procedureCallbacks.delete(requestId);
        cb?.(result);
        break;
      }
      case "OneOffQueryResult": {
        const { requestId, result } = serverMessage.value;
        const cb = this.#sqlQueryCallbacks.get(requestId);
        this.#sqlQueryCallbacks.delete(requestId);
        if (!cb) {
          stdbLogger(
            "warn",
            `Received OneOffQueryResult for unknown requestId ${requestId}.`
          );
          break;
        }
        const res = result;
        if ("err" in res) {
          cb.reject(new Error(res.err));
          break;
        }
        const queryRows = res.ok;
        const tables = queryRows.tables.map(
          (tableRows) => {
            const tableName = tableRows.table;
            const rows = this.#decodeOneOffRows(
              tableName,
              tableRows.rows,
              cb.temporal
            );
            return { tableName, rows };
          }
        );
        const elapsedMs = performance.now() - cb.startTime;
        cb.resolve({
          tables,
          durationMicros: BigInt(Math.round(elapsedMs * 1e3))
        });
        break;
      }
      case "SubscribeBatchApplied": {
        this.#applyReplayBatch(serverMessage.value);
        break;
      }
    }
  }
  /** TurboTwin: replay events from the envelope's `Replay` arm. */
  #processReplayMessage(replayMessage) {
    stdbLogger(
      "trace",
      () => `Processing replay message: ${stringify(replayMessage)}`
    );
    const serverMessage = replayMessage;
    switch (serverMessage.tag) {
      case "ReplayStarted": {
        this.replay._handleReplayStarted(serverMessage.value);
        break;
      }
      case "ReplaySpeedChanged": {
        this.replay._handleReplaySpeedChanged(serverMessage.value);
        break;
      }
      case "ReplayDensityChanged": {
        this.replay._handleReplayDensityChanged(serverMessage.value);
        break;
      }
      case "ReplayDirectionChanged": {
        this.replay._handleReplayDirectionChanged(serverMessage.value);
        break;
      }
      case "ReplaySeeked": {
        this.clientCache = new ClientCache();
        this.db = this.#makeDbView();
        this.replay._handleReplaySeeked(serverMessage.value);
        break;
      }
      case "ReplayPaused": {
        this.replay._handleReplayPaused(serverMessage.value);
        break;
      }
      case "ReplayResumed": {
        this.replay._handleReplayResumed(serverMessage.value);
        break;
      }
      case "ReplayCaughtUp": {
        this.replay._handleReplayCaughtUp(serverMessage.value);
        break;
      }
      case "ReplayHeartbeat": {
        this.replay._handleReplayHeartbeat(serverMessage.value);
        break;
      }
      case "ReplayEndOfHistory": {
        this.replay._handleReplayEndOfHistory(serverMessage.value);
        break;
      }
      case "ReplayError": {
        this.replay._handleReplayError(serverMessage.value);
        break;
      }
      case "ReplayTimestampedUpdate": {
        const event = {
          id: this.#nextEventId(),
          tag: "Transaction"
        };
        const callbacks = this.#applyTableUpdates(
          this.#timestampedUpdateToTableUpdates(serverMessage.value),
          this.#makeEventContext(event)
        );
        this.#dispatchPendingCallbacks(callbacks);
        break;
      }
    }
  }
  /**
   * TurboTwin: a `*.bsatn.turbotwin` payload holds one envelope (v2 framing)
   * or one or more consecutive envelopes (v3 framing).
   */
  #processTurbotwinPayload(data) {
    const reader = this.#messageReader;
    reader.reset(data);
    do {
      let message;
      try {
        message = TtServerMessage.deserialize(reader);
        if (message?.value === void 0) throw new Error("unknown tag");
      } catch (e) {
        const error = new Error(
          `Failed to decode a TurboTwin envelope; the server speaks a newer replay protocol than this SDK. Disconnecting; upgrade the SDK. (${e})`
        );
        stdbLogger("error", error.message);
        if (!this.#fatalDecodeError) {
          this.#fatalDecodeError = error;
          this.disconnect();
        }
        return;
      }
      if (message.tag === "Std") {
        this.#processServerMessage(message.value);
      } else {
        this.#processReplayMessage(message.value);
      }
    } while (reader.remaining > 0);
  }
  #processV2Message(data) {
    const reader = this.#messageReader;
    reader.reset(data);
    let message;
    try {
      message = ServerMessage.deserialize(reader);
    } catch (cause) {
      this.#handleProtocolError(cause);
      return;
    }
    this.#processServerMessage(message);
  }
  #processMessage(data) {
    if (this.#turbotwin) {
      this.#processTurbotwinPayload(data);
      return;
    }
    if (this.#negotiatedWsProtocol !== V3_WS_PROTOCOL) {
      this.#processV2Message(data);
      return;
    }
    let dispatching = false;
    const generation = this.#socketGeneration;
    try {
      forEachServerMessageV3(this.#messageReader, data, (serverMessage) => {
        if (generation !== this.#socketGeneration) return;
        dispatching = true;
        this.#processServerMessage(serverMessage);
        dispatching = false;
      });
    } catch (cause) {
      if (dispatching) throw cause;
      this.#handleProtocolError(cause);
    }
  }
  #handleProtocolError(cause) {
    if (!this.#automaticReconnect) throw cause;
    const error = new WebSocketProtocolError("Invalid server message", {
      cause
    });
    if (this.#socketEstablished) this.#handleConnectionLoss(error);
    else this.#handleAttemptFailure(error);
  }
  /**
   * Handles WebSocket onMessage event.
   * @param wsMessage MessageEvent object.
   */
  #handleOnMessage(wsMessage) {
    this.#inboundQueue.push(wsMessage.data);
    if (this.#isDrainingInboundQueue) {
      return;
    }
    this.#isDrainingInboundQueue = true;
    try {
      while (this.#inboundQueueOffset < this.#inboundQueue.length) {
        if (this.#fatalDecodeError) break;
        const data = this.#inboundQueue[this.#inboundQueueOffset];
        this.#inboundQueueOffset += 1;
        if (data) {
          try {
            this.#processMessage(data);
          } catch (e) {
            stdbLogger(
              "error",
              `Dropping an undecodable server message: ${e instanceof Error ? e.stack || e.message : e}`
            );
          }
        }
      }
    } finally {
      if (this.#inboundQueueOffset >= this.#inboundQueue.length) {
        this.#inboundQueue.length = 0;
      } else if (this.#inboundQueueOffset > 0) {
        this.#inboundQueue = this.#inboundQueue.slice(this.#inboundQueueOffset);
      }
      this.#inboundQueueOffset = 0;
      this.#isDrainingInboundQueue = false;
    }
  }
  /**
   * Call a reducer on your SpacetimeDB module.
   *
   * @param reducerName The name of the reducer to call
   * @param argsSerializer The arguments to pass to the reducer
   */
  callReducer(reducerName, argsBuffer, reducerArgs) {
    const encodedReducerName = this.#reducerNameBytes[reducerName];
    if (encodedReducerName) {
      return this.#callReducerWithEncodedName(
        reducerName,
        encodedReducerName,
        argsBuffer,
        reducerArgs
      );
    }
    return this.#callReducerGeneric(reducerName, argsBuffer, reducerArgs);
  }
  #callReducerWithEncodedName(reducerName, encodedReducerName, argsBuffer, reducerArgs) {
    const rejected = this.#rejectCallIfDisconnected();
    if (rejected) {
      return quietRejection(rejected);
    }
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#getNextRequestId();
    this.#sendCallReducerMessage(requestId, encodedReducerName, argsBuffer);
    if (reducerArgs) {
      this.#reducerCallInfo.set(requestId, {
        name: reducerName,
        args: reducerArgs
      });
    }
    this.#pendingCallRejecters.set(requestId, quietRejecter(promise, reject));
    this.#reducerCallbacks.set(requestId, (result) => {
      this.#pendingCallRejecters.delete(requestId);
      if (result.tag === "Ok" || result.tag === "OkEmpty") {
        resolve();
      } else {
        if (result.tag === "Err") {
          const reader = new BinaryReader(result.value);
          const errorString = reader.readString();
          reject(new SenderError(errorString));
        } else if (result.tag === "InternalError") {
          reject(new InternalError(result.value));
        } else {
          reject(new Error("Unexpected reducer result"));
        }
      }
    });
    return promise;
  }
  #callReducerGeneric(reducerName, argsBuffer, reducerArgs) {
    const rejected = this.#rejectCallIfDisconnected();
    if (rejected) {
      return quietRejection(rejected);
    }
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#getNextRequestId();
    const message = ClientMessage.CallReducer({
      reducer: reducerName,
      args: argsBuffer,
      requestId,
      flags: 0
    });
    this.#sendMessage(message);
    if (reducerArgs) {
      this.#reducerCallInfo.set(requestId, {
        name: reducerName,
        args: reducerArgs
      });
    }
    this.#pendingCallRejecters.set(requestId, quietRejecter(promise, reject));
    this.#reducerCallbacks.set(requestId, (result) => {
      this.#pendingCallRejecters.delete(requestId);
      if (result.tag === "Ok" || result.tag === "OkEmpty") {
        resolve();
      } else {
        if (result.tag === "Err") {
          const reader = new BinaryReader(result.value);
          const errorString = reader.readString();
          reject(new SenderError(errorString));
        } else if (result.tag === "InternalError") {
          reject(new InternalError(result.value));
        } else {
          reject(new Error("Unexpected reducer result"));
        }
      }
    });
    return promise;
  }
  /**
   * Call a reducer on your SpacetimeDB module with typed arguments.
   * @param reducerSchema The schema of the reducer to call
   * @param callReducerFlags The flags for the reducer call
   * @param params The arguments to pass to the reducer
   */
  callReducerWithParams(reducerName, _paramsType, params) {
    const writer = this.#reducerArgsEncoder;
    writer.clear();
    this.#reducerArgsSerializers[reducerName].serialize(writer, params);
    const argsBuffer = writer.getBuffer();
    return this.callReducer(reducerName, argsBuffer, params);
  }
  /**
   * Call a reducer on your SpacetimeDB module.
   *
   * @param procedureName The name of the reducer to call
   * @param argsBuffer The arguments to pass to the reducer
   */
  callProcedure(procedureName, argsBuffer) {
    const encodedProcedureName = this.#procedureNameBytes[procedureName];
    if (encodedProcedureName) {
      return this.#callProcedureWithEncodedName(
        procedureName,
        encodedProcedureName,
        argsBuffer
      );
    }
    return this.#callProcedureGeneric(procedureName, argsBuffer);
  }
  #callProcedureWithEncodedName(procedureName, encodedProcedureName, argsBuffer) {
    const rejected = this.#rejectCallIfDisconnected();
    if (rejected) {
      return quietRejection(rejected);
    }
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#getNextRequestId();
    this.#sendCallProcedureMessage(requestId, encodedProcedureName, argsBuffer);
    this.#pendingCallRejecters.set(requestId, quietRejecter(promise, reject));
    this.#procedureCallbacks.set(requestId, (result) => {
      this.#pendingCallRejecters.delete(requestId);
      if (result.tag === "Ok") {
        resolve(result.value);
      } else {
        reject(result.value);
      }
    });
    return promise;
  }
  #callProcedureGeneric(procedureName, argsBuffer) {
    const rejected = this.#rejectCallIfDisconnected();
    if (rejected) {
      return quietRejection(rejected);
    }
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#getNextRequestId();
    const message = ClientMessage.CallProcedure({
      procedure: procedureName,
      args: argsBuffer,
      requestId,
      // reserved for future use - 0 is the only valid value
      flags: 0
    });
    this.#sendMessage(message);
    this.#pendingCallRejecters.set(requestId, quietRejecter(promise, reject));
    this.#procedureCallbacks.set(requestId, (result) => {
      this.#pendingCallRejecters.delete(requestId);
      if (result.tag === "Ok") {
        resolve(result.value);
      } else {
        reject(result.value);
      }
    });
    return promise;
  }
  /**
   * Call a reducer on your SpacetimeDB module with typed arguments.
   * @param reducerSchema The schema of the reducer to call
   * @param callReducerFlags The flags for the reducer call
   * @param params The arguments to pass to the reducer
   */
  callProcedureWithParams(procedureName, _paramsType, params, _returnType) {
    const writer = new BinaryWriter(1024);
    const { serializeArgs, deserializeReturn } = this.#procedureSerializers[procedureName];
    serializeArgs(writer, params);
    const argsBuffer = writer.getBuffer();
    return thenQuietOnLoss(
      this.callProcedure(procedureName, argsBuffer),
      (returnBuf) => deserializeReturn(new BinaryReader(returnBuf))
    );
  }
  /**
   * Decode BSATN-encoded rows from a OneOffQuery result.
   * For temporal queries, each row has a prepended __system_time__ (Timestamp).
   */
  #decodeOneOffRows(tableName, rowList, temporal) {
    const rows = [];
    if (!this.#rowDeserializers[tableName]) {
      stdbLogger(
        "warn",
        `No deserializer for table '${tableName}' in OneOffQuery result. Skipping rows.`
      );
      return rows;
    }
    for (const { row, systemTime } of this.#readRows(
      tableName,
      rowList,
      temporal
    )) {
      if (temporal && systemTime) {
        rows.push({ __system_time__: systemTime, ...row });
      } else {
        rows.push(row);
      }
    }
    return rows;
  }
  /**
   * Execute a one-off SQL query via WebSocket.
   * Use this for non-temporal queries (no SAMPLE, EVERY, or ALL).
   *
   * @param queryString The SQL query to execute
   * @returns Promise resolving to query results with decoded rows
   */
  sql(queryString) {
    const rejected = this.#rejectCallIfDisconnected();
    if (rejected) {
      return quietRejection(rejected);
    }
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#getNextRequestId();
    this.#sendMessage(ClientMessage.OneOffQuery({ requestId, queryString }));
    this.#sqlQueryCallbacks.set(requestId, {
      promise,
      resolve,
      reject,
      temporal: false,
      startTime: performance.now()
    });
    return promise;
  }
  /**
   * Execute a temporal SQL query via WebSocket.
   * Use this for temporal queries (SAMPLE, EVERY, ALL) where rows have a
   * prepended __system_time__ column.
   *
   * @param queryString The temporal SQL query to execute
   * @returns Promise resolving to query results with __system_time__ on each row
   */
  sqlTemporal(queryString) {
    const rejected = this.#rejectCallIfDisconnected();
    if (rejected) {
      return quietRejection(rejected);
    }
    const { promise, resolve, reject } = createDeferred();
    const requestId = this.#getNextRequestId();
    this.#sendMessage(ClientMessage.OneOffQuery({ requestId, queryString }));
    this.#sqlQueryCallbacks.set(requestId, {
      promise,
      resolve,
      reject,
      temporal: true,
      startTime: performance.now()
    });
    return promise;
  }
  /**
   * Close the current connection.
   *
   * @example
   *
   * ```ts
   * const connection = DbConnection.builder().build();
   * connection.disconnect()
   * ```
   */
  disconnect() {
    this.isDisconnectRequested = true;
    if (this.#automaticReconnect) {
      if (this.#connectionEnded) {
        this.#emitter.emit("disconnect", this);
      } else {
        this.#endConnection(void 0);
      }
    } else {
      this.wsPromise.then((ws) => ws?.close());
    }
  }
  on(eventName, callback) {
    this.#emitter.on(eventName, callback);
  }
  off(eventName, callback) {
    this.#emitter.off(eventName, callback);
  }
  onConnect(callback) {
    this.#emitter.on("connect", callback);
  }
  onAutomaticReconnect(callback) {
    this.#emitter.on("automaticReconnect", callback);
  }
  onDisconnect(callback) {
    this.#emitter.on("disconnect", callback);
  }
  onConnectError(callback) {
    this.#emitter.on("connectError", callback);
  }
  removeOnConnect(callback) {
    this.#emitter.off("connect", callback);
  }
  removeOnAutomaticReconnect(callback) {
    this.#emitter.off("automaticReconnect", callback);
  }
  removeOnDisconnect(callback) {
    this.#emitter.off("disconnect", callback);
  }
  removeOnConnectError(callback) {
    this.#emitter.off("connectError", callback);
  }
};

// src/lib/schema.ts
function tablesToSchema(ctx, tables) {
  const tableDefs = /* @__PURE__ */ Object.create(null);
  for (const [accName, schema2] of Object.entries(tables)) {
    tableDefs[accName] = tableToSchema(
      accName,
      schema2,
      schema2.tableDef(ctx, accName)
    );
  }
  return {
    tables: tableDefs
  };
}
function tableToSchema(accName, schema2, tableDef) {
  const getColName = (i) => schema2.rowType.algebraicType.value.elements[i].name;
  const resolvedIndexes = tableDef.indexes.map(
    (idx) => {
      const accessorName = idx.accessorName;
      if (typeof accessorName !== "string" || accessorName.length === 0) {
        throw new TypeError(
          `Index '${idx.sourceName ?? "<unknown>"}' on table '${tableDef.sourceName}' is missing accessor name`
        );
      }
      const columnIds = idx.algorithm.tag === "Direct" ? [idx.algorithm.value] : idx.algorithm.value;
      const unique = tableDef.constraints.some(
        (c) => c.data.tag === "Unique" && c.data.value.columns.every((col) => columnIds.includes(col))
      );
      const algorithm = {
        BTree: "btree",
        Hash: "hash",
        Direct: "direct"
      }[idx.algorithm.tag];
      return {
        name: accessorName,
        unique,
        algorithm,
        columns: columnIds.map(getColName)
      };
    }
  );
  return {
    // For client,`schama.tableName` will always be there as canonical name.
    // For module, if explicit name is not provided via `name`, accessor name will
    // be used, it is stored as alias in database, hence works in query builder.
    sourceName: schema2.tableName || accName,
    accessorName: accName,
    isOutbox: schema2.outbox !== void 0,
    columns: schema2.rowType.row,
    // typed as T[i]['rowType']['row'] under TablesToSchema<T>
    rowType: schema2.rowSpacetimeType,
    // Keep declarative indexes in their original shape for type-level consumers.
    indexes: schema2.idxs,
    constraints: tableDef.constraints.map((c) => ({
      name: c.sourceName,
      constraint: "unique",
      columns: c.data.value.columns.map(getColName)
    })),
    // Expose resolved runtime indexes separately so runtime users don't have to
    // reinterpret `indexes` with unsafe casts.
    resolvedIndexes,
    tableDef,
    ...tableDef.isEvent ? { isEvent: true } : {}
  };
}
var ModuleContext = class {
  #compoundTypes = /* @__PURE__ */ new Map();
  /**
   * The global module definition that gets populated by calls to `reducer()` and lifecycle hooks.
   */
  #moduleDef = {
    typespace: { types: [] },
    tables: [],
    reducers: [],
    types: [],
    rowLevelSecurity: [],
    schedules: [],
    outboxes: [],
    procedures: [],
    views: [],
    viewPrimaryKeys: [],
    lifeCycleReducers: [],
    httpHandlers: [],
    httpRoutes: [],
    caseConversionPolicy: { tag: "SnakeCase" },
    explicitNames: {
      entries: []
    },
    submodules: [],
    environment: []
  };
  get moduleDef() {
    return this.#moduleDef;
  }
  rawModuleDefV10() {
    const sections = [];
    const push = (s) => {
      if (s) sections.push(s);
    };
    const module = this.#moduleDef;
    push(module.typespace && { tag: "Typespace", value: module.typespace });
    push(module.types && { tag: "Types", value: module.types });
    push(module.tables && { tag: "Tables", value: module.tables });
    push(module.reducers && { tag: "Reducers", value: module.reducers });
    push(module.procedures && { tag: "Procedures", value: module.procedures });
    push(module.views && { tag: "Views", value: module.views });
    push(
      module.viewPrimaryKeys && {
        tag: "ViewPrimaryKeys",
        value: module.viewPrimaryKeys
      }
    );
    push(module.schedules && { tag: "Schedules", value: module.schedules });
    push(module.outboxes && { tag: "Outboxes", value: module.outboxes });
    push(
      module.lifeCycleReducers && {
        tag: "LifeCycleReducers",
        value: module.lifeCycleReducers
      }
    );
    push(
      module.httpHandlers && {
        tag: "HttpHandlers",
        value: module.httpHandlers
      }
    );
    push(
      module.httpRoutes && {
        tag: "HttpRoutes",
        value: module.httpRoutes
      }
    );
    push(
      module.rowLevelSecurity && {
        tag: "RowLevelSecurity",
        value: module.rowLevelSecurity
      }
    );
    push(
      module.explicitNames && {
        tag: "ExplicitNames",
        value: module.explicitNames
      }
    );
    push(
      module.caseConversionPolicy && {
        tag: "CaseConversionPolicy",
        value: module.caseConversionPolicy
      }
    );
    push(
      module.submodules && {
        tag: "Submodules",
        value: module.submodules
      }
    );
    push({ tag: "Environment", value: module.environment });
    return { sections };
  }
  addSubmodule(submodule) {
    this.#moduleDef.submodules.push(submodule);
  }
  /**
   * Set the case conversion policy for this module.
   * Called by the settings mechanism.
   */
  setCaseConversionPolicy(policy) {
    this.#moduleDef.caseConversionPolicy = policy;
  }
  get typespace() {
    return this.#moduleDef.typespace;
  }
  /**
   * Resolves the actual type of a TypeBuilder by following its references until it reaches a non-ref type.
   * @param typespace The typespace to resolve types against.
   * @param typeBuilder The TypeBuilder to resolve.
   * @returns The resolved algebraic type.
   */
  resolveType(typeBuilder) {
    let ty = typeBuilder.algebraicType;
    while (ty.tag === "Ref") {
      ty = this.typespace.types[ty.value];
    }
    return ty;
  }
  /**
   * Adds a type to the module definition's typespace as a `Ref` if it is a named compound type (Product or Sum).
   * Otherwise, returns the type as is.
   * @param name
   * @param ty
   * @returns
   */
  registerTypesRecursively(typeBuilder) {
    if (typeBuilder instanceof ProductBuilder && !isUnit(typeBuilder) || typeBuilder instanceof SumBuilder || typeBuilder instanceof RowBuilder) {
      return this.#registerCompoundTypeRecursively(typeBuilder);
    } else if (typeBuilder instanceof OptionBuilder) {
      return new OptionBuilder(
        this.registerTypesRecursively(typeBuilder.value)
      );
    } else if (typeBuilder instanceof ResultBuilder) {
      return new ResultBuilder(
        this.registerTypesRecursively(typeBuilder.ok),
        this.registerTypesRecursively(typeBuilder.err)
      );
    } else if (typeBuilder instanceof ArrayBuilder) {
      return new ArrayBuilder(
        this.registerTypesRecursively(typeBuilder.element)
      );
    } else {
      return typeBuilder;
    }
  }
  #registerCompoundTypeRecursively(typeBuilder) {
    const ty = typeBuilder.algebraicType;
    const name = typeBuilder.typeName;
    if (name === void 0) {
      throw new Error(
        `Missing type name for ${typeBuilder.constructor.name ?? "TypeBuilder"} ${JSON.stringify(typeBuilder)}`
      );
    }
    let r = this.#compoundTypes.get(ty);
    if (r != null) {
      return r;
    }
    const newTy = typeBuilder instanceof RowBuilder || typeBuilder instanceof ProductBuilder ? {
      tag: "Product",
      value: { elements: [] }
    } : {
      tag: "Sum",
      value: { variants: [] }
    };
    r = new RefBuilder(this.#moduleDef.typespace.types.length);
    this.#moduleDef.typespace.types.push(newTy);
    this.#compoundTypes.set(ty, r);
    if (typeBuilder instanceof RowBuilder) {
      for (const [name2, elem] of Object.entries(typeBuilder.row)) {
        newTy.value.elements.push({
          name: name2,
          algebraicType: this.registerTypesRecursively(elem.typeBuilder).algebraicType
        });
      }
    } else if (typeBuilder instanceof ProductBuilder) {
      for (const [name2, elem] of Object.entries(typeBuilder.elements)) {
        newTy.value.elements.push({
          name: name2,
          algebraicType: this.registerTypesRecursively(elem).algebraicType
        });
      }
    } else if (typeBuilder instanceof SumBuilder) {
      for (const [name2, variant] of Object.entries(typeBuilder.variants)) {
        newTy.value.variants.push({
          name: name2,
          algebraicType: this.registerTypesRecursively(variant).algebraicType
        });
      }
    }
    this.#moduleDef.types.push({
      sourceName: splitName(name),
      ty: r.ref,
      customOrdering: true
    });
    return r;
  }
};
function isUnit(typeBuilder) {
  return typeBuilder.typeName == null && typeBuilder.algebraicType.value.elements.length === 0;
}
function splitName(name) {
  const scope = name.split(".");
  return { sourceName: scope.pop(), scope };
}

// src/sdk/schema.ts
var Tables = class {
  constructor(schemaType) {
    this.schemaType = schemaType;
  }
  schemaType;
};
function schema(tables) {
  const ctx = new ModuleContext();
  return new Tables(tablesToSchema(ctx, tables));
}
function convertToAccessorMap(arr) {
  return Object.fromEntries(
    arr.map((v) => [v.accessorName, v])
  );
}

// src/lib/remote_module.ts
var receiverModules = /* @__PURE__ */ new WeakMap();
var identityModules = /* @__PURE__ */ new WeakMap();
function makeOutbox(receiver, row, primaryKey, scheduledOrEvent) {
  const columns = Object.entries(row.row);
  if (scheduledOrEvent)
    throw new TypeError("Outbox tables cannot be scheduled or event tables");
  const pk = primaryKey.length === 1 ? columns[primaryKey[0]][1] : void 0;
  if (!pk?.columnMetadata.isAutoIncrement || pk.typeBuilder.algebraicType.tag !== "U64") {
    throw new TypeError(
      "Outbox tables require a u64 primary key with autoInc()"
    );
  }
  const targets = columns.flatMap(
    ([, col], i) => col.columnMetadata.isTarget ? [i] : []
  );
  if (targets.length !== 1 || !deepEqual(
    columns[targets[0]][1].typeBuilder.algebraicType,
    t.identity().algebraicType
  )) {
    throw new TypeError(
      "Outbox tables require exactly one identity().target() column"
    );
  }
  const argColumns = Object.entries(receiver.params).map(([name, type]) => {
    const matches = columns.flatMap(
      ([key, col], i) => (col.columnMetadata.paramName ?? key) === name ? [i] : []
    );
    if (matches.length !== 1)
      throw new TypeError(
        `Outbox requires exactly one column for receiver parameter '${name}'`
      );
    const index = matches[0];
    if (!deepEqual(
      columns[index][1].typeBuilder.algebraicType,
      type.algebraicType
    )) {
      throw new TypeError(`Outbox column for '${name}' has the wrong type`);
    }
    return index;
  });
  const module = receiverModules.get(receiver);
  const target = columns[targets[0]][1].typeBuilder;
  if (module === void 0 || identityModules.get(target) !== module) {
    throw new TypeError(
      "Outbox target must use the selected receiver module identity() builder"
    );
  }
  return {
    targetColumn: targets[0],
    argColumns,
    remoteReducer: receiver.name,
    signatureHash: receiver.signatureHash
  };
}

// src/lib/autogen/types.ts
var AlgebraicType2 = t.enum("AlgebraicType", {
  Ref: t.u32(),
  get Sum() {
    return SumType2;
  },
  get Product() {
    return ProductType2;
  },
  get Array() {
    return AlgebraicType2;
  },
  String: t.unit(),
  Bool: t.unit(),
  I8: t.unit(),
  U8: t.unit(),
  I16: t.unit(),
  U16: t.unit(),
  I32: t.unit(),
  U32: t.unit(),
  I64: t.unit(),
  U64: t.unit(),
  I128: t.unit(),
  U128: t.unit(),
  I256: t.unit(),
  U256: t.unit(),
  F32: t.unit(),
  F64: t.unit()
});
var CaseConversionPolicy = t.enum("CaseConversionPolicy", {
  None: t.unit(),
  SnakeCase: t.unit()
});
var ExplicitNameEntry = t.enum("ExplicitNameEntry", {
  get Table() {
    return NameMapping;
  },
  get Function() {
    return NameMapping;
  },
  get Index() {
    return NameMapping;
  },
  get Namespace() {
    return NameMapping;
  }
});
var ExplicitNames = t.object("ExplicitNames", {
  get entries() {
    return t.array(ExplicitNameEntry);
  }
});
var FunctionVisibility = t.enum("FunctionVisibility", {
  Private: t.unit(),
  ClientCallable: t.unit()
});
var HttpHeaderPair = t.object("HttpHeaderPair", {
  name: t.string(),
  value: t.byteArray()
});
var HttpHeaders = t.object("HttpHeaders", {
  get entries() {
    return t.array(HttpHeaderPair);
  }
});
var HttpMethod = t.enum("HttpMethod", {
  Get: t.unit(),
  Head: t.unit(),
  Post: t.unit(),
  Put: t.unit(),
  Delete: t.unit(),
  Connect: t.unit(),
  Options: t.unit(),
  Trace: t.unit(),
  Patch: t.unit(),
  Extension: t.string()
});
t.object("HttpRequest", {
  get method() {
    return HttpMethod;
  },
  get headers() {
    return HttpHeaders;
  },
  timeout: t.option(t.timeDuration()),
  uri: t.string(),
  get version() {
    return HttpVersion;
  }
});
t.object("HttpResponse", {
  get headers() {
    return HttpHeaders;
  },
  get version() {
    return HttpVersion;
  },
  code: t.u16()
});
var HttpVersion = t.enum("HttpVersion", {
  Http09: t.unit(),
  Http10: t.unit(),
  Http11: t.unit(),
  Http2: t.unit(),
  Http3: t.unit()
});
var IndexType = t.enum("IndexType", {
  BTree: t.unit(),
  Hash: t.unit()
});
var Lifecycle = t.enum("Lifecycle", {
  Init: t.unit(),
  OnConnect: t.unit(),
  OnDisconnect: t.unit()
});
var MethodOrAny = t.enum("MethodOrAny", {
  Any: t.unit(),
  get Method() {
    return HttpMethod;
  }
});
var MiscModuleExport = t.enum("MiscModuleExport", {
  get TypeAlias() {
    return TypeAlias;
  }
});
var NameMapping = t.object("NameMapping", {
  sourceName: t.string(),
  canonicalName: t.string()
});
var ProductType2 = t.object("ProductType", {
  get elements() {
    return t.array(ProductTypeElement);
  }
});
var ProductTypeElement = t.object("ProductTypeElement", {
  name: t.option(t.string()),
  get algebraicType() {
    return AlgebraicType2;
  }
});
var RawColumnDefV8 = t.object("RawColumnDefV8", {
  colName: t.string(),
  get colType() {
    return AlgebraicType2;
  }
});
var RawColumnDefaultValueV10 = t.object("RawColumnDefaultValueV10", {
  colId: t.u16(),
  value: t.byteArray()
});
var RawColumnDefaultValueV9 = t.object("RawColumnDefaultValueV9", {
  table: t.string(),
  colId: t.u16(),
  value: t.byteArray()
});
var RawConstraintDataV9 = t.enum("RawConstraintDataV9", {
  get Unique() {
    return RawUniqueConstraintDataV9;
  }
});
var RawConstraintDefV10 = t.object("RawConstraintDefV10", {
  sourceName: t.option(t.string()),
  get data() {
    return RawConstraintDataV9;
  }
});
var RawConstraintDefV8 = t.object("RawConstraintDefV8", {
  constraintName: t.string(),
  constraints: t.u8(),
  columns: t.array(t.u16())
});
var RawConstraintDefV9 = t.object("RawConstraintDefV9", {
  name: t.option(t.string()),
  get data() {
    return RawConstraintDataV9;
  }
});
var RawEnvVarTypeV10 = t.enum("RawEnvVarTypeV10", {
  String: t.unit(),
  StringLiteral: t.string(),
  Union: t.array(t.string())
});
var RawEnvironmentDeclarationV10 = t.object(
  "RawEnvironmentDeclarationV10",
  {
    name: t.string(),
    get ty() {
      return RawEnvVarTypeV10;
    },
    optional: t.bool()
  }
);
var RawHttpHandlerDefV10 = t.object("RawHttpHandlerDefV10", {
  sourceName: t.string()
});
var RawHttpRouteDefV10 = t.object("RawHttpRouteDefV10", {
  handlerFunction: t.string(),
  get method() {
    return MethodOrAny;
  },
  path: t.string()
});
var RawIndexAlgorithm = t.enum("RawIndexAlgorithm", {
  BTree: t.array(t.u16()),
  Hash: t.array(t.u16()),
  Direct: t.u16()
});
var RawIndexDefV10 = t.object("RawIndexDefV10", {
  sourceName: t.option(t.string()),
  accessorName: t.option(t.string()),
  get algorithm() {
    return RawIndexAlgorithm;
  }
});
var RawIndexDefV8 = t.object("RawIndexDefV8", {
  indexName: t.string(),
  isUnique: t.bool(),
  get indexType() {
    return IndexType;
  },
  columns: t.array(t.u16())
});
var RawIndexDefV9 = t.object("RawIndexDefV9", {
  name: t.option(t.string()),
  accessorName: t.option(t.string()),
  get algorithm() {
    return RawIndexAlgorithm;
  }
});
var RawLifeCycleReducerDefV10 = t.object(
  "RawLifeCycleReducerDefV10",
  {
    get lifecycleSpec() {
      return Lifecycle;
    },
    functionName: t.string()
  }
);
var RawMiscModuleExportV9 = t.enum("RawMiscModuleExportV9", {
  get ColumnDefaultValue() {
    return RawColumnDefaultValueV9;
  },
  get Procedure() {
    return RawProcedureDefV9;
  },
  get View() {
    return RawViewDefV9;
  }
});
t.enum("RawModuleDef", {
  get V8BackCompat() {
    return RawModuleDefV8;
  },
  get V9() {
    return RawModuleDefV9;
  },
  get V10() {
    return RawModuleDefV10;
  }
});
var RawModuleDefV10 = t.object("RawModuleDefV10", {
  get sections() {
    return t.array(RawModuleDefV10Section);
  }
});
var RawModuleDefV10Section = t.enum("RawModuleDefV10Section", {
  get Typespace() {
    return Typespace;
  },
  get Types() {
    return t.array(RawTypeDefV10);
  },
  get Tables() {
    return t.array(RawTableDefV10);
  },
  get Reducers() {
    return t.array(RawReducerDefV10);
  },
  get Procedures() {
    return t.array(RawProcedureDefV10);
  },
  get Views() {
    return t.array(RawViewDefV10);
  },
  get Schedules() {
    return t.array(RawScheduleDefV10);
  },
  get LifeCycleReducers() {
    return t.array(RawLifeCycleReducerDefV10);
  },
  get RowLevelSecurity() {
    return t.array(RawRowLevelSecurityDefV9);
  },
  get CaseConversionPolicy() {
    return CaseConversionPolicy;
  },
  get ExplicitNames() {
    return ExplicitNames;
  },
  get HttpHandlers() {
    return t.array(RawHttpHandlerDefV10);
  },
  get HttpRoutes() {
    return t.array(RawHttpRouteDefV10);
  },
  get ViewPrimaryKeys() {
    return t.array(RawViewPrimaryKeyDefV10);
  },
  get Submodules() {
    return t.array(RawSubmoduleV10);
  },
  get Environment() {
    return t.array(RawEnvironmentDeclarationV10);
  },
  get Outboxes() {
    return t.array(RawOutboxDefV10);
  }
});
var RawModuleDefV8 = t.object("RawModuleDefV8", {
  get typespace() {
    return Typespace;
  },
  get tables() {
    return t.array(TableDesc);
  },
  get reducers() {
    return t.array(ReducerDef);
  },
  get miscExports() {
    return t.array(MiscModuleExport);
  }
});
var RawModuleDefV9 = t.object("RawModuleDefV9", {
  get typespace() {
    return Typespace;
  },
  get tables() {
    return t.array(RawTableDefV9);
  },
  get reducers() {
    return t.array(RawReducerDefV9);
  },
  get types() {
    return t.array(RawTypeDefV9);
  },
  get miscExports() {
    return t.array(RawMiscModuleExportV9);
  },
  get rowLevelSecurity() {
    return t.array(RawRowLevelSecurityDefV9);
  }
});
var RawOutboxDefV10 = t.object("RawOutboxDefV10", {
  tableName: t.string(),
  remoteReducer: t.string(),
  targetColumn: t.u16(),
  argColumns: t.array(t.u16()),
  onResultReducer: t.option(t.string()),
  signatureHash: t.u256()
});
var RawProcedureDefV10 = t.object("RawProcedureDefV10", {
  sourceName: t.string(),
  get params() {
    return ProductType2;
  },
  get returnType() {
    return AlgebraicType2;
  },
  get visibility() {
    return FunctionVisibility;
  }
});
var RawProcedureDefV9 = t.object("RawProcedureDefV9", {
  name: t.string(),
  get params() {
    return ProductType2;
  },
  get returnType() {
    return AlgebraicType2;
  }
});
var RawReducerDefV10 = t.object("RawReducerDefV10", {
  sourceName: t.string(),
  get params() {
    return ProductType2;
  },
  get visibility() {
    return FunctionVisibility;
  },
  get okReturnType() {
    return AlgebraicType2;
  },
  get errReturnType() {
    return AlgebraicType2;
  }
});
var RawReducerDefV9 = t.object("RawReducerDefV9", {
  name: t.string(),
  get params() {
    return ProductType2;
  },
  get lifecycle() {
    return t.option(Lifecycle);
  }
});
var RawRowLevelSecurityDefV9 = t.object("RawRowLevelSecurityDefV9", {
  sql: t.string()
});
var RawScheduleDefV10 = t.object("RawScheduleDefV10", {
  sourceName: t.option(t.string()),
  tableName: t.string(),
  scheduleAtCol: t.u16(),
  functionName: t.string()
});
var RawScheduleDefV9 = t.object("RawScheduleDefV9", {
  name: t.option(t.string()),
  reducerName: t.string(),
  scheduledAtColumn: t.u16()
});
var RawScopedTypeNameV10 = t.object("RawScopedTypeNameV10", {
  scope: t.array(t.string()),
  sourceName: t.string()
});
var RawScopedTypeNameV9 = t.object("RawScopedTypeNameV9", {
  scope: t.array(t.string()),
  name: t.string()
});
var RawSequenceDefV10 = t.object("RawSequenceDefV10", {
  sourceName: t.option(t.string()),
  column: t.u16(),
  start: t.option(t.i128()),
  minValue: t.option(t.i128()),
  maxValue: t.option(t.i128()),
  increment: t.i128()
});
var RawSequenceDefV8 = t.object("RawSequenceDefV8", {
  sequenceName: t.string(),
  colPos: t.u16(),
  increment: t.i128(),
  start: t.option(t.i128()),
  minValue: t.option(t.i128()),
  maxValue: t.option(t.i128()),
  allocated: t.i128()
});
var RawSequenceDefV9 = t.object("RawSequenceDefV9", {
  name: t.option(t.string()),
  column: t.u16(),
  start: t.option(t.i128()),
  minValue: t.option(t.i128()),
  maxValue: t.option(t.i128()),
  increment: t.i128()
});
var RawSubmoduleV10 = t.object("RawSubmoduleV10", {
  namespace: t.string(),
  get module() {
    return RawModuleDefV10;
  }
});
var RawTableDefV10 = t.object("RawTableDefV10", {
  sourceName: t.string(),
  productTypeRef: t.u32(),
  primaryKey: t.array(t.u16()),
  get indexes() {
    return t.array(RawIndexDefV10);
  },
  get constraints() {
    return t.array(RawConstraintDefV10);
  },
  get sequences() {
    return t.array(RawSequenceDefV10);
  },
  get tableType() {
    return TableType;
  },
  get tableAccess() {
    return TableAccess;
  },
  get defaultValues() {
    return t.array(RawColumnDefaultValueV10);
  },
  isEvent: t.bool()
});
var RawTableDefV8 = t.object("RawTableDefV8", {
  tableName: t.string(),
  get columns() {
    return t.array(RawColumnDefV8);
  },
  get indexes() {
    return t.array(RawIndexDefV8);
  },
  get constraints() {
    return t.array(RawConstraintDefV8);
  },
  get sequences() {
    return t.array(RawSequenceDefV8);
  },
  tableType: t.string(),
  tableAccess: t.string(),
  scheduled: t.option(t.string())
});
var RawTableDefV9 = t.object("RawTableDefV9", {
  name: t.string(),
  productTypeRef: t.u32(),
  primaryKey: t.array(t.u16()),
  get indexes() {
    return t.array(RawIndexDefV9);
  },
  get constraints() {
    return t.array(RawConstraintDefV9);
  },
  get sequences() {
    return t.array(RawSequenceDefV9);
  },
  get schedule() {
    return t.option(RawScheduleDefV9);
  },
  get tableType() {
    return TableType;
  },
  get tableAccess() {
    return TableAccess;
  }
});
var RawTypeDefV10 = t.object("RawTypeDefV10", {
  get sourceName() {
    return RawScopedTypeNameV10;
  },
  ty: t.u32(),
  customOrdering: t.bool()
});
var RawTypeDefV9 = t.object("RawTypeDefV9", {
  get name() {
    return RawScopedTypeNameV9;
  },
  ty: t.u32(),
  customOrdering: t.bool()
});
var RawUniqueConstraintDataV9 = t.object(
  "RawUniqueConstraintDataV9",
  {
    columns: t.array(t.u16())
  }
);
var RawViewDefV10 = t.object("RawViewDefV10", {
  sourceName: t.string(),
  index: t.u32(),
  isPublic: t.bool(),
  isAnonymous: t.bool(),
  get params() {
    return ProductType2;
  },
  get returnType() {
    return AlgebraicType2;
  }
});
var RawViewDefV9 = t.object("RawViewDefV9", {
  name: t.string(),
  index: t.u32(),
  isPublic: t.bool(),
  isAnonymous: t.bool(),
  get params() {
    return ProductType2;
  },
  get returnType() {
    return AlgebraicType2;
  }
});
var RawViewPrimaryKeyDefV10 = t.object("RawViewPrimaryKeyDefV10", {
  viewSourceName: t.string(),
  columns: t.array(t.string())
});
var ReducerDef = t.object("ReducerDef", {
  name: t.string(),
  get args() {
    return t.array(ProductTypeElement);
  }
});
var SumType2 = t.object("SumType", {
  get variants() {
    return t.array(SumTypeVariant);
  }
});
var SumTypeVariant = t.object("SumTypeVariant", {
  name: t.option(t.string()),
  get algebraicType() {
    return AlgebraicType2;
  }
});
var TableAccess = t.enum("TableAccess", {
  Public: t.unit(),
  Private: t.unit()
});
var TableDesc = t.object("TableDesc", {
  get schema() {
    return RawTableDefV8;
  },
  data: t.u32()
});
var TableType = t.enum("TableType", {
  System: t.unit(),
  User: t.unit()
});
var TypeAlias = t.object("TypeAlias", {
  name: t.string(),
  ty: t.u32()
});
var Typespace = t.object("Typespace", {
  get types() {
    return t.array(AlgebraicType2);
  }
});
t.enum("ViewResultHeader", {
  RowData: t.unit(),
  RawSql: t.string()
});

// src/lib/table.ts
function table(opts, row, ..._) {
  const {
    name,
    public: isPublic = false,
    indexes: userIndexes = [],
    scheduled,
    event: isEvent = false
  } = opts;
  const colIds = /* @__PURE__ */ new Map();
  const colNameList = [];
  if (!(row instanceof RowBuilder)) {
    row = new RowBuilder(row);
  }
  row.algebraicType.value.elements.forEach((elem, i) => {
    colIds.set(elem.name, i);
    colNameList.push(elem.name);
  });
  const pk = [];
  const indexes = [];
  const constraints = [];
  const sequences = [];
  let scheduleAtCol;
  const defaultValues = [];
  for (const [name2, builder] of Object.entries(row.row)) {
    const meta = builder.columnMetadata;
    if (meta.isPrimaryKey) {
      pk.push(colIds.get(name2));
    }
    const isUnique = meta.isUnique || meta.isPrimaryKey;
    if (meta.indexType || isUnique) {
      const algo = meta.indexType ?? "btree";
      const id = colIds.get(name2);
      let algorithm;
      switch (algo) {
        case "btree":
          algorithm = RawIndexAlgorithm.BTree([id]);
          break;
        case "hash":
          algorithm = RawIndexAlgorithm.Hash([id]);
          break;
        case "direct":
          algorithm = RawIndexAlgorithm.Direct(id);
          break;
      }
      indexes.push({
        sourceName: void 0,
        // Unnamed indexes will be assigned a globally unique name
        accessorName: name2,
        algorithm
      });
    }
    if (isUnique) {
      constraints.push({
        sourceName: void 0,
        data: { tag: "Unique", value: { columns: [colIds.get(name2)] } }
      });
    }
    if (meta.isAutoIncrement) {
      sequences.push({
        sourceName: void 0,
        start: void 0,
        minValue: void 0,
        maxValue: void 0,
        column: colIds.get(name2),
        increment: 1n
      });
    }
    if (Object.prototype.hasOwnProperty.call(meta, "defaultValue")) {
      const writer = new BinaryWriter(16);
      builder.serialize(writer, meta.defaultValue);
      defaultValues.push({
        colId: colIds.get(name2),
        value: writer.getBuffer()
      });
    }
    const algebraicType = builder.typeBuilder.algebraicType;
    if (schedule_at_default.isScheduleAt(algebraicType)) {
      scheduleAtCol = colIds.get(name2);
    }
  }
  for (const indexOpts of userIndexes ?? []) {
    const accessor = indexOpts.accessor;
    if (typeof accessor !== "string" || accessor.length === 0) {
      const tableLabel = name ?? "<unnamed>";
      const indexLabel = indexOpts.name ?? "<unnamed>";
      throw new TypeError(
        `Index '${indexLabel}' on table '${tableLabel}' must define a non-empty 'accessor'`
      );
    }
    let algorithm;
    switch (indexOpts.algorithm) {
      case "btree":
        algorithm = {
          tag: "BTree",
          value: indexOpts.columns.map((c) => colIds.get(c))
        };
        break;
      case "hash":
        algorithm = {
          tag: "Hash",
          value: indexOpts.columns.map((c) => colIds.get(c))
        };
        break;
      case "direct":
        algorithm = { tag: "Direct", value: colIds.get(indexOpts.column) };
        break;
    }
    indexes.push({
      sourceName: void 0,
      accessorName: accessor,
      algorithm,
      canonicalName: indexOpts.name
    });
  }
  for (const constraintOpts of opts.constraints ?? []) {
    if (constraintOpts.constraint === "unique") {
      const data = {
        tag: "Unique",
        value: { columns: constraintOpts.columns.map((c) => colIds.get(c)) }
      };
      constraints.push({ sourceName: constraintOpts.name, data });
      continue;
    }
  }
  const productType = row.algebraicType.value;
  const schedule = scheduled && scheduleAtCol !== void 0 ? { scheduleAtCol, reducer: scheduled } : void 0;
  const receiver = opts.outbox?.receiver;
  const outbox = receiver ? makeOutbox(receiver, row, pk, !!scheduled || isEvent) : void 0;
  return {
    outbox,
    rowType: row,
    tableName: name,
    rowSpacetimeType: productType,
    tableDef: (ctx, accName) => {
      const tableName = name ?? accName;
      if (row.typeName === void 0) {
        row.typeName = toPascalCase(tableName);
      }
      for (const index of indexes) {
        const cols = index.algorithm.tag === "Direct" ? [index.algorithm.value] : index.algorithm.value;
        const colS = cols.map((i) => colNameList[i]).join("_");
        const sourceName = index.sourceName = `${accName}_${colS}_idx_${index.algorithm.tag.toLowerCase()}`;
        const { canonicalName } = index;
        if (canonicalName !== void 0) {
          ctx.moduleDef.explicitNames.entries.push(
            ExplicitNameEntry.Index({ sourceName, canonicalName })
          );
        }
      }
      return {
        sourceName: accName,
        productTypeRef: ctx.registerTypesRecursively(row).ref,
        primaryKey: pk,
        indexes,
        constraints,
        sequences,
        tableType: { tag: "User" },
        tableAccess: { tag: isPublic ? "Public" : "Private" },
        defaultValues,
        isEvent
      };
    },
    // Preserve the declared index options as runtime data so `tableToSchema`
    // can expose them without type-smuggling.
    idxs: userIndexes,
    constraints,
    scheduleAtCol,
    schedule
  };
}

// src/sdk/reducers.ts
var Reducers = class {
  reducersType;
  constructor(handles) {
    this.reducersType = reducersToSchema(handles);
  }
};
function reducersToSchema(reducers2) {
  const mapped = reducers2.map((r) => {
    const paramsRow = r.params.row;
    return {
      name: r.reducerName,
      // Prefer the schema's own accessorName if present at runtime; otherwise derive it.
      accessorName: r.accessorName,
      params: paramsRow,
      paramsType: r.paramsSpacetimeType
    };
  });
  const result = { reducers: mapped };
  return result;
}
function reducers(...args) {
  const handles = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
  return new Reducers(handles);
}
function reducerSchema(name, params, accessorName) {
  const paramType = {
    elements: Object.entries(params).map(([n, c]) => ({
      name: n,
      algebraicType: "typeBuilder" in c ? c.typeBuilder.algebraicType : c.algebraicType
    }))
  };
  return {
    reducerName: name,
    accessorName: accessorName ?? toCamelCase(name),
    params: new RowBuilder(params),
    paramsSpacetimeType: paramType,
    reducerDef: {
      name,
      params: paramType,
      lifecycle: void 0
    }
  };
}

// src/sdk/procedures.ts
function procedures(...args) {
  const procedures2 = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
  return { procedures: procedures2 };
}
function procedureSchema(name, params, returnType, accessorName) {
  return {
    name,
    accessorName: accessorName ?? toCamelCase(name),
    params: coerceParams(params),
    returnType
  };
}

// src/sdk/connection_manager.ts
var CONNECTION_MANAGER_RECONNECT_BASE_DELAY_MS = 1e3;
var CONNECTION_MANAGER_RECONNECT_MAX_DELAY_MS = 3e4;
function connectionManagerReconnectDelayMs(attempt) {
  return Math.min(
    CONNECTION_MANAGER_RECONNECT_BASE_DELAY_MS * 2 ** attempt,
    CONNECTION_MANAGER_RECONNECT_MAX_DELAY_MS
  );
}
function defaultState() {
  return {
    isActive: false,
    identity: void 0,
    token: void 0,
    connectionId: ConnectionId.random(),
    connectionError: void 0
  };
}
var ConnectionManagerImpl = class _ConnectionManagerImpl {
  #connections = /* @__PURE__ */ new Map();
  /** Generates a unique key for a connection based on URI and module name. */
  static getKey(uri, moduleName) {
    return `${uri}::${moduleName}`;
  }
  /** Instance method wrapper for getKey. */
  getKey(uri, moduleName) {
    return _ConnectionManagerImpl.getKey(uri, moduleName);
  }
  #ensureEntry(key) {
    const existing = this.#connections.get(key);
    if (existing) {
      return existing;
    }
    const managed = {
      connection: void 0,
      builder: void 0,
      refCount: 0,
      state: defaultState(),
      listeners: /* @__PURE__ */ new Set(),
      pendingRelease: null,
      reconnectTimer: null,
      reconnectAttempt: 0
    };
    this.#connections.set(key, managed);
    return managed;
  }
  #notify(managed) {
    for (const listener of managed.listeners) {
      listener();
    }
  }
  #updateState(managed, updates) {
    managed.state = { ...managed.state, ...updates };
    this.#notify(managed);
  }
  #ensureCallbacks(managed) {
    if (managed.onConnect) {
      return;
    }
    managed.onConnect = (conn) => {
      if (conn !== managed.connection) {
        return;
      }
      managed.reconnectAttempt = 0;
      this.#updateState(managed, {
        isActive: conn.isActive,
        identity: conn.identity,
        token: conn.token,
        connectionId: conn.connectionId,
        connectionError: void 0
      });
    };
    managed.onDisconnect = (ctx, error, nextReconnectAttempt) => {
      if (ctx !== managed.connection) {
        return;
      }
      this.#updateState(managed, {
        isActive: false,
        connectionError: error ?? void 0
      });
      if (nextReconnectAttempt === void 0) {
        this.#scheduleRebuild(managed);
      }
    };
    managed.onConnectError = (ctx, error, nextReconnectAttempt) => {
      if (ctx !== managed.connection) {
        return;
      }
      this.#updateState(managed, {
        isActive: false,
        connectionError: error
      });
      if (nextReconnectAttempt === void 0) {
        this.#scheduleRebuild(managed);
      }
    };
  }
  #attachCallbacks(managed, builder) {
    this.#ensureCallbacks(managed);
    builder.onConnect(managed.onConnect);
    builder.onAutomaticReconnect(managed.onConnect);
    builder.onDisconnect(managed.onDisconnect);
    builder.onConnectError(managed.onConnectError);
  }
  #detachCallbacks(managed, connection) {
    if (managed.onConnect) {
      connection.removeOnConnect(managed.onConnect);
      connection.removeOnAutomaticReconnect(managed.onConnect);
    }
    if (managed.onDisconnect) {
      connection.removeOnDisconnect(managed.onDisconnect);
    }
    if (managed.onConnectError) {
      connection.removeOnConnectError(managed.onConnectError);
    }
  }
  /** Reuse the latest session token when rebuilding from the retained builder. */
  #buildManagedConnection(managed, builder, { resumeSession = true } = {}) {
    managed.builder = builder;
    builder.withAutomaticReconnect();
    if (resumeSession && managed.state.token) {
      builder.withToken(managed.state.token);
    }
    const connection = builder.build();
    managed.connection = connection;
    this.#attachCallbacks(managed, builder);
    this.#updateState(managed, {
      isActive: connection.isActive,
      identity: connection.identity,
      token: connection.token,
      connectionId: connection.connectionId,
      connectionError: void 0
    });
    return connection;
  }
  /** Preserve framework retries for failures the core connection will not retry. */
  #scheduleRebuild(managed) {
    if (managed.refCount <= 0 || managed.pendingRelease || managed.reconnectTimer || !managed.builder) {
      return;
    }
    const connection = managed.connection;
    if (connection) {
      this.#detachCallbacks(managed, connection);
    }
    managed.connection = void 0;
    if (connection?.isDisconnectRequested) {
      return;
    }
    const delay = connectionManagerReconnectDelayMs(managed.reconnectAttempt);
    managed.reconnectAttempt += 1;
    managed.reconnectTimer = setTimeout(() => {
      managed.reconnectTimer = null;
      if (managed.refCount <= 0 || managed.pendingRelease || managed.connection || !managed.builder) {
        return;
      }
      this.#buildManagedConnection(managed, managed.builder);
    }, delay);
  }
  /**
   * Retains a connection, incrementing its reference count.
   * Creates the connection on first call; returns existing connection on subsequent calls.
   * Cancels any pending release if the connection was about to be cleaned up.
   *
   * @param key - Unique identifier for the connection (use getKey to generate)
   * @param builder - Connection builder to create the connection if needed
   * @returns The managed connection instance
   */
  retain(key, builder) {
    const managed = this.#ensureEntry(key);
    if (managed.pendingRelease) {
      clearTimeout(managed.pendingRelease);
      managed.pendingRelease = null;
    }
    if (managed.reconnectTimer) {
      clearTimeout(managed.reconnectTimer);
      managed.reconnectTimer = null;
    }
    managed.refCount += 1;
    managed.builder = builder;
    if (managed.connection) {
      return managed.connection;
    }
    return this.#buildManagedConnection(managed, builder);
  }
  /**
   * Tears down the current connection and builds a fresh one from `builder`,
   * preserving the entry's ref count and listener set.
   *
   * `retain()` deliberately ignores the builder once a connection is live —
   * the right behaviour for ref-counting, but it blocks "reconnect with a
   * fresh token" flows (e.g. swapping an anonymous session for a signed-in one
   * after an auth change). `rebuild()` is the supported escape hatch: pass a
   * builder carrying the new token and the pool swaps the live connection
   * under the same subscribers, so framework hooks (`useTable`, `useReducer`,
   * …) re-bind to the new connection automatically.
   *
   * The old connection's callbacks are detached before it is closed, so its
   * disconnect event never leaks into pool state, and any pending rebuild is
   * cancelled (the caller is driving the replacement explicitly). Returns the
   * newly-built connection, or `null` if the key has no retained entry.
   *
   * @param key - Unique identifier for the connection (use getKey to generate)
   * @param builder - Fresh connection builder; its handlers are rewired into the pool
   */
  rebuild(key, builder) {
    const managed = this.#connections.get(key);
    if (!managed || managed.refCount <= 0) {
      return null;
    }
    if (managed.pendingRelease) {
      clearTimeout(managed.pendingRelease);
      managed.pendingRelease = null;
    }
    if (managed.reconnectTimer) {
      clearTimeout(managed.reconnectTimer);
      managed.reconnectTimer = null;
    }
    managed.reconnectAttempt = 0;
    const connection = managed.connection;
    if (connection) {
      this.#detachCallbacks(managed, connection);
      connection.disconnect();
    }
    managed.connection = void 0;
    try {
      return this.#buildManagedConnection(managed, builder, {
        resumeSession: false
      });
    } catch (error) {
      this.#updateState(managed, {
        isActive: false,
        connectionError: error instanceof Error ? error : new Error(String(error))
      });
      throw error;
    }
  }
  release(key) {
    const managed = this.#connections.get(key);
    if (!managed) {
      return;
    }
    managed.refCount -= 1;
    if (managed.refCount > 0 || managed.pendingRelease) {
      return;
    }
    if (managed.reconnectTimer) {
      clearTimeout(managed.reconnectTimer);
      managed.reconnectTimer = null;
    }
    managed.pendingRelease = setTimeout(() => {
      managed.pendingRelease = null;
      if (managed.refCount > 0) {
        return;
      }
      const connection = managed.connection;
      managed.connection = void 0;
      if (connection) {
        this.#detachCallbacks(managed, connection);
        connection.disconnect();
      }
      this.#connections.delete(key);
    }, 0);
  }
  subscribe(key, listener) {
    const managed = this.#ensureEntry(key);
    managed.listeners.add(listener);
    return () => {
      managed.listeners.delete(listener);
      if (managed.refCount <= 0 && managed.listeners.size === 0 && !managed.connection) {
        this.#connections.delete(key);
      }
    };
  }
  getSnapshot(key) {
    return this.#connections.get(key)?.state;
  }
  getConnection(key) {
    return this.#connections.get(key)?.connection ?? null;
  }
};
var ConnectionManager = new ConnectionManagerImpl();

export { AlgebraicType, algebraic_type_variants_exports as AlgebraicTypeVariants, ArrayBuilder, ArrayColumnBuilder, BinaryReader, BinaryWriter, BoolBuilder, BoolColumnBuilder, BooleanExpr, ByteArrayBuilder, ByteArrayColumnBuilder, ClientCache, ColumnBuilder, ColumnExpression, ConnectionId, ConnectionIdBuilder, ConnectionIdColumnBuilder, ConnectionManager, DEFAULT_RECONNECT_POLICY, DbConnectionBuilder, DbConnectionImpl2 as DbConnectionImpl, DisconnectedError, F32Builder, F32ColumnBuilder, F64Builder, F64ColumnBuilder, I128Builder, I128ColumnBuilder, I16Builder, I16ColumnBuilder, I256Builder, I256ColumnBuilder, I32Builder, I32ColumnBuilder, I64Builder, I64ColumnBuilder, I8Builder, I8ColumnBuilder, Identity, IdentityBuilder, IdentityChangedError, IdentityColumnBuilder, InternalError, Interval, Option, OptionBuilder, OptionColumnBuilder, ProductBuilder, ProductColumnBuilder, ProductType, RECONNECT_INITIAL_DELAY_MS, RECONNECT_JITTER, RECONNECT_MAX_DELAY_FLOOR_MS, RECONNECT_MAX_DELAY_MS, RECONNECT_MIN_DELAY_FLOOR_MS, REPLAY_TIMESTAMP_SYMBOL, RefBuilder, ReplayController, Result, ResultBuilder, ResultColumnBuilder, RowBuilder, ScheduleAt, ScheduleAtBuilder, ScheduleAtColumnBuilder, SenderError, SimpleSumBuilder, SimpleSumColumnBuilder, StringBuilder, StringColumnBuilder, SubscriptionBuilderImpl, SubscriptionHandleImpl, SumBuilder, SumColumnBuilder, SumType, Time, TimeDuration, TimeDurationBuilder, TimeDurationColumnBuilder, Timestamp, TimestampBuilder, TimestampColumnBuilder, TypeBuilder, U128Builder, U128ColumnBuilder, U16Builder, U16ColumnBuilder, U256Builder, U256ColumnBuilder, U32Builder, U32ColumnBuilder, U64Builder, U64ColumnBuilder, U8Builder, U8ColumnBuilder, UnknownCallResultError, Uuid, UuidBuilder, UuidColumnBuilder, and, bsatnBaseSize, coerceParams, coerceToBigInt, computeReconnectDelayMs, convertToAccessorMap, createTableRefFromDef, deepEqual, evaluateBooleanExpr, getGlobalLogLevel, getQueryAccessorName, getQueryTableName, getQueryWhereClause, hasOwn, hexStringToU128, hexStringToU256, hexStringToUint8Array, isRowTypedQuery, isTypedQuery, literal, makeFromBuilder, makeQueryBuilder, not, or, parseValue, procedureSchema, procedures, reducerSchema, reducers, resolveReconnectPolicy, schema, setGlobalLogLevel, stdbLogger, stringify, t, table, toCamelCase, toComparableValue, toPascalCase, toSnakeCase, toSql, tokenNeedsRefresh, u128ToHexString, u128ToUint8Array, u256ToHexString, u256ToUint8Array, uint8ArrayToHexString, uint8ArrayToU128, uint8ArrayToU256 };
//# sourceMappingURL=index.browser.mjs.map
//# sourceMappingURL=index.browser.mjs.map